import { CommonModule } from '@angular/common';
import { saveAs } from 'file-saver';
import {
  Component,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { DateTime } from 'luxon';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { Observable, catchError, firstValueFrom, of, timeout } from 'rxjs';

import OrderAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/process/order/order.entity';
import SalesOfficeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/configurations/sales-office/sales-office.entity';
import SubRouteAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/routing/sub-route/sub-route.entity';
import VehicleAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity';

import { GeoManagerComponent } from 'app/common/components/geo-manager/geo-manager.component';
import {
  MapPolyline,
  MarkerData,
  MarkerGroup,
} from 'app/common/components/geo-manager/geo-manager.interfaces';
import { HandlerMessageService } from 'app/common/services/handler-error/handler-message.service';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { SalesOfficeService } from 'app/modules/components/configuration/sales-office/sales-office.service';
import { SubRouteService } from 'app/modules/components/routing/sub-routes/sub-route.service';
import {
  AssignedTruck,
  CrossTerritoryConflict,
  DuplicateResolution,
  TerritoryDemand,
  PlanningTerritory,
  assignedTrucksFromSubRoutes,
  crossTerritoryConflicts,
  fleetByTerritory,
  resolveDuplicateVehicles,
  territoriesFromSubRoutes,
} from 'app/modules/components/routing/sub-routes/route-optimization/planning-catalogs';

import { IPaginationResultFrequencies } from 'app/common/interfaces';

import { LoadsService } from '../../loads.service';
import { RouteLoadingAnimationComponent } from 'app/shared/components/route-loading-animation/route-loading-animation.component';
import { OrderDetailsTableComponent } from '../order-details-table/order-details-table.component';

import { CdkDrag, CdkDragDrop, CdkDropList, DragDropModule } from '@angular/cdk/drag-drop';

import { ConfirmationModalType } from 'app/shared/components/confirmation-modal';
import { ConfirmationService } from 'app/shared/services/confirmation.service';
import { TripVisitOrderService } from 'app/modules/components/sales/trip/trip-visit-order.service';

import { apiErrorDetail } from 'app/modules/components/routing/sub-routes/route-optimization/api-error';
import {
  ClientLike,
  clientLabel,
} from 'app/modules/components/routing/sub-routes/route-optimization/client-label';
import { territoryIdOf } from 'app/modules/components/routing/sub-routes/route-optimization/delivery-territory';
import {
  applySavedOrder,
  buildReviewRoutes,
  destinationKey,
  savedOrderDiffers,
  ConfirmRoutePayload,
  customCount,
  moveReviewStop,
  moveStopToRoute,
  MoveTarget,
  moveTargetsFor,
  resetReviewRoute,
  reviewDayCapacityLb,
  reviewLoadLb,
  reviewOverloadLb,
  ReviewRoute,
  ReviewStop,
  toConfirmRoutes,
  applyReviewOrderDelta,
  reviewOrderDelta,
  canRouteReceive,
  reclaimSavedDestinations,
  refreshReviewCustom,
  addStopToRoute,
  removeReviewStop,
  injectedCount,
} from 'app/modules/components/routing/sub-routes/route-optimization/confirm-review';
import {
  applySortResult,
  canRecalcRoute,
  recalcExceedsShiftMin,
  recalcIsStale,
  applyPlanResult,
  toPlanRequestForRoute,
  toSortRequestForRoute,
  RecalcAssumptions,
  RecalcStopTimes,
  RouteRecalc,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-recalc';
import {
  calibrateMinPerKm,
  estimateExceedsShiftMin,
  estimateRouteTimeline,
  routeCalibrationSamples,
  CalibrationSample,
  EstimatedDay,
  RunCalibration,
  EstimatedTimeline,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-estimate';
import {
  timelineFromPlan,
  timelineFromRecalc,
  RouteTimeline,
  StopTimeline,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-timeline';
import {
  groupPendingByReason,
  pendingCandidates,
  pendingPool,
  rankRoutesByDetour,
  toReviewStop,
  PendingGroup,
  PendingOrder,
  PendingSource,
  RouteDetour,
} from 'app/modules/components/routing/sub-routes/route-optimization/pending-orders';
import { DeliveryOrdersService } from 'app/modules/components/routing/sub-routes/route-optimization/delivery-orders.service';
import {
  readFleetSizing,
  sizingFleet,
} from 'app/modules/components/routing/sub-routes/route-optimization/fleet-sizing';
import {
  isDeliverySubRoute,
  nearestStopKm,
  partitionByTerritory,
  TerritoryAssignment,
  UnplannedOrder,
  UnplannedReason,
} from 'app/modules/components/routing/sub-routes/route-optimization/plan-partitioning';
import {
  MoveConsequence,
  Placement,
  StopDecision,
  StopDecisionInput,
  TerritoryBasis,
  stopDecision,
} from 'app/modules/components/routing/sub-routes/route-optimization/stop-decision';
import {
  CoverageFunnel,
  coverageFunnel,
} from 'app/modules/components/routing/sub-routes/route-optimization/coverage-funnel';
import {
  PARAM_SOURCE_LABELS,
  ParamRow,
  RoutePlanParams,
  capacityAssumed,
  paramRows,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-plan-params';
import {
  Dash,
  OfficeSummary,
  DEFAULT_CAPACITY_LB,
  DeliveryPoint,
  GroupPlanView,
  FleetGap,
  OfficePlanView,
  StopRow,
  TerritoryParts,
  TruckRouteView,
  WAREHOUSE_COLOR,
  aggregateDash,
  buildOfficeSummary,
  buildTruckRouteView,
  capacityLbOrDefault,
  createOfficePlanView,
  customerIdOf,
  failureReasonsOf,
  formatDuration,
  fleetGapSummary,
  groupsSummary,
  routesProgressLabel,
  unassignedSummary,
  makePointOf,
  mergeUnassigned,
  officeGeoOf,
  orderIdOf,
  planErrorMessage,
  recomputeOfficeDash,
  truckColorAt,
} from 'app/modules/components/routing/sub-routes/route-optimization/plan-result';
import {
  WAREHOUSE_GROUP_NAME,
  routeMapView,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-map-view';
import {
  buildRouteTags,
  passesRouteTags,
  RouteTag,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-tags';

import {
  ReviewViewState,
  RouteOptimizationRunCache,
  runFingerprint,
  RunSnapshot,
} from './route-optimization-run-cache.service';
import {
  StopOrdersView,
  stopOrders,
} from 'app/modules/components/routing/sub-routes/route-optimization/stop-orders';
import {
  RunAverages,
  StopTimeCost,
  runAverages,
  stopTimeCost,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-times';
import {
  matchesSearch,
  searchSummary,
} from 'app/modules/components/routing/sub-routes/route-optimization/stop-search';
import {
  RouteRef,
  deliveryTerritory,
} from 'app/modules/components/routing/sub-routes/route-optimization/delivery-territory';
import {
  LatLon,
  PlanRequest,
  RouteMapFeatureCollection,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.interface';
import {
  basePlanOrderId,
  DEFAULT_ORDER_WEIGHT,
  orderDeliveryWeightLb,
  SERVICE_MIN_PER_STOP,
  registerServiceSplitWeights,
  resolveStart,
  toGroupPlanRequests,
} from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.mapper';
import {
  buildRouteExport,
  RouteEngineTotals,
  RouteExport,
} from 'app/modules/components/routing/sub-routes/route-optimization/review-export';
import { SERVICE_MIN_OPTIONS, resolveServiceMin } from './service-time-options';
import { DEFAULT_SHIFT, resolveShift, shiftMinutes } from './shift-options';
import { RouteOptimizationService } from 'app/modules/components/routing/sub-routes/route-optimization/route-optimization.service';
import { runWithLimit } from 'app/modules/components/routing/sub-routes/route-optimization/run-with-limit';
import { FleetFallback, resolveTerritoryFleet } from 'app/modules/components/routing/sub-routes/route-optimization/subroute-fleet';
import {
  IConfirmOptimizedRouteResult,
  TripRouteService,
} from 'app/modules/components/sales/trip/trip.service';

/**
 * Alcance de la sugerencia, tomado de los filtros de Asignación de Carga.
 *
 * No lleva la pestaña activa a propósito: la sugerencia planifica **siempre lo
 * mismo** —lo que hay que repartir— y eso son dos conjuntos, no uno. Ver
 * `PLANNING_SCOPES`.
 */
export interface RouteOptimizationSuggestionData {
  /**
   * `_id` de Mongo de la compañía, si el filtro tiene una. Sin ella se toman
   * todas las oficinas activas: cada pedido ya trae su compañía y la oficina
   * acota el universo, así que no hace falta elegirla para optimizar.
   */
  companyMongoId?: string;
  /** Id numérico de la compañía, si el filtro tiene una. */
  companyId?: number;
  /** Día de carga elegido en la pantalla (etiqueta del plan y día de la semana). */
  loadDate: Date;
  /** Rango UTC de ese día. */
  dayRange: { start: string; end: string };
  /** Oficinas del filtro (ids numéricos). Vacío = todas. */
  salesOfficeIds?: number[];
  /** Territorios del filtro (ids numéricos). Vacío = todos. */
  routeIds?: number[];
  /** Sub-rutas del filtro (ids numéricos). Vacío = todas. */
  subRouteIds?: number[];
  /** Clientes del filtro (`shipTo.id` numéricos). Vacío = todos. */
  customerIds?: number[];
  /** Clases de documento del filtro (códigos de `class.code`). Vacío = todas. */
  documentClassCodes?: string[];
  /**
   * Unidad de medida paralela activa en la compañía. Solo afecta cómo la tabla de
   * materiales rotula las cantidades; sin él dice "(UMB)" en vez de "(UMB / UMP)".
   */
  UMPActive?: boolean;
}

/** Uno de los dos conjuntos de pedidos que entran a la sugerencia. */
export type PlanningScopeKey = 'with-trip' | 'without-trip';

/** Cuántos pedidos aportó cada conjunto (para que el resultado sea auditable). */
export type ScopeCounts = Record<PlanningScopeKey, number>;

/**
 * Qué conjuntos de pedidos entran a la corrida.
 *
 * Reemplaza al viejo `BacklogScope` ('day' | 'all'), que elegía otra cosa: hasta
 * dónde llegaba el backlog de los sin-viaje, mientras los dos conjuntos entraban
 * **siempre**. Ninguna de sus dos opciones reproducía la pestaña —la de por
 * defecto acotaba por `deliveryDateTime`, que la pestaña no filtra, y la otra
 * traía el pendiente histórico completo—, así que la elección útil es esta.
 */
export type ScopeSelection = 'with-trip' | 'without-trip' | 'both';

/**
 * `select` es el estado inicial: el modal no calcula hasta que hay al menos una
 * oficina elegida.
 */
export type DialogState = 'select' | 'loading' | 'optimizing' | 'result' | 'empty' | 'error';

/** Los dos modos de presentación de las rutas. Ver `viewMode`. */
export type ViewMode = 'list' | 'grid';

/**
 * Modo con el que arranca el diálogo cuando no hay una elección que respetar.
 *
 * En UN solo lugar a propósito. El arranque en frío y el respaldo de la foto
 * tienen que dar la MISMA respuesta, y con un literal en cada sitio el día que uno
 * cambie el otro queda atrás **en silencio**. Es el mismo criterio por el que
 * `gridTilesWidth` se guarda como `null` en vez de con su valor por omisión: para
 * que el default pueda cambiar sin arrastrar copias suyas por el código.
 *
 * `grid` porque el mosaico es lo que contesta "¿qué rutas hay?" de un vistazo, y
 * 16 tarjetas apiladas entierran ese panorama — que es justamente el problema que
 * el modo grid existe para resolver. Como opción había que descubrir el selector
 * para llegar a él.
 */
export const DEFAULT_VIEW_MODE: ViewMode = 'grid';

/**
 * Recargas de respaldo. Al re-particionar por sub-ruta, los VARIOS viajes que
 * esa sub-ruta hace en el día se funden en un solo problema: sin permitir que el
 * camión vuelva a cargar, todo lo que excede un viaje volvería como "sin
 * capacidad" y la sugerencia parecería rota. `resolveGroupFleet` solo las
 * aplica si la sub-ruta las admite (`allowMultiTrips`).
 */

/** Timeout del `/plan` de UN grupo (~34 paradas): corto, para no bloquear la corrida. */
const GROUP_PLAN_TIMEOUT_MS = 90_000;

/**
 * Planes en vuelo simultáneos. Valhalla corre con `replicas: 1` y `limits.cpu: 3`,
 * y VROOM con `limits.cpu: 2`: más simultaneidad no da más throughput, solo cola.
 */
const PLAN_CONCURRENCY = 4;

/** Página de la carga de órdenes: chica, para no arriesgar timeouts. */
const ORDERS_PAGE_SIZE = 100;
/**
 * Timeout de UNA página de órdenes.
 *
 * 60 s y no los 20 s de los maestros: la consulta de pedidos sin viaje supera los
 * 20 s en producción **aun después** de quitarle el bloque de regex que la hacía
 * un scan de colección (medido contra Ofic. Germania). La pestaña que corre esta
 * misma consulta no tiene timeout ninguno —tarda y pinta—, así que 60 s sigue
 * siendo una vara más estricta que la que el resto de la pantalla acepta hoy.
 *
 * Es por página, no por corrida: el total queda acotado por `ORDERS_MAX_PAGES`.
 * Si una página limpia tarda más que esto, la conversación es un índice del lado
 * del servicio de órdenes, no más tiempo del lado del cliente.
 */
const ORDERS_PAGE_TIMEOUT_MS = 60_000;
/**
 * Páginas de cola de UN conjunto en vuelo a la vez.
 *
 * Techo bajo a propósito: cada página paga el `$count` de `withCount` completo, y
 * el trace de producción muestra que ese conteo domina (29 s la primera página,
 * 7–10 con el caché caliente). Disparar todas las colas juntas reparte la misma
 * base de datos entre más conteos simultáneos — con los DOS conjuntos en paralelo
 * el peor caso ya son 2 × este techo consultas en vuelo.
 */
const ORDERS_PAGE_CONCURRENCY = 2;
/** Tope de seguridad de páginas por oficina y conjunto (5.000 órdenes). */
const ORDERS_MAX_PAGES = 50;

/**
 * Un conjunto de pedidos a traer.
 *
 * El alcance trae su propio `page`: cada conjunto se pide con la consulta que le
 * corresponde, y quien pagina no necesita saber cuál es. Los sin viaje van por el
 * servicio de la pantalla; los que ya tienen viaje, por el del kit de
 * optimización (ver `planningScopes`).
 */
interface PlanningScope {
  key: PlanningScopeKey;
  label: string;
  /** Trae UNA página (1-based) del conjunto. */
  page: (page: number) => Observable<IPaginationResultFrequencies<OrderAttributes>>;
}

/** Sobre de una página de `withCount`: filas y total. */
function envelopeOf(
  res: IPaginationResultFrequencies<OrderAttributes> | null | undefined,
): { data?: OrderAttributes[]; pageInfo?: Array<{ total?: number }> } | undefined {
  return (
    res?.data as unknown as Array<{
      data?: OrderAttributes[];
      pageInfo?: Array<{ total?: number }>;
    }>
  )?.[0];
}

/** Alias local para tipar el arrastre sin re-exportar el modelo del gestor. */
type ReviewStopLike = ReviewRoute['stops'][number];

/**
 * Motivo de los pedidos que el operador RETIRÓ de una ruta.
 *
 * Grupo propio y no fundido con los que nunca entraron: esos no tenían camión que los
 * llevara, y estos sí lo tenían. Fundirlos escondería cuál de las dos cosas pasó.
 */
const RETIRADO_DE_RUTA = 'Los sacaste de una ruta';

/**
 * Nombre de la sub-ruta que declara la ORDEN.
 *
 * Último respaldo del rótulo de ruta: los excluidos no pasan por ningún grupo, así
 * que no hay maestro resuelto del que sacarlo, pero la orden trae la referencia.
 */
function subRouteNameOf(order: OrderAttributes | undefined): string | undefined {
  return (order as unknown as { subRoute?: { name?: string } } | undefined)?.subRoute?.name;
}

/**
 * Listas en las que puede abrirse el detalle de un pedido.
 *
 * Las listas en las que una entrega puede terminar —planificada, rechazada por
 * el motor, o excluida antes de llegar a él— más el gestor de confirmación
 * (`review`), que muestra el mismo detalle sobre las paradas que va a persistir.
 */
export type DetailList = 'stops' | 'unassigned' | 'excluded' | 'without-route' | 'review';

/**
 * Clave de un punto que no es una parada planificada (no asignado o excluido).
 *
 * Es su `orderId`. Antes se armaba con nombre + dirección porque el modelo de
 * vista no traía el id; ahora sí, y el id no colisiona entre dos clientes con el
 * mismo nombre comercial — que en el maestro real los hay.
 */
function pointKey(point: DeliveryPoint): string {
  return point.orderId;
}

/**
 * Porcentaje que `part` representa de `whole`, a un decimal.
 *
 * Se mide contra la BASE: el ahorro es respecto de lo que se hubiera recorrido, no
 * respecto de lo que se recorrió. Con base 0 devuelve 0 en vez de `Infinity` o `NaN`,
 * que en la plantilla se pintarían tal cual.
 */
function pctOf(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((part / whole) * 1000) / 10;
}

/**
 * Velocidad media en km/h a partir de kilómetros y minutos de VIAJE.
 *
 * Sirve para reconciliar los dos porcentajes del ahorro: cuando la distancia baja 48 %
 * y el tiempo solo 19 %, la diferencia está acá — el plan agrupa clientes cercanos y
 * cambia kilómetros de carretera por kilómetros urbanos, más cortos y más lentos.
 *
 * Con cero minutos devuelve `null`: un `Infinity` en la plantilla se pintaría tal cual,
 * y "velocidad infinita" es peor que no decir nada.
 */
function kmhOf(km: number, minutes: number): number | null {
  if (!minutes) return null;
  return Math.round((km * 60) / minutes);
}

/**
 * Rendimiento de arranque, en km por galón.
 *
 * Es un punto de partida, no una medición. Es el promedio esperado del Mitsubishi Fuso
 * Canter en la tabla de referencia que la pantalla muestra — uno de los dos modelos de
 * 11.500 lb, la clase de esta flota; el otro, el Isuzu NPR, da 15. Quien conozca su flota
 * debería reemplazarlo por el número real.
 */
const DEFAULT_KM_PER_GALLON = 17;

/**
 * Precio de arranque, en lempiras por galón.
 *
 * Aproxima el diésel de la SEN —que en agosto de 2026 estaba en L 134,25— y se corrige
 * en un clic con el enlace a la publicación oficial. Se actualiza los lunes.
 */
const DEFAULT_FUEL_PRICE_LPS = 130;

/**
 * Sugerencia de optimización de rutas para las pestañas de Asignación de Carga.
 *
 * Qué hace: vuelve a planificar con el motor de ruteo los pedidos de la pestaña
 * desde la que se abrió, acotados a las oficinas que el usuario elija, y muestra
 * el recorrido propuesto por sub-ruta. **No guarda nada**: los viajes reales
 * quedan intactos.
 *
 * Arranca en un paso de selección de oficina y no calcula solo. No es un rodeo:
 * una corrida toca N oficinas × todas sus órdenes del alcance y dispara una
 * petición al motor por sub-ruta — en la pestaña "sin atención pasada" eso son
 * tres meses de pedidos. Elegir el alcance antes evita pagar esa corrida para
 * después descartarla.
 *
 * Por qué re-consulta las órdenes en vez de usar las filas de la grilla: el
 * endpoint del tab de viajes (`getTripsByLoadDocs`) devuelve el viaje
 * **agregado** —id, sub-ruta, peso, conteo— y el motor necesita cada parada con
 * su `geolocationPoint`, su cliente y su peso real. Esos datos no están en la
 * grilla; el propio tab los carga por viaje recién al expandir la fila.
 */
@Component({
  selector: 'app-route-optimization-suggestion',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    DragDropModule,
    GeoManagerComponent,
    RouteLoadingAnimationComponent,
    OrderDetailsTableComponent,
  ],
  templateUrl: './route-optimization-suggestion.dialog.html',
})
export class RouteOptimizationSuggestionDialog implements OnInit, OnDestroy {
  state: DialogState = 'select';
  errorMessage = '';
  /** Oficinas efectivamente planificadas, para el mensaje de "sin órdenes". */
  loadedScopeLabel = '';

  /* --- Paso de selección de oficinas --- */

  /** El catálogo de oficinas aún está cargando. */
  officesLoading = false;
  /** Oficinas elegibles: activas y **con geolocalización** (son el almacén). */
  officeOptions: SalesOfficeAttributes[] = [];
  filteredOfficeOptions: SalesOfficeAttributes[] = [];
  officeSearch = '';
  /** Oficinas elegidas para la corrida (ids numéricos). */
  selectedOfficeIds: number[] = [];

  /* -------------------------- Territorios ---------------------------- */

  /**
   * Territorios de las oficinas elegidas, desde `GET sub-route/territories`.
   * Vacío si el endpoint no existe en el ambiente o falló: el selector se
   * oculta y manda el filtro de la pantalla, como antes.
   */
  territoryOptions: PlanningTerritory[] = [];
  filteredTerritoryOptions: PlanningTerritory[] = [];
  territorySearch = '';
  /**
   * Territorios de la corrida. TODOS elegidos = sin filtro: pedir por la lista
   * completa excluiría pedidos cuyos territorios no están en el catálogo de las
   * oficinas, que no es lo que "todos" significa.
   */
  selectedTerritoryIds: number[] = [];
  /** `true` tras la primera siembra: las recargas conservan lo elegido. */
  private territoriesSeeded = false;
  /** Última combinación de oficinas consultada, para no recargar en vano. */
  private catalogOfficesKey = '';
  /** Última combinación de territorios con la que se pidieron camiones. */
  private catalogTerritoriesKey = '';

  /** Filtra el combo de territorios por nombre o código. */
  filterTerritoryOptions(): void {
    const q = this.territorySearch.trim().toLowerCase();
    this.filteredTerritoryOptions = q
      ? this.territoryOptions.filter((t) =>
          [t.name, t.externalKeys?.code].some((x) => (x ?? '').toLowerCase().includes(q)),
        )
      : this.territoryOptions;
  }

  /** ¿Están todos los territorios del catálogo seleccionados? */
  get allTerritoriesSelected(): boolean {
    return (
      this.territoryOptions.length > 0 &&
      this.selectedTerritoryIds.length === this.territoryOptions.length
    );
  }

  toggleAllTerritories(): void {
    this.selectedTerritoryIds = this.allTerritoriesSelected
      ? []
      : this.territoryOptions.map((t) => t.id);
    void this.reloadVehiclesIfScopeChanged();
  }

  /**
   * Oficinas contra las que se piden los catálogos dependientes (territorios y
   * camiones): la selección viva, o el filtro de la pantalla mientras no la hay.
   */
  private officeIdsForCatalogs(): number[] {
    return this.selectedOfficeIds.length
      ? [...this.selectedOfficeIds]
      : [...(this.data.salesOfficeIds ?? [])];
  }

  /**
   * Territorios EFECTIVOS de la corrida — lo que viaja en las consultas de
   * pedidos y de camiones. Tres casos, en orden:
   * - catálogo ausente (endpoint sin desplegar, o falló): manda el filtro de la
   *   pantalla, que era el comportamiento anterior;
   * - todos elegidos: sin filtro (`[]` = todos, el contrato de `routeIds`);
   * - subselección: exactamente esa.
   */
  private effectiveTerritoryIds(): number[] {
    if (!this.territoryOptions.length) return [...(this.data.routeIds ?? [])];
    if (this.allTerritoriesSelected) return [];
    return [...this.selectedTerritoryIds];
  }

  /**
   * Carga (o recarga) el catálogo de territorios de las oficinas elegidas.
   *
   * Los territorios se DERIVAN del maestro de sub-ruta, no de un endpoint
   * aparte: son los `routes[]` de las sub-rutas de ENTREGA de esas oficinas (ver
   * `planning-catalogs`). Por eso lo primero es traer el maestro — de ahí salen
   * también los camiones y, más tarde, la flota de cada grupo, así que la misma
   * consulta sirve para las tres cosas.
   *
   * La primera vez siembra la selección: la del filtro de la pantalla si acota,
   * todos si no, o la restaurada de una apertura anterior. Las recargas por
   * cambio de oficinas conservan lo elegido y suman lo que aparece nuevo — un
   * territorio recién llegado al catálogo nunca fue deseleccionable, así que
   * dejarlo fuera sería una exclusión que nadie hizo.
   */
  async loadTerritoryOptions(): Promise<void> {
    const officeIds = this.officeIdsForCatalogs();
    this.catalogOfficesKey = officeIds.slice().sort((a, b) => a - b).join(',');
    await this.loadSubRouteMaster(officeIds);
    const rows = officeIds.length ? territoriesFromSubRoutes([...this.subRoutesById.values()]) : [];
    const prevIds = new Set(this.territoryOptions.map((t) => t.id));
    this.territoryOptions = rows;
    this.filterTerritoryOptions();

    const ids = rows.map((t) => t.id);
    const valid = new Set(ids);
    if (!this.territoriesSeeded) {
      this.territoriesSeeded = true;
      if (this.restoredInputs && this.selectedTerritoryIds.length) {
        // Restaurada de una apertura anterior, acotada al catálogo vigente.
        this.selectedTerritoryIds = this.selectedTerritoryIds.filter((id) => valid.has(id));
        return;
      }
      const fromFilter = new Set(this.data.routeIds ?? []);
      this.selectedTerritoryIds = fromFilter.size
        ? ids.filter((id) => fromFilter.has(id))
        : [...ids];
      return;
    }
    const kept = this.selectedTerritoryIds.filter((id) => valid.has(id));
    const added = ids.filter((id) => !prevIds.has(id) && !kept.includes(id));
    this.selectedTerritoryIds = [...kept, ...added];
  }

  /**
   * Al cerrar el selector de oficinas: recarga territorios y camiones, que
   * dependen de ellas. Al cierre y no en cada clic, para no disparar una
   * petición por opción marcada.
   */
  onOfficesClosed(opened: boolean): void {
    if (opened) return;
    const key = this.officeIdsForCatalogs().slice().sort((a, b) => a - b).join(',');
    if (key === this.catalogOfficesKey) return;
    void this.loadTerritoryOptions().then(() => this.loadVehicleOptions());
  }

  /** Al cerrar el selector de territorios: los camiones dependen de ellos. */
  onTerritoriesClosed(opened: boolean): void {
    if (opened) return;
    void this.reloadVehiclesIfScopeChanged();
  }

  /** Recarga el pool de camiones solo si el alcance territorial cambió. */
  private async reloadVehiclesIfScopeChanged(): Promise<void> {
    const key = this.effectiveTerritoryIds().slice().sort((a, b) => a - b).join(',');
    if (key === this.catalogTerritoriesKey) return;
    await this.loadVehicleOptions();
  }

  /* --------------------- Camiones disponibles ------------------------ */

  /**
   * Camiones ASIGNADOS a las rutas de entrega del alcance elegido, derivados del
   * maestro de sub-ruta. Vacío = ninguna ruta del alcance declara camión; no hay
   * exclusión que hacer y todos los grupos caen a la capacidad de respaldo.
   *
   * Ya no es "el catálogo de vehículos": es la flota que el maestro dice que
   * sale. La distinción importa — antes, cuando el endpoint de planificación
   * fallaba, esto caía al catálogo COMPLETO de la compañía y ofrecía camiones que
   * ninguna ruta declara.
   */
  vehicles: VehicleAttributes[] = [];
  filteredVehicles: VehicleAttributes[] = [];
  vehicleSearch = '';
  /**
   * Territorios que sirve cada camión (por `String(_id)`), derivados de las
   * sub-rutas de ENTREGA que lo declaran. Un camión en varias rutas acumula los
   * territorios de todas: así el combo muestra cuando una placa rompe la regla de
   * "un camión, un territorio" en vez de esconderlo.
   */
  private vehicleTerritoryNames = new Map<string, string[]>();

  /**
   * Camiones que varias rutas del alcance se disputan, con las rutas que los
   * declaran. Se calcula al cargar el catálogo para poder rotular la opción; la
   * DECISIÓN (a qué territorio va cada uno) se resuelve en la corrida, cuando ya se
   * sabe cuántos pedidos tiene cada territorio.
   *
   * Es informativo, no bloqueante: el maestro dice qué rutas PUEDE hacer el camión,
   * y el optimizador completa el dato del día asignándolo al de más pedidos.
   */
  disputedVehicles: CrossTerritoryConflict[] = [];

  /** La decisión de la última corrida, para declararla en el resultado. */
  duplicateVehicles: DuplicateResolution[] = [];

  /** ¿Varias rutas del alcance se disputan este camión? Para rotular su opción. */
  isDuplicateVehicle(vehicle: VehicleAttributes): boolean {
    const id = String(vehicle?._id);
    return this.disputedVehicles.some((c) => c.vehicleId === id);
  }

  /**
   * Las rutas que se disputan un camión, para su opción. Se nombran las RUTAS y no
   * los territorios: es lo que el operador reconoce y edita en el maestro.
   */
  duplicateRoutesLabel(vehicle: VehicleAttributes): string {
    const id = String(vehicle?._id);
    const disputed = this.disputedVehicles.find((c) => c.vehicleId === id);
    return disputed ? disputed.subRouteNames.join(' · ') : '';
  }

  /** Territorios del camión para la opción: hasta dos nombres y "+N". */
  vehicleTerritoryLabel(vehicle: VehicleAttributes): string {
    const names = this.vehicleTerritoryNames.get(String(vehicle._id)) ?? [];
    if (!names.length) return '';
    return names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ');
  }

  /**
   * Detalle del camión para su opción, SIN repetir datos: en parte de la flota
   * real el código ES la placa (`HINO · TEA6941 · TEA6941`), así que cada dato
   * entra una sola vez, y nunca si ya está en el nombre.
   */
  vehicleDetailLabel(vehicle: VehicleAttributes): string {
    const v = vehicle as unknown as {
      name?: string;
      code?: string;
      plate?: string;
      type?: { name?: string };
    };
    const parts: string[] = [];
    const seen = new Set<string>([(v.name ?? '').trim().toLowerCase()]);
    const push = (value?: string): void => {
      const text = (value ?? '').trim();
      if (!text || seen.has(text.toLowerCase())) return;
      seen.add(text.toLowerCase());
      parts.push(text);
    };
    push(v.code);
    push(v.plate);
    push(v.type?.name);
    const weight = vehicle.dimensions?.grossWeight;
    if (weight) {
      const unit = vehicle.dimensions?.weightUnitOfMeasure?.code || 'LB';
      parts.push(`${Math.round(weight).toLocaleString('en-US')} ${unit}`);
    }
    push(this.vehicleTerritoryLabel(vehicle));
    return parts.join(' · ');
  }
  /**
   * Camiones DISPONIBLES para la corrida. Por defecto todos: excluir es la
   * excepción (un camión en el taller), no el flujo normal.
   *
   * El efecto de excluir es real, no informativo: el grupo cuya sub-ruta apunta
   * a un camión excluido se planifica con la capacidad de respaldo y queda
   * contado en "camión y jornada por defecto" — a diferencia de Rutas AI, donde
   * el flag solo decora. Un plan calculado con la capacidad de un camión que no
   * va a salir del patio es la clase de error que se descubre cargando.
   */
  selectedVehicleIds: string[] = [];

  /** Filtra el combo de camiones por nombre, código o placa. */
  filterVehicleOptions(): void {
    const q = this.vehicleSearch.trim().toLowerCase();
    this.filteredVehicles = q
      ? this.vehicles.filter((v) =>
          [v.name, v.code, (v as unknown as { plate?: string }).plate].some((s) =>
            (s ?? '').toLowerCase().includes(q),
          ),
        )
      : this.vehicles;
  }

  /**
   * Deriva la flota de la corrida del maestro de sub-ruta ya cargado. Pública
   * para poder ejercitarla en tests, como `loadOfficeOptions`.
   *
   * Sale del maestro, que se asegura acá y no se asume cargado: es un método
   * público y hacer que dependa de que otro haya corrido antes es la clase de
   * acoplamiento que se rompe en silencio. `loadSubRouteMaster` no repite la
   * consulta si el alcance es el mismo, así que llamarlo de los dos lados
   * cuesta una sola petición.
   *
   * Antes esto eran 1 + N peticiones a `GET sub-route/vehicles` (una por
   * territorio, con su propia concurrencia) contra la API de otro ambiente, y un
   * fallback al catálogo completo de la compañía cuando fallaban. Ese fallback es
   * lo que ofrecía camiones que ninguna ruta declara: la flota que sale no es
   * "los vehículos con peso cargado", es "los que una ruta de entrega declara".
   *
   * Un camión sin capacidad usable SÍ se ofrece: está asignado, y su grupo cae a
   * la capacidad de respaldo con el aviso de `resolveGroupFleet`. Ocultarlo diría
   * que la ruta no tiene camión, que es falso.
   */
  async loadVehicleOptions(): Promise<void> {
    // El catálogo ANTERIOR, antes de rearmarlo. Es lo único que distingue un camión
    // que el operador destildó —estaba y lo sacó— de uno que acaba de aparecer.
    const previousIds = new Set(this.vehicles.map((v) => String(v._id)));
    const hadCatalog = this.vehicleCatalogBuilt;
    const officeIds = this.officeIdsForCatalogs();
    await this.loadSubRouteMaster(officeIds);
    const territoryIds = this.effectiveTerritoryIds();
    this.catalogTerritoriesKey = territoryIds.slice().sort((a, b) => a - b).join(',');
    // Sin oficinas no hay flota que ofrecer: la flota es POR oficina/territorio.
    const trucks: AssignedTruck[] = officeIds.length
      ? assignedTrucksFromSubRoutes([...this.subRoutesById.values()], territoryIds)
      : [];
    this.vehicleTerritoryNames = new Map(
      trucks.map((t) => [String(t.vehicle._id), t.territoryNames]),
    );
    this.vehicles = trucks.map((t) => t.vehicle);
    this.filteredVehicles = this.vehicles;
    // El catálogo y el bloqueo salen del MISMO maestro y del MISMO alcance: un
    // camión no puede aparecer disponible acá y ser descartado por el planificador,
    // que es exactamente lo que pasaba con dos definiciones de flota.
    this.disputedVehicles = officeIds.length
      ? crossTerritoryConflicts([...this.subRoutesById.values()], territoryIds)
      : [];
    const ids = this.vehicles.map((v) => String(v._id));
    this.vehicleCatalogBuilt = true;
    if (this.restoredInputs) {
      // La selección restaurada se acota al catálogo vigente, como las oficinas.
      const valid = new Set(ids);
      const kept = this.selectedVehicleIds.filter((id) => valid.has(id));
      // Y lo que APARECIÓ entra seleccionado: el default de este selector es "todos",
      // así que un camión que no estaba en el catálogo anterior no fue rechazado por
      // nadie. Sin esto, agregar una oficina traía sus camiones sin seleccionar y sin
      // nada que lo explicara — y el catálogo se rearma en cada cambio de alcance,
      // así que era el caso corriente y no uno raro.
      //
      // Solo a partir del SEGUNDO armado: al restaurar, `this.vehicles` está vacío,
      // así que en el primero todo parecería nuevo y pisaría la selección guardada.
      const fresh = hadCatalog ? ids.filter((id) => !previousIds.has(id)) : [];
      this.selectedVehicleIds = fresh.length ? [...kept, ...fresh] : kept;
      return;
    }
    this.selectedVehicleIds = ids;
  }

  /**
   * Si el catálogo de camiones ya se armó en esta apertura.
   *
   * Flag propio y no `catalogTerritoriesKey`: esa arranca en `''`, que además es una
   * clave legítima —el alcance sin territorios— así que no distingue "todavía no se
   * armó" de "se armó sin territorios". Acá hace falta exactamente esa distinción.
   */
  private vehicleCatalogBuilt = false;

  /**
   * Camiones EXCLUIDOS de la corrida: el catálogo menos la selección.
   *
   * Se deriva (no se guarda) para que un catálogo que no cargó signifique
   * "ninguna exclusión" y no "todos excluidos".
   */
  private excludedVehicleIdSet(): Set<string> {
    if (!this.vehicles.length) return new Set();
    const selected = new Set(this.selectedVehicleIds);
    return new Set(
      this.vehicles.map((v) => String(v._id)).filter((id) => !selected.has(id)),
    );
  }

  /* ------------------------ Viajes y recargas ------------------------- */

  /**
   * Viajes máximos por camión en el día. `1` = sin recargas.
   *
   * **Sin control en pantalla, a propósito.** Hubo un selector acá y se retiró: el
   * multi-viaje solo se aplica donde el maestro de la sub-ruta declara
   * `allowMultiTrips === true` (estricto, el silencio no es permiso), y NINGUNA
   * pantalla de la app escribe esa bandera. O sea que el selector se podía mover y el
   * plan salía idéntico — una palanca falsa, en el único panel donde todo lo demás sí
   * cambia el plan.
   *
   * Queda en **1**, que es lo que toda corrida real producía igual. La fontaneria se
   * conserva: si vale más de uno viaja al request, y el contrato de la API sigue
   * aceptándolo. Devolver el control exige primero poder habilitar el multi-viaje —un
   * control en el maestro de sub-rutas—. Ver el cambio `hide-reload-control`.
   */
  maxTrips = 1;
  /** Minutos de recarga en el almacén entre viajes. Sin control, como `maxTrips`. */
  reloadMin = 30;
  /**
   * Nombres de las oficinas descartadas por no tener coordenadas. Se listan en
   * vez de omitirse en silencio: si falta la que el usuario busca, tiene que
   * poder ver por qué no está.
   */
  officesWithoutGeo: string[] = [];

  /**
   * Por qué no se pudo traer el catálogo de oficinas, o `''` si se trajo.
   *
   * Antes esta consulta pasaba por `safe()`, que hace
   * `catchError(() => of(null))` sin log ni rethrow: un fallo dejaba
   * `officeOptions` vacío y la pantalla decía "No hay oficinas de ventas activas
   * con geolocalización para optimizar" —que es una afirmación sobre los DATOS
   * cuando lo que paso fue un fallo de RED—. El usuario no podía distinguir los
   * dos casos y nadie podía diagnosticarlo, porque el error no llegaba ni a la
   * consola.
   */
  officesError = '';

  /**
   * Cuántas oficinas devolvió el catálogo, antes de descartar las que no tienen
   * geolocalización usable.
   *
   * Sirve para separar "el maestro no tiene oficinas activas" de "las tiene pero
   * ninguna se puede usar": son dos problemas con dos dueños distintos.
   */
  officesLoadedCount = 0;

  /**
   * Hasta dónde llega el backlog sin viaje. Por defecto solo el día: la pestaña
   * "Pedidos sin viaje" no filtra fecha, así que "todos" puede ser el pendiente
   * histórico completo — útil para ver el tamaño del problema, malo para
   * meterlo entero en las rutas de una jornada.
   */
  scopeSelection: ScopeSelection = 'both';

  /** Cuántos pedidos aportó cada conjunto en la última corrida. */
  scopeCounts: ScopeCounts = { 'with-trip': 0, 'without-trip': 0 };

  officePlans: OfficePlanView[] = [];
  dash?: Dash;
  /**
   * Resumen por oficina con la cadena pedidos → visitas → ubicadas.
   *
   * Derivado: se recalcula junto al `dash` (no en el template, que lo evaluaría
   * en cada ciclo de detección de cambios) y no se guarda en la foto de la
   * corrida — al restaurar se reconstruye de lo restaurado.
   */
  officeSummary?: OfficeSummary;

  /**
   * Viaje del orden SIN optimizar, por `groupKey` — la mitad baseline del KPI
   * "tiempo ahorrado".
   *
   * El baseline es el orden en que los pedidos ENTRARON al grupo (el de la
   * pestaña, antes de optimizar), evaluado por el backend contra la misma matriz
   * de Valhalla que alimenta al plan: mismas paradas, mismo día, mismo motor —
   * la única variable es el orden. Mejor esfuerzo: un grupo sin baseline (el
   * endpoint falló o aún no respondió) solo se excluye del KPI, nunca rompe la
   * corrida — y la cobertura se declara ("sobre N de M rutas").
   */
  private baselineByGroupKey = new Map<string, { durationMin: number; distanceKm: number }>();

  /** Clave de grupo real de las órdenes auto-asignadas (ver `PartitionResult`). */
  private autoGroupKeyByOrderId = new Map<string, string>();

  /**
   * Cómo quedó resuelto el territorio de cada pedido, por `_id` de la orden.
   *
   * Es el primer tramo del recorrido de decisión de una parada. Se guarda la
   * asignación entera y no un texto ya compuesto: el resumen de la insignia arma su
   * frase y la parada arma la suya, y una sola cadena guardada obligaría a que las
   * dos dijeran lo mismo con el mismo ancho.
   */
  private assignmentByOrderId = new Map<string, TerritoryAssignment>();

  /**
   * Los parámetros con los que se planificó cada ruta, por **sub-ruta**.
   *
   * Reemplaza a `fleetSizeByGroupKey` y `assumedCapacityGroups`, que eran dos
   * recortes del mismo dato: uno se quedaba con el tamaño de la flota y el otro con
   * una bandera de capacidad, y ninguno de los dos podía responder con qué se armó
   * la ruta. Una fuente, y los recortes salen de ella.
   *
   * La llave es la SUB-RUTA y no el grupo porque los parámetros son del camión: un
   * territorio con dos sub-rutas tiene dos jornadas posibles, y `subRouteId` ya se
   * resuelve por camión (`subRouteIdOfVehicle`), no por grupo. El tamaño de la flota
   * sí es del territorio y viaja repetido dentro — es un número, no un índice.
   */
  private paramsBySubRouteId = new Map<string, RoutePlanParams>();

  /**
   * El punto de entrega de cada pedido, por id BASE.
   *
   * El gestor lo necesita porque `ReviewStop` solo trae id, nombre, dirección y peso
   * —el territorio de la parada y su disconformidad viven en el punto—, y una parada
   * movida ya no está en las del camión que la muestra, así que buscarla ahí no la
   * encuentra. Se llena con el MISMO `pointOf` que arma el resultado: componerlo
   * aparte haría que el mismo pedido se leyera distinto en las dos vistas.
   */
  private decisionPointByOrderId = new Map<string, DeliveryPoint>();

  /**
   * El KPI: Σ(baseline) − Σ(optimizado), solo sobre rutas CON baseline.
   *
   * Devuelve también los **absolutos** y no solo las restas. Un ahorro sin su base no
   * se puede juzgar: 87,4 km es el 5,9 % de 1.470 km y el 43,7 % de 200 km — el mismo
   * número cuenta dos historias opuestas. Los cuatro ya se sumaban acá y se
   * descartaban en el `return`, así que devolverlos no cuesta nada.
   */
  get timeSaved():
    | {
        minutes: number;
        km: number;
        baselineMinutes: number;
        optimizedMinutes: number;
        baselineKm: number;
        optimizedKm: number;
        minutesPct: number;
        kmPct: number;
        /** Velocidad media del recorrido sin optimizar, o `null` si no se puede medir. */
        baselineKmh: number | null;
        /** Velocidad media del recorrido optimizado. */
        optimizedKmh: number | null;
        covered: number;
        total: number;
      }
    | undefined {
    const trucks = this.officePlans.flatMap((op) => op.truckViews);
    if (!trucks.length) return undefined;
    let baselineMin = 0;
    let optimizedMin = 0;
    let baselineKm = 0;
    let optimizedKm = 0;
    // DOS recorridos y no uno, porque las unidades no coinciden: el optimizado se
    // mide por CAMIÓN y el baseline por GRUPO. `loadGroupBaseline` pide un solo
    // `/evaluate-route` con todas las paradas del grupo, pero el plan puede
    // repartirlas entre varios camiones —"con dos camiones en un territorio son
    // sub-rutas distintas"—. Sumar el baseline una vez por camión comparaba una
    // medición contra N mediciones de las MISMAS paradas: con dos camiones el
    // ahorro se duplicaba, con tres se triplicaba, siempre para el lado que
    // favorece al optimizador.
    const measured = new Set<string>();
    for (const t of trucks) {
      // Un grupo sin baseline no aporta NINGUNO de los dos lados: contar su
      // optimizado sin su base daría un ahorro contra nada.
      if (!this.baselineByGroupKey.has(t.groupKey)) continue;
      measured.add(t.groupKey);
      optimizedMin += t.durationMin;
      optimizedKm += t.distanceKm;
    }
    for (const key of measured) {
      const baseline = this.baselineByGroupKey.get(key);
      if (!baseline) continue;
      baselineMin += baseline.durationMin;
      baselineKm += baseline.distanceKm;
    }
    const covered = measured.size;
    if (!covered) return undefined;
    // La cobertura se cuenta en GRUPOS. El baseline es por grupo, así que contarla
    // en camiones mezclaba dos unidades en la misma frase: con un grupo de dos
    // camiones, "sobre 11 de 17" no decía nada verificable.
    const groups = new Set(trucks.map((t) => t.groupKey));
    return {
      minutes: Math.round(baselineMin - optimizedMin),
      km: Math.round((baselineKm - optimizedKm) * 10) / 10,
      baselineMinutes: Math.round(baselineMin),
      optimizedMinutes: Math.round(optimizedMin),
      baselineKm: Math.round(baselineKm * 10) / 10,
      optimizedKm: Math.round(optimizedKm * 10) / 10,
      // Contra la BASE, no contra el optimizado: el ahorro se mide respecto de lo
      // que se hubiera recorrido. Con base 0 no hay porcentaje que calcular.
      minutesPct: pctOf(baselineMin - optimizedMin, baselineMin),
      kmPct: pctOf(baselineKm - optimizedKm, baselineKm),
      baselineKmh: kmhOf(baselineKm, baselineMin),
      optimizedKmh: kmhOf(optimizedKm, optimizedMin),
      covered,
      total: groups.size,
    };
  }

  /**
   * Precio del combustible en lempiras por galón, o `null` si no se cargó.
   *
   * Se carga a MANO, no se trae de ninguna API, y eso está medido: estimarlo del
   * crudo da entre −8,9 % y −21,1 % contra el precio real de la SEN —el diésel, que
   * es el de esta flota, es el peor—, y el precio local no sigue al crudo: entre
   * abril y agosto de 2026 el WTI cayó 12,8 % y la gasolina Súper se movió 8
   * centavos. Un precio que se queda quieto mientras su materia prima cae 13 % está
   * regulado o compensado, y eso no está en ninguna variable de mercado.
   *
   * Tampoco hay fuente estructurada gratis: la Secretaría de Energía publica los
   * precios como imágenes JPG, semanalmente.
   */
  fuelPricePerGallon: number | null = DEFAULT_FUEL_PRICE_LPS;

  /**
   * Rendimiento de la flota en kilómetros por galón, o `null` si no se cargó.
   *
   * Sin valor por omisión A PROPÓSITO. Cualquier número puesto acá se vuelve el que
   * todos usan sin mirarlo, y propaga un error silencioso a cada cifra en lempiras
   * que la pantalla produzca. Vacío obliga a que salga de la operación.
   *
   * Uno solo para toda la corrida: es un supuesto plano, y el consumo real depende
   * de la carga —un camión al 92 % quema bastante más por km que uno al 20 %—.
   */
  kmPerGallon: number | null = DEFAULT_KM_PER_GALLON;

  /** La URL de la fuente oficial, en un solo lugar: cambiarla es un renglón. */
  readonly fuelPriceSourceUrl = 'https://sen.hn/';

  /**
   * Consumos de referencia, **en la pantalla y no en un enlace externo**.
   *
   * Un enlace tenía tres problemas que una tabla propia no tiene: sacaba al usuario del
   * diálogo, dependía de que un sitio ajeno siguiera existiendo con el mismo contenido, y
   * las fuentes de consumo de camiones suelen publicar en km por **litro** —el campo pide
   * km por **galón**— así que había que cargar la conversión al lado para evitar un error
   * de 3,785× hacia arriba.
   *
   * Todos los valores están en **km por galón**, la misma unidad del campo, y los modelos
   * son de la clase de esta flota: 3.000 a 6.500 kg de peso bruto.
   */
  readonly fuelYieldReference: ReadonlyArray<{
    model: string;
    weight: string;
    /** Rango en ruta montañosa o cargado. Texto porque es un rango, no un valor. */
    loaded: string;
    /** Rango en ruta plana o ligero. */
    light: string;
    /** El valor a usar si no se sabe el tipo de ruta. */
    expected: number;
  }> = [
    { model: 'Hino Serie 300 (Dutro)', weight: '5.500 kg (12.100 lb)', loaded: '16 a 18', light: '22 a 25', expected: 20 },
    { model: 'Toyota Dyna', weight: '4.500 kg (9.900 lb)', loaded: '15 a 17', light: '20 a 22', expected: 18.5 },
    { model: 'Mitsubishi Fuso Canter', weight: '5.200 kg (11.500 lb)', loaded: '14 a 15', light: '18 a 20', expected: 17 },
    { model: 'Hyundai HD65 / Mighty', weight: '6.500 kg (14.300 lb)', loaded: '13 a 15', light: '17 a 19', expected: 16 },
    { model: 'Isuzu NPR (Motor 5.2L)', weight: '5.200 kg (11.500 lb)', loaded: '12 a 14', light: '16 a 18', expected: 15 },
  ];

  /** Si la tabla de consumos de referencia está desplegada. Nace **cerrada**. */
  fuelReferenceOpen = false;

  /**
   * Por debajo de esta diferencia relativa, las dos velocidades medias se consideran
   * la misma. Son promedios de toda la flota: una diferencia de pocos puntos es ruido
   * de redondeo, y afirmar que "por eso" el ahorro cambió sería explicar el ruido.
   */
  private static readonly SPEED_TIE = 0.05;

  /**
   * Cómo se relacionan el ahorro de tiempo y el de distancia, según qué pasó con la
   * velocidad media. `null` si alguna velocidad no se pudo medir.
   *
   * Los dos porcentajes casi nunca coinciden, y la diferencia no es un error de
   * cálculo: el plan reordena paradas, así que cambia la mezcla de kilómetros rápidos
   * y lentos. Sin decirlo, "48 % menos de distancia pero solo 19 % menos de tiempo"
   * se lee como una cuenta mal hecha.
   *
   * Se explica en las TRES direcciones. La primera versión mostraba la nota solo
   * cuando la velocidad bajaba, y eso dejaba el caso contrario —más ahorro de tiempo
   * que de distancia— igual de inexplicado: el hueco cambiaba de lugar, no se cerraba.
   */
  get speedNote(): string | null {
    const saved = this.timeSaved;
    const from = saved?.baselineKmh;
    const to = saved?.optimizedKmh;
    if (!from || !to) return null;
    const head =
      `La velocidad media pasa de ${from} km/h en el recorrido sin optimizar ` +
      `a ${to} km/h en el optimizado`;
    if (Math.abs(to - from) / from < RouteOptimizationSuggestionDialog.SPEED_TIE) {
      return (
        `${head}: prácticamente la misma, así que el ahorro de tiempo y el de ` +
        `distancia son parecidos.`
      );
    }
    if (to < from) {
      return (
        `${head}: el plan agrupa clientes cercanos y cambia kilómetros de carretera ` +
        `por kilómetros urbanos, más cortos y más lentos. Por eso el ahorro de tiempo ` +
        `es menor que el de distancia.`
      );
    }
    return (
      `${head}: además de acortar el recorrido, el plan evita los tramos más lentos. ` +
      `Por eso el ahorro de tiempo es mayor que el de distancia.`
    );
  }

  /**
   * Si el cálculo de los promedios de la corrida está desplegado. Nace **cerrado**.
   *
   * Aparte del del ahorro porque son dos bloques distintos y con **escalas distintas**:
   * estos cuatro son promedios —por parada o por ruta— y el de ahorro son totales de
   * toda la flota. Sin decir cuál es cuál, "41 h de viaje" contra "6 h 41 min de
   * jornada por ruta" se lee como una contradicción.
   */
  averagesMethodOpen = false;

  /**
   * Si la metodología del ahorro está desplegada. Nace **cerrada**.
   *
   * Es lo que las cuatro estadísticas comparten, así que repetirlo en cada tile
   * sería ruido; y es demasiado texto para un tooltip nativo —se corta en algunos
   * navegadores, no existe en touch y no se puede copiar—. Desplegable se lee con
   * calma, se copia, y se alcanza con el dedo.
   */
  methodOpen = false;

  /**
   * El ahorro traducido a galones y lempiras.
   *
   * Deriva de `timeSaved.km`, que es el número duro —mismas paradas, mismo día,
   * mismo motor, la única variable es el orden—. Los dos parámetros que lo
   * multiplican los cargó el usuario, así que la pantalla lo muestra DESPUÉS de los
   * km: poner el dinero primero pondría al frente el número más fácil de refutar.
   *
   * Devuelve `undefined` si falta un parámetro o alguno es cero, y NO cero: un cero
   * se lee como "no ahorraste", que es distinto de "no sé cuánto ahorraste".
   * Arrastra la cobertura de `timeSaved` porque el ahorro se mide solo sobre las
   * rutas con baseline, y una cifra de dinero incompleta que no se declara
   * incompleta es la que termina en una presentación.
   */
  get fuelSaved():
    | {
        gallons: number;
        lempiras: number;
        baselineGallons: number;
        optimizedGallons: number;
        baselineLempiras: number;
        optimizedLempiras: number;
        covered: number;
        total: number;
      }
    | undefined {
    const saved = this.timeSaved;
    const price = this.fuelPricePerGallon;
    const yieldKmGal = this.kmPerGallon;
    if (!saved || !price || !yieldKmGal || price <= 0 || yieldKmGal <= 0) return undefined;
    // Todo sale de los MISMOS absolutos, así que `baseline − optimizado` cierra
    // contra el ahorro. Derivar el ahorro de `saved.km` —ya redondeado— y los
    // absolutos de otra fuente los dejaría descuadrados en el último decimal.
    const baselineGallons = saved.baselineKm / yieldKmGal;
    const optimizedGallons = saved.optimizedKm / yieldKmGal;
    const gallons = baselineGallons - optimizedGallons;
    return {
      // Un decimal en galones y ninguno en lempiras: los km ya vienen redondeados a
      // un decimal, así que la cadena no gana precisión falsa por el camino.
      gallons: Math.round(gallons * 10) / 10,
      lempiras: Math.round(gallons * price),
      baselineGallons: Math.round(baselineGallons * 10) / 10,
      optimizedGallons: Math.round(optimizedGallons * 10) / 10,
      // El costo del DÍA en combustible, un dato que la pantalla no tenía en ninguna
      // parte: sirve para presupuestar y comparar oficinas, que es otra pregunta.
      baselineLempiras: Math.round(baselineGallons * price),
      optimizedLempiras: Math.round(optimizedGallons * price),
      covered: saved.covered,
      total: saved.total,
    };
  }
  /** `true` mientras se confirma la propuesta contra `ave-trip` (fs-98). */
  confirming = false;
  /** Resultado por subruta de la última confirmación (vacío = todavía no se confirmó). */
  confirmResults: IConfirmOptimizedRouteResult[] = [];
  activeTab: 'list' | 'map' = 'list';
  /**
   * Modo de presentación de las rutas propuestas.
   *
   * `list` es la tarjeta por ruta, apiladas. `grid` es el mosaico de tiles con las
   * paradas de la seleccionada al lado. Con 16 rutas, elegir cuál mirar y leer sus
   * paradas comparten hoy el mismo eje vertical: recorrer para elegir obliga a pasar
   * por lo que ya se descartó, y el mosaico separa los dos ejes.
   *
   * Arranca en el default de `DEFAULT_VIEW_MODE`, no en un literal: el respaldo de
   * la foto tiene que responder lo mismo y dos literales divergen sin avisar.
   */
  viewMode: ViewMode = DEFAULT_VIEW_MODE;
  /**
   * La ruta elegida en el mosaico, por `truckId`.
   *
   * NO es `selectedTruckId`: ese es el del mapa, que abre en la primera ruta por
   * requisito. Son dos conceptos y por eso son dos campos.
   *
   * Es la elección EXPLÍCITA del usuario, no la que se está mostrando: cuál se
   * muestra lo deriva `selectedTruckOf`, que respeta el filtro.
   */
  gridTruckId: string | null = null;
  /**
   * Ancho del mosaico en píxeles, o `null` si el usuario nunca movió el divisor.
   *
   * `null` y no el valor por omisión: guardar `352` lo volvería indistinguible de
   * una elección deliberada, y el día que el default cambie las fotos viejas
   * quedarían clavadas al viejo.
   *
   * Cuál es el ancho correcto depende de la pantalla, de cuántas rutas trajo la
   * corrida y de si el operador está eligiendo o leyendo. Un valor fijo acierta en
   * un caso y estorba en los otros.
   */
  gridTilesWidth: number | null = null;
  /**
   * Marcadores a pintar. **Campo estable, no getter**: un getter devolvería un
   * array nuevo por ciclo de detección y el `GeoManager` re-encuadraría el mapa
   * en cada uno, tirándole abajo el zoom al usuario.
   */
  visibleMarkerGroups: MarkerGroup[] = [];

  /**
   * El mapa vivo, para `resize()`/`fitAll()`. Puede no existir: la pestaña Lista
   * no lo renderiza, y en tests no hay template. Todos los usos van con `?.`.
   */
  @ViewChild(GeoManagerComponent) private geoManager?: GeoManagerComponent;

  /**
   * `true` = el mapa ocupa el diálogo completo.
   *
   * El contenedor cambia de tamaño con CSS, pero Leaflet no lo detecta solo:
   * sin el `resize()` posterior quedan tiles grises en la zona nueva — por eso
   * el toggle agenda el reajuste en vez de confiar en el reflow.
   */
  mapExpanded = false;

  /** Expande o restaura el mapa, reajustando Leaflet al tamaño nuevo. */
  toggleMapExpanded(): void {
    this.mapExpanded = !this.mapExpanded;
    // Tras el repaint: si se llama en el mismo tick, Leaflet mide el tamaño viejo.
    setTimeout(() => this.geoManager?.resize(), 60);
  }

  /** Vuelve a encuadrar todas las paradas de la selección. */
  refitMap(): void {
    this.geoManager?.fitAll();
  }

  /**
   * `Escape` tiene DOS niveles: con el mapa expandido lo restaura; sin él,
   * cierra el diálogo. Sin esta prioridad, salir del mapa expandido cerraría el
   * modal entero y se perdería la corrida de vista.
   */
  handleEscape(): void {
    // La capa del mapa es la más arriba: se cierra antes que cualquier otra cosa.
    if (this.mapOverlay) {
      this.closeMapOverlay();
      return;
    }
    if (this.mapExpanded) {
      this.toggleMapExpanded();
      return;
    }
    if (this.reviewOpen) {
      this.closeReview();
      return;
    }
    this.close();
  }
  /** Trazo de la ruta aislada; vacío en "Todas las rutas". */
  visiblePolylines: MapPolyline[] = [];
  readonly warehouseColor = WAREHOUSE_COLOR;

  /** `truck_id` de la ruta que muestra el mapa; `null` = todas. */
  selectedTruckId: string | null = null;
  /**
   * El mapa de UNA ruta, superpuesto sobre el diálogo. `null` = cerrado.
   *
   * Superpuesto y no navegando porque ver el mapa de una ruta es una **consulta**, no
   * un destino: se mira para decidir algo y se vuelve. Llevar a la pestaña de mapa
   * obligaba a rehacer el camino —salir del gestor y volver por Confirmar, o cambiar
   * de pestaña y volver— justo cuando lo que motivó mirar el mapa era una tarea a
   * medias.
   *
   * Estado PROPIO, separado de `selectedTruckId`: reusar el del mapa principal movería
   * su selección, que es exactamente la navegación que no se quiere perder. Es la misma
   * razón por la que `gridTruckId` no es `selectedTruckId`.
   *
   * `edited` = muestra el orden vigente del gestor y NO dibuja trayecto.
   *
   * NO viaja en la foto de la corrida: es una consulta, no "dónde estaba mirando".
   */
  mapOverlay: { truckId: string; label: string; edited: boolean } | null = null;

  /** Marcadores de la capa. Aparte de los del mapa principal, que no se toca. */
  overlayMarkerGroups: MarkerGroup[] = [];
  /** Trayecto de la capa. Vacío cuando muestra un orden editado. */
  overlayPolylines: MapPolyline[] = [];

  /** Opciones del selector del mapa, en el orden de las tarjetas. */
  routeOptions: Array<{ truckId: string; label: string; color: string; stops: number }> = [];
  /** `geojson` de cada grupo resuelto, indexado por su `key`. */
  private geojsonByGroup = new Map<string, RouteMapFeatureCollection>();

  /** Progreso por grupos resueltos. */
  groupsDone = 0;
  groupsTotal = 0;
  /** Progreso de la carga de órdenes ("Ofic. X (n/m)…"). */
  progressOfficeName = '';
  progressIndex = 0;
  progressTotal = 0;

  /** Órdenes de todas las oficinas de la corrida (para nombres/pesos/reintento). */
  private allOrders: OrderAttributes[] = [];
  /** Peso (lb) acumulado por cliente. */
  private weightByCustomer = new Map<string, number>();
  /** Peso (lb) por `PlanOrder.id` del request (incluye paradas divididas). */
  private weightByOrderId = new Map<string, number>();
  /** Maestro de sub-rutas de la corrida, indexado por `_id`. */
  private subRoutesById = new Map<string, SubRouteAttributes>();
  /** Oficinas con las que se cargó `subRoutesById`, para no volver a pedirlo. */
  private masterOfficesKey = '';
  /** Índice de colores de camión, continuo entre oficinas. */
  private truckColorIndex = 0;

  constructor(
    private readonly dialogRef: MatDialogRef<RouteOptimizationSuggestionDialog>,
    @Inject(MAT_DIALOG_DATA) public readonly data: RouteOptimizationSuggestionData,
    private readonly salesOfficeService: SalesOfficeService,
    private readonly subRouteService: SubRouteService,
    private readonly deliveryOrdersService: DeliveryOrdersService,
    private readonly routeOptimizationService: RouteOptimizationService,
    private readonly handlerMessage: HandlerMessageService,
    private readonly runCache: RouteOptimizationRunCache,
    /**
     * El servicio de la PANTALLA. Se usa para los pedidos sin viaje: así el modal
     * consulta lo mismo que la grilla en vez de tener su propia definición de
     * "qué pedidos son" — que es de donde salieron las tres divergencias que
     * hubo que corregir (campo de fecha equivocado, condición faltante, y un
     * filtro por fecha vacía que el backend no responde).
     */
    private readonly loadsService: LoadsService,
    private readonly businessPartnerService: BusinessPartnerService,
    private readonly tripService: TripRouteService,
    private readonly tripVisitOrderService: TripVisitOrderService,
    private readonly confirmationService: ConfirmationService,
    /**
     * Para atender el arrastre del divisor FUERA de la zona. Con ~573 expresiones
     * en esta plantilla y 60–120 eventos de puntero por segundo, un `pointermove`
     * dentro de la zona serían 12–24 veces la carga que ya obligó a no construir
     * las paradas de una ruta cerrada.
     */
    private readonly zone: NgZone,
  ) {}

  ngOnInit(): void {
    // Antes de cargar el catálogo: `loadOfficeOptions` preselecciona las oficinas
    // del filtro y pisaría las que se acaban de restaurar.
    this.restoreRun();
    void this.loadOfficeOptions();
    // Los camiones se piden DESPUÉS de los territorios: su consulta lleva el
    // alcance territorial recién sembrado.
    void this.loadTerritoryOptions().then(() => this.loadVehicleOptions());
    // El modal se abre con `disableClose` para que un clic afuera no tire la corrida,
    // pero eso también desactiva `Escape` — la única salida por teclado, y la que ARIA
    // espera de un diálogo modal. Se restaura acá: el accidente queda bloqueado y el
    // cierre deliberado sigue existiendo para quien no usa mouse.
    this.dialogRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') this.handleEscape();
    });
  }

  /**
   * Cierra.
   *
   * Sin guarda: la había desde que el aviso permanente de "los añadidos se pierden si
   * cerrás sin confirmar" se retiró, y **este cambio elimina esa pérdida** —los
   * incorporados a mano se restauran de la foto igual que el resto del orden—. Un aviso
   * que sobrevive a su condición enseña a ignorar todos los demás.
   */
  close(): void {
    this.dialogRef.close();
  }


  /**
   * Guarda la corrida al destruirse, no en `close()`.
   *
   * `ngOnDestroy` cubre **todas** las salidas —el botón Cerrar, el `Escape` que el
   * propio diálogo restituye, un `closeAll()` de otro lado—; engancharlo a
   * `close()` dejaría afuera las que no pasan por ahí.
   */
  ngOnDestroy(): void {
    clearTimeout(this.searchDebounce);
    // Cerrar el diálogo a mitad de arrastre dejaría los listeners del divisor
    // vivos sobre un elemento que ya no existe. `pointerup` no siempre llega.
    this.detachTilesResize();
    this.saveRun();
  }

  /* --------------------- Estado entre aperturas ---------------------- */

  /**
   * Hora a la que se calculó el plan restaurado, o `''` si esta corrida es nueva.
   *
   * Se declara porque la huella **no alcanza**: puede coincidir y el plan estar
   * viejo igual —alguien asignó pedidos a un viaje sin tocar ningún filtro, que es
   * a lo que se dedica esta pantalla—. Es la respuesta barata y sincera a un
   * problema que no se puede cerrar sin volver a consultar.
   */
  restoredAtLabel = '';

  /**
   * Rehidrata la última corrida si sus entradas son las mismas.
   *
   * Con la huella distinta —otro día, otra compañía, otro filtro— **el resultado
   * se descarta** y solo vuelven las preferencias: restaurar un plan calculado
   * contra otro universo invitaría a despachar camiones sobre pedidos que ya se
   * movieron.
   */
  private restoreRun(): void {
    const snap = this.runCache.read();
    if (!snap) return;

    // NIVEL 1 — preferencias del usuario. Vuelven siempre: la atención por parada
    // y la jornada son suyas y valen para cualquier día, así que perderlas porque
    // cambió la fecha del filtro sería un castigo sin motivo.
    this.selectedServiceMin = snap.inputs.selectedServiceMin;
    this.shiftStart = snap.inputs.shiftStart;
    this.shiftEnd = snap.inputs.shiftEnd;
    this.scopeSelection = snap.inputs.scopeSelection;
    this.maxTrips = snap.inputs.maxTrips;
    this.reloadMin = snap.inputs.reloadMin;
    // El precio cambia una vez por semana: retipearlo porque cambió la fecha del
    // filtro sería trabajo inventado. `?? null` para las fotos anteriores al KPI.
    // `??` y no una asignación directa: si la foto trae un valor, ese manda —es el que
    // el usuario eligió—. El arranque entra SOLO cuando no hay ninguno, que es el caso
    // de una foto anterior a estos campos o de una corrida sin nada cargado.
    this.fuelPricePerGallon = snap.inputs.fuelPricePerGallon ?? DEFAULT_FUEL_PRICE_LPS;
    this.kmPerGallon = snap.inputs.kmPerGallon ?? DEFAULT_KM_PER_GALLON;

    // NIVEL 2 — todo lo que describe UNA corrida: solo si las entradas de la
    // pantalla son las mismas. Con otro día, otra compañía u otro filtro el plan
    // describe un universo que ya no existe, y restaurarlo invitaría a despachar
    // camiones sobre pedidos que ya se movieron.
    if (snap.fingerprint !== runFingerprint(this.data)) {
      this.runCache.clear();
      return;
    }

    // La selección de oficinas es de la corrida, no una preferencia: si el filtro
    // de la pantalla cambió, manda su preselección.
    this.restoredInputs = true;
    this.selectedOfficeIds = [...snap.inputs.selectedOfficeIds];
    // Fotos viejas (sin el campo) rehidratan vacío: `loadTerritoryOptions` lo
    // trata como "sembrar de cero", no como "nada elegido".
    this.selectedTerritoryIds = [...(snap.inputs.selectedTerritoryIds ?? [])];
    // El pool es de la corrida, no una preferencia: un camión en el taller hoy
    // vuelve mañana, así que con otras entradas manda el catálogo completo.
    this.selectedVehicleIds = [...snap.inputs.selectedVehicleIds];

    const run = snap.result;
    if (!run) return;

    this.state = run.state;
    this.errorMessage = run.errorMessage;
    this.loadedScopeLabel = run.loadedScopeLabel;
    this.officesWithoutGeo = [...run.officesWithoutGeo];
    this.scopeCounts = { ...run.scopeCounts };
    this.officePlans = run.officePlans;
    this.dash = run.dash;
    this.officeSummary = buildOfficeSummary(run.officePlans);
    this.baselineByGroupKey = run.baselineByGroupKey ?? new Map();
    this.routeOptions = run.routeOptions;
    this.geojsonByGroup = run.geojsonByGroup;
    this.allOrders = run.allOrders;
    this.weightByCustomer = run.weightByCustomer;
    this.weightByOrderId = run.weightByOrderId;
    this.subRoutesById = run.subRoutesById;
    this.truckColorIndex = run.truckColorIndex;
    this.routeTags = run.routeTags;
    this.averages = run.averages;
    this.assumedServiceMin = run.assumedServiceMin;
    this.assumedShift = { ...run.assumedShift };
    this.groupsDone = run.groupsDone;
    this.groupsTotal = run.groupsTotal;

    this.activeTab = snap.view.activeTab;
    this.selectedTruckId = snap.view.selectedTruckId;
    // Con respaldo: una foto guardada antes de que el modo grid existiera no trae
    // estos campos.
    //
    // Para `viewMode` el respaldo es DEFENSA, no migración: la caché vive en
    // memoria —un F5 la pierde, así que no sobrevive a un deploy— y el campo se
    // escribe SIEMPRE al guardar, así que una foto sin él no puede llegar hasta
    // acá. Si llegara, el default vigente es la respuesta correcta: no hay
    // elección del usuario que respetar. Por eso referencia la constante y no un
    // literal — clavarlo en `'list'` haría que el arranque en frío y la
    // restauración discrepen.
    this.viewMode = snap.view.viewMode ?? DEFAULT_VIEW_MODE;
    this.gridTruckId = snap.view.gridTruckId ?? null;
    this.gridTilesWidth = snap.view.gridTilesWidth ?? null;
    this.reviewGridRouteId = snap.view.reviewGridRouteId ?? null;
    this.restoreReview(snap.view.review);
    this.searchText = snap.view.searchText;
    this.selectedTagKeys = new Set(snap.view.selectedTagKeys);

    // Lo derivado NO se guarda: se recalcula, y así no puede quedar desfasado de
    // lo que describe.
    this.refreshVisibleMap();
    // Solo si algo filtra: `applyFilters` reescribe qué tarjetas están abiertas, y
    // sin filtro las cerraría todas, perdiendo las que el usuario dejó abiertas.
    if (this.searchText.trim() || this.selectedTagKeys.size) this.applyFilters();

    this.restoredAtLabel = new Date(snap.savedAt).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * `true` si las preferencias vinieron de una apertura anterior.
   *
   * `loadOfficeOptions` lo consulta para no pisar la selección restaurada con la
   * del filtro de la pantalla.
   */
  private restoredInputs = false;

  /**
   * Toma la foto de la corrida.
   *
   * El resultado se guarda **solo si terminó**: cerrar a mitad de una optimización
   * la pierde, porque las promesas siguen corriendo contra un componente destruido
   * y una foto a medias describiría un plan que nunca existió.
   *
   * Las colecciones se guardan **por referencia**: hay un solo consumidor a la vez
   * y copiar 262 órdenes en profundidad sería desperdicio.
   *
   * Al agregar estado nuevo al diálogo, este método, `restoreRun` y `reset` son
   * los tres lugares que hay que mirar.
   */
  private saveRun(): void {
    const finished = this.state === 'result' || this.state === 'empty' || this.state === 'error';
    const snapshot: RunSnapshot = {
      fingerprint: runFingerprint(this.data),
      savedAt: Date.now(),
      inputs: {
        selectedOfficeIds: [...this.selectedOfficeIds],
        selectedTerritoryIds: [...this.selectedTerritoryIds],
        selectedVehicleIds: [...this.selectedVehicleIds],
        selectedServiceMin: this.selectedServiceMin,
        fuelPricePerGallon: this.fuelPricePerGallon,
        kmPerGallon: this.kmPerGallon,
        shiftStart: this.shiftStart,
        shiftEnd: this.shiftEnd,
        scopeSelection: this.scopeSelection,
        maxTrips: this.maxTrips,
        reloadMin: this.reloadMin,
      },
      view: {
        activeTab: this.activeTab,
        selectedTruckId: this.selectedTruckId,
        viewMode: this.viewMode,
        gridTruckId: this.gridTruckId,
        gridTilesWidth: this.gridTilesWidth,
        reviewGridRouteId: this.reviewGridRouteId,
        searchText: this.searchText,
        selectedTagKeys: [...this.selectedTagKeys],
        // Sin rutas en el gestor no hay decisión que conservar, y guardar una
        // sección vacía haría indistinguible "no revisó" de "revisó y no cambió
        // nada" al leer la foto.
        ...(this.reviewRoutes.length
          ? {
              review: {
                order: reviewOrderDelta(this.reviewRoutes),
                appliedSaved: [...this.appliedSavedOrder],
                open: this.reviewOpen,
                tab: this.reviewTab,
              },
            }
          : {}),
      },
      result: finished
        ? {
            state: this.state,
            errorMessage: this.errorMessage,
            loadedScopeLabel: this.loadedScopeLabel,
            officesWithoutGeo: this.officesWithoutGeo,
            scopeCounts: this.scopeCounts,
            officePlans: this.officePlans,
            dash: this.dash,
            routeOptions: this.routeOptions,
            geojsonByGroup: this.geojsonByGroup,
            allOrders: this.allOrders,
            weightByCustomer: this.weightByCustomer,
            weightByOrderId: this.weightByOrderId,
            subRoutesById: this.subRoutesById,
            truckColorIndex: this.truckColorIndex,
            routeTags: this.routeTags,
            baselineByGroupKey: this.baselineByGroupKey,
            averages: this.averages,
            assumedServiceMin: this.assumedServiceMin,
            assumedShift: this.assumedShift,
            groupsDone: this.groupsDone,
            groupsTotal: this.groupsTotal,
          }
        : undefined,
    };
    this.runCache.save(snapshot);
  }

  /* ---------------------- Selección de oficinas ---------------------- */

  /**
   * Carga el catálogo de oficinas elegibles y preselecciona las del filtro de la
   * pantalla. **No optimiza**: solo prepara la elección.
   *
   * Solo entran las que tienen geolocalización usable: la oficina ES el almacén
   * del que sale y al que vuelve el camión, así que sin coordenadas no hay plan
   * posible, no un plan peor.
   */
  async loadOfficeOptions(): Promise<void> {
    this.officesLoading = true;
    this.officesError = '';
    try {
      // Sin `safe()` a propósito: acá el fallo TIENE que ser visible. Ver
      // `officesError`.
      const res = await firstValueFrom(
        // Sin compañía en el filtro, el maestro devuelve todas las activas: cada
        // pedido ya trae la suya y la oficina es lo que acota el universo.
        this.salesOfficeService
          .findForRoutePlanning(this.data.companyMongoId || undefined)
          .pipe(timeout(20_000)),
      );
      const all = (res?.data ?? []).filter((o) => o.id != null);
      this.officesLoadedCount = all.length;
      this.officeOptions = all.filter((o) => officeGeoOf(o) !== null);
      this.officesWithoutGeo = all
        .filter((o) => officeGeoOf(o) === null)
        .map((o) => o.name ?? String(o.id));
      this.filterOfficeOptions();

      // Preselección: lo que la pantalla ya tenía filtrado. Si el filtro no
      // acota, se deja vacío a propósito — elegir es el punto de este paso.
      // Lo que el usuario eligió en una apertura anterior manda sobre el filtro:
      // si no, restaurar la selección no serviría de nada. Se acota igual a las
      // oficinas elegibles, porque el catálogo pudo cambiar.
      const valid = new Set(this.officeOptions.map((o) => o.id as number));
      if (this.restoredInputs) {
        this.selectedOfficeIds = this.selectedOfficeIds.filter((id) => valid.has(id));
        return;
      }
      const fromFilter = new Set(this.data.salesOfficeIds ?? []);
      this.selectedOfficeIds = fromFilter.size
        ? this.officeOptions.filter((o) => fromFilter.has(o.id as number)).map((o) => o.id as number)
        : [];
    } catch (err) {
      this.officesError = this.errorReasonOf(err, 'El maestro de oficinas');
      // Se limpia todo: dejar un catálogo a medias invitaría a optimizar con
      // menos oficinas de las que hay, sin que nada lo declare.
      this.officeOptions = [];
      this.filteredOfficeOptions = [];
      this.officesWithoutGeo = [];
      this.officesLoadedCount = 0;
      this.selectedOfficeIds = [];
    } finally {
      this.officesLoading = false;
    }
  }

  /**
   * Motivo legible de un fallo HTTP.
   *
   * Muestra el estado cuando no hay mensaje del backend: es un back office
   * interno, y "respondió 400" le dice al que reporta el problema muchísimo más
   * que "no se pudo cargar".
   */
  private errorReasonOf(err: unknown, subject: string): string {
    const detail = apiErrorDetail(err);
    if (detail) return detail;
    const e = err as { name?: string; status?: number; statusText?: string; message?: string };
    if (e?.name === 'TimeoutError') return `${subject}: tardó más de lo permitido en responder.`;
    if (e?.status) {
      return `${subject}: respondió ${e.status}${e.statusText ? ` (${e.statusText})` : ''}.`;
    }
    return e?.message || `${subject}: fallo desconocido.`;
  }

  /** Filtra el combo de oficinas por nombre o código. */
  filterOfficeOptions(): void {
    const q = this.officeSearch.trim().toLowerCase();
    this.filteredOfficeOptions = q
      ? this.officeOptions.filter((o) =>
          [o.name, o.externalKeys?.code].some((s) => (s ?? '').toLowerCase().includes(q)),
        )
      : this.officeOptions;
  }

  /** ¿Están todas las oficinas elegibles seleccionadas? */
  get allOfficesSelected(): boolean {
    return (
      this.officeOptions.length > 0 && this.selectedOfficeIds.length === this.officeOptions.length
    );
  }

  /** Opción "Todas": marca/desmarca los ids reales, no un valor centinela. */
  toggleAllOffices(): void {
    this.selectedOfficeIds = this.allOfficesSelected
      ? []
      : this.officeOptions.map((o) => o.id as number);
    // Mismo refresco que al cerrar el selector: territorios y camiones dependen
    // de las oficinas.
    this.onOfficesClosed(false);
  }

  /** Oficinas elegidas, en el orden del catálogo. */
  private get selectedOffices(): SalesOfficeAttributes[] {
    return this.officeOptions.filter((o) => this.selectedOfficeIds.includes(o.id as number));
  }

  /** ¿Se puede lanzar la corrida? Sin oficina no hay almacén y no hay plan. */
  get canGenerate(): boolean {
    return (
      this.selectedOfficeIds.length > 0 &&
      !this.officesLoading &&
      this.state !== 'loading' &&
      this.state !== 'optimizing'
    );
  }

  /** Vuelve al paso de selección para correr con otro alcance. */
  backToSelection(): void {
    if (this.isRunningGroups) return;
    this.reset();
    this.state = 'select';
  }

  /** Día planificado como `YYYY-MM-DD` (etiqueta del request). */
  get planDate(): string {
    return DateTime.fromJSDate(new Date(this.data.loadDate)).toISODate() ?? '';
  }

  /**
   * Los dos conjuntos que entran a la sugerencia.
   *
   * Planificar solo los que ya tienen viaje respondería "¿voy bien con lo que
   * asigné?", pero no "¿qué hago con lo que falta?" — y es la misma flota la que
   * tiene que llevar ambos. Se planifican juntos para que la propuesta se pueda
   * ejecutar tal cual: cada sub-ruta ve todas sus paradas del día, asignadas y
   * pendientes, y su `unassigned` mide la capacidad que realmente falta.
   *
   * Los dos conjuntos son **disjuntos** por construcción (`trip != null` y
   * `trip == null`), así que fusionarlos no puede duplicar una parada.
   *
   * Los "sin atención pasada" quedan fuera: son entregas de viajes que YA
   * cerraron, un problema de reprogramación —no de ruteo de la jornada— y meter
   * tres meses de pendientes desvirtuaría el plan del día.
   */
  private planningScopesFor(salesOfficeId: number): PlanningScope[] {
    const day = this.data.dayRange;
    const dayRange = { start: day.start, end: day.end };
    const scopes: PlanningScope[] = [];

    if (this.scopeSelection !== 'without-trip') {
      // Este SÍ va por el kit de optimización, y a propósito: la pestaña "Pedidos
      // con ruta y viaje" consulta `getTripsByLoadDocs`, que devuelve el payload
      // agrupado POR VIAJE y no una lista de órdenes. Sus predicados ya son
      // idénticos —`loadDateTime` del día, `deliveryDateTime != null`,
      // `trip != null`, `statusId == 1`—, así que no hay divergencia que cerrar:
      // solo habría que remapear una respuesta con otra forma, sin ganar nada.
      scopes.push({
        key: 'with-trip',
        label: 'Con viaje de la fecha de carga',
        page: (page) =>
          this.deliveryOrdersService.findForPlanning({
            companyId: this.data.companyId,
            salesOfficeId,
            typeCode: 'DT003',
            date: { field: 'loadDateTime', ...dayRange },
            requireDate: 'deliveryDateTime',
            routeIds: this.effectiveTerritoryIds(),
            subRouteIds: this.data.subRouteIds,
            customerIds: this.data.customerIds,
            documentClassCodes: this.data.documentClassCodes,
            hasTrip: true,
            onlyPending: true,
            options: { currentPage: page, limitPerPage: ORDERS_PAGE_SIZE },
          }),
      });
    }

    if (this.scopeSelection !== 'with-trip') {
      scopes.push({
        key: 'without-trip',
        label: 'Sin viaje',
        page: (page) => this.pendingOrdersPage(salesOfficeId, page),
      });
    }

    return scopes;
  }

  /**
   * Una página de los pedidos **sin viaje**, con la consulta de la PANTALLA.
   *
   * Es la misma que alimenta la pestaña "Pedidos sin viaje", así que el modal no
   * puede volver a desalinearse: no hay una segunda definición de qué pedidos
   * son. De ahí salieron los tres bugs que hubo que corregir —el campo de fecha
   * equivocado, la condición faltante del otro conjunto, y un filtro por "fecha
   * de carga vacía" que el backend no responde en 20 s—.
   *
   * No se reusan las filas que la grilla ya tiene: la tabla pagina de 50 en 50 y
   * `data()` es UNA página, mientras el plan necesita todas (262 en la corrida
   * medida). Pasarle la tabla planificaría 50 sin decirlo.
   *
   * `date` viaja porque el método lo exige, y **se ignora**: sus dos líneas de
   * filtro por fecha están comentadas (`loads.service.ts`). Un pedido sin viaje no
   * tiene fecha de carga en la que confiar, y por eso la pestaña dejó de acotar.
   */
  private pendingOrdersPage(
    salesOfficeId: number,
    page: number,
  ): Observable<IPaginationResultFrequencies<OrderAttributes>> {
    const day = this.data.dayRange;
    // Sin compañía en el filtro se cae al kit: `findOrdersHoutTrip` la exige y
    // relajarla ahí cambiaría el aislamiento de datos de la pestaña, que es una
    // decisión de producto y no de código.
    if (this.data.companyId == null) {
      return this.deliveryOrdersService.findForPlanning({
        salesOfficeId,
        typeCode: 'DT003',
        routeIds: this.effectiveTerritoryIds(),
        subRouteIds: this.data.subRouteIds,
        customerIds: this.data.customerIds,
        documentClassCodes: this.data.documentClassCodes,
        hasTrip: false,
        onlyPending: true,
        options: { currentPage: page, limitPerPage: ORDERS_PAGE_SIZE },
      });
    }
    return this.loadsService.findOrdersWithoutTrip(
      'DT003',
      { start: day.start, end: day.end },
      this.data.companyId,
      undefined,
      [salesOfficeId],
      undefined,
      undefined,
      undefined,
      this.data.subRouteIds ?? [],
      this.data.customerIds ?? [],
      this.data.documentClassCodes,
      { currentPage: page, limitPerPage: ORDERS_PAGE_SIZE, search: '' },
      // Sin el overlay global: el diálogo ya muestra su propio progreso y esta
      // consulta se pagina varias veces por corrida.
      true,
    );
  }

  /** Opciones del selector de conjuntos, en el orden en que se muestran. */
  readonly scopeOptions: ReadonlyArray<{ value: ScopeSelection; label: string; hint: string }> = [
    {
      value: 'both',
      label: 'Ambos',
      hint: 'Lo asignado al día más lo que falta repartir',
    },
    {
      value: 'with-trip',
      label: 'Solo pedidos con viaje',
      hint: 'Los que ya tienen viaje con la fecha de carga elegida',
    },
    {
      value: 'without-trip',
      label: 'Solo pedidos sin viaje',
      hint: 'Los de la fecha elegida y los que todavía no tienen ninguna',
    },
  ];

  /** Total de pedidos que entraron a la corrida. */
  get totalOrdersLoaded(): number {
    return this.scopeCounts['with-trip'] + this.scopeCounts['without-trip'];
  }

  /** Etiqueta del progreso para la pantalla de espera. */
  get progressLabel(): string {
    if (this.groupsTotal > 0) {
      // Sin repetir el sustantivo: el prefijo ya dice "rutas".
      return `Generando rutas — ${this.groupsDone} de ${this.groupsTotal}…`;
    }
    return this.progressOfficeName
      ? `Cargando pedidos — ${this.progressOfficeName} (${this.progressIndex}/${this.progressTotal})…`
      : 'Cargando pedidos…';
  }

  /* --------------------------- Corrida ------------------------------- */

  async run(): Promise<void> {
    const offices = this.selectedOffices;
    if (!offices.length) {
      this.handlerMessage.warning({
        message: 'Selecciona al menos una oficina de ventas para optimizar las rutas.',
      });
      this.state = 'select';
      return;
    }

    this.reset();
    // Se congela acá el valor de la corrida: si el usuario mueve el selector
    // mientras corre, o después, el resultado sigue describiéndose con el número
    // con el que realmente se calculó.
    this.assumedServiceMin = resolveServiceMin(this.selectedServiceMin);
    this.assumedShift = resolveShift(this.shiftStart, this.shiftEnd);
    this.state = 'loading';
    try {
      const loaded = await this.loadOrdersByOffice(offices);
      if (!loaded.some((l) => l.orders.length)) {
        // Se publican las secciones aunque no haya plan: los motivos por oficina
        // viven en ellas, y sin esto `officePlans` quedaba vacío y el motivo se
        // perdía justo en el caso en que hace falta.
        this.officePlans = loaded.map((l) => l.view);
        const failed = loaded.filter((l) => l.view.error);
        this.state = failed.length ? 'error' : 'empty';
        // El motivo, no una frase genérica. "No se pudieron cargar los pedidos de
        // las oficinas elegidas" no dice si fue un 400, un timeout o permisos, y
        // era lo único que se veía.
        if (failed.length) {
          this.errorMessage = failed
            .map((l) => (loaded.length > 1 ? `${l.view.office.name} — ${l.view.error}` : l.view.error))
            .join(' · ');
        }
        return;
      }

      await this.loadSubRouteMaster(
        offices.map((o) => o.id as number).filter((id) => id != null),
      );
      this.state = 'optimizing';
      await this.runPartitioned(loaded);
      this.finalize(loaded.map((l) => l.view));
    } catch (err) {
      this.handleError(err);
    }
  }

  /**
   * Carga los pedidos de cada oficina, paginando en memoria. Un fallo en una
   * oficina se registra en su sección sin abortar a las demás.
   */
  private async loadOrdersByOffice(
    offices: SalesOfficeAttributes[],
  ): Promise<Array<{ view: OfficePlanView; orders: OrderAttributes[] }>> {
    this.loadedScopeLabel = offices.map((o) => o.name).join(', ');
    this.scopeCounts = { 'with-trip': 0, 'without-trip': 0 };
    const loaded: Array<{ view: OfficePlanView; orders: OrderAttributes[] }> = [];

    for (const [i, office] of offices.entries()) {
      this.progressOfficeName = office.name ?? '';
      this.progressIndex = i + 1;
      this.progressTotal = offices.length;

      const view = createOfficePlanView(office);
      try {
        const { orders, failures } = await this.loadAllOrders(office.id as number);
        view.ordersCount = orders.length;
        if (failures.length && !orders.length) {
          // Nada que planificar Y hubo fallos: es un error, con su motivo.
          view.error = `No se pudieron cargar los pedidos: ${failures.join(' · ')}`;
        } else if (failures.length) {
          // Se planifica con lo que hay, pero se DECLARA lo que falta: un plan
          // hecho sobre un subconjunto silencioso es peor que ninguno.
          view.ordersWarning = `Falta parte de los pedidos — ${failures.join(' · ')}`;
        }
        this.allOrders.push(...orders);
        loaded.push({ view, orders });
      } catch (err) {
        // Solo llega acá lo que NO es un fallo de consulta (los alcances ya se
        // contienen adentro). El motivo se escribe: un `catch` vacío era la razón
        // por la que esto no dejaba rastro ni en la consola.
        view.error = this.errorReasonOf(err, 'La carga de pedidos');
        loaded.push({ view, orders: [] });
      }
    }
    this.buildWeightByCustomer();
    // El peso POR PEDIDO, para los que no van a viajar en ninguna petición: sin
    // esto caen al total de su cliente y la brecha lo cuenta una vez por pedido.
    this.seedOrderWeights();
    return loaded;
  }

  /**
   * Los pedidos a planificar de una oficina: **los dos conjuntos fusionados**.
   *
   * Se piden en dos consultas y se unen acá en lugar de armar una sola con un
   * `OR`, porque los dos conjuntos no comparten filtro de fecha: los que ya
   * tienen viaje se acotan por `loadDateTime` del día, y los que no lo tienen no
   * traen fecha de carga todavía. Un `OR` anidado con este constructor de
   * queries sería frágil y, peor, ilegible; dos consultas disjuntas
   * (`trip != null` / `trip == null`) no se pueden pisar entre sí.
   *
   * La deduplicación por `_id` es un cinturón: los conjuntos son disjuntos por
   * definición, pero si el backend cambiara la semántica de `trip`, una parada
   * repetida rompería el peso por cliente sin dar ninguna señal.
   */
  private async loadAllOrders(
    salesOfficeId: number,
  ): Promise<{ orders: OrderAttributes[]; failures: string[] }> {
    const byId = new Map<string, OrderAttributes>();
    const failures: string[] = [];

    // Los conjuntos van EN PARALELO: son consultas independientes contra el mismo
    // servicio, y en secuencia el barato esperaba al caro — medido, ~47 s de
    // órdenes en fila cuando se seleccionan ambos.
    //
    // Cada alcance se contiene solo (un fallo no se lleva a la oficina), y la
    // FUSIÓN es secuencial en el orden declarado de los alcances: con la
    // deduplicación por `_id`, fusionar en orden de llegada haría que el conteo
    // por conjunto dependiera de qué respuesta ganó la carrera.
    const settled = await Promise.all(
      this.planningScopesFor(salesOfficeId).map(async (scope) => {
        try {
          return { scope, rows: await this.loadScopeOrders(scope), err: undefined };
        } catch (err) {
          return { scope, rows: [] as OrderAttributes[], err };
        }
      }),
    );

    for (const { scope, rows, err } of settled) {
      if (err !== undefined) {
        failures.push(this.errorReasonOf(err, scope.label));
        continue;
      }
      let added = 0;
      for (const order of rows) {
        const id = orderIdOf(order);
        if (!id || byId.has(id)) continue;
        byId.set(id, order);
        added++;
      }
      this.scopeCounts[scope.key] += added;
    }
    return { orders: [...byId.values()], failures };
  }

  /**
   * Todas las páginas de UN conjunto.
   *
   * La página 1 va sola y primero: además de filas trae el TOTAL, que es lo que
   * permite saber cuántas colas hay y dispararlas en paralelo, en vez de
   * descubrirlas de a una. Las colas van con `runWithLimit` y techo bajo (ver
   * `ORDERS_PAGE_CONCURRENCY`).
   *
   * Una cola que falla tira el conjunto ENTERO, no devuelve lo que alcanzó: un
   * subconjunto silencioso planificaría de menos, y el aviso por alcance de
   * `loadAllOrders` ya explica qué faltó y por qué.
   */
  private async loadScopeOrders(scope: PlanningScope): Promise<OrderAttributes[]> {
    const first = await firstValueFrom(scope.page(1).pipe(timeout(ORDERS_PAGE_TIMEOUT_MS)));
    const firstEnvelope = envelopeOf(first);
    const all = [...(firstEnvelope?.data ?? [])];
    if (!all.length) return all;

    const total = firstEnvelope?.pageInfo?.[0]?.total ?? all.length;
    const pageCount = Math.min(Math.ceil(total / ORDERS_PAGE_SIZE), ORDERS_MAX_PAGES);
    if (pageCount <= 1) return all;

    const tailPages = Array.from({ length: pageCount - 1 }, (_u, i) => i + 2);
    const settled = await runWithLimit(
      tailPages.map(
        (page) => () => firstValueFrom(scope.page(page).pipe(timeout(ORDERS_PAGE_TIMEOUT_MS))),
      ),
      ORDERS_PAGE_CONCURRENCY,
    );
    // En orden de índice, no de llegada: `runWithLimit` ya lo garantiza, así que
    // el resultado es idéntico al del bucle secuencial que reemplaza.
    for (const result of settled) {
      if (result.status === 'rejected') throw result.error;
      all.push(...(envelopeOf(result.value)?.data ?? []));
    }
    return all;
  }

  /**
   * Baseline de UN grupo: el orden del request (pre-optimización) evaluado por el
   * backend. NUNCA lanza — sin baseline solo falta ese grupo en el KPI.
   */
  private async loadGroupBaseline(groupKey: string, request: PlanRequest): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.routeOptimizationService
          .evaluateRoute({
            warehouse: request.warehouse,
            stops: request.orders.map((o) => ({ id: o.id, lat: o.lat, lon: o.lon })),
            round_trip: true,
          })
          .pipe(timeout(30_000)),
      );
      this.baselineByGroupKey.set(groupKey, {
        durationMin: res.total_duration_min,
        distanceKm: res.total_distance_km,
      });
    } catch (err) {
      // Cobertura parcial declarada en el KPI; la corrida no depende de esto.
      // Pero el MOTIVO va a la consola: un catch mudo ya nos costó una tarde de
      // "los logs no muestran nada" en la carga de pedidos.
      console.warn(`[evaluate-route] baseline de ${groupKey} no disponible:`, err);
    }
  }

  /**
   * Maestro de sub-rutas de unas oficinas: **una sola vez por alcance**, no una
   * por grupo. De acá sale TODO lo que la corrida sabe de la flota — el catálogo
   * de territorios, los camiones asignados, y el camión/turno/ventanas/
   * multi-viaje de cada grupo.
   *
   * Se salta la consulta si ya está cargado para las mismas oficinas: al elegir
   * el alcance se carga para los catálogos y la corrida volvería a pedir lo
   * mismo. El aviso de fallo es el que ya estaba — sin maestro no hay flota real
   * y todo cae al respaldo, así que hay que decirlo.
   */
  private async loadSubRouteMaster(officeIds: number[]): Promise<void> {
    const ids = (officeIds ?? []).filter((id) => id != null);
    const key = ids.slice().sort((a, b) => a - b).join(',');
    if (key === this.masterOfficesKey && this.subRoutesById.size) return;
    this.subRoutesById = new Map();
    this.masterOfficesKey = key;
    const res = await this.safe(this.subRouteService.findForPlanning(ids), null, 20_000);
    if (!res) {
      // Sin datos no se puede afirmar que el alcance esté cargado: un reintento
      // (elegir otra oficina y volver) tiene que volver a pedirlo.
      this.masterOfficesKey = '';
      this.handlerMessage.warning({
        message:
          'No se pudo cargar la configuración de las rutas: se planifica con una capacidad y jornada por defecto.',
      });
      return;
    }
    for (const sr of res.data ?? []) {
      const id = (sr as unknown as { _id?: unknown })._id;
      if (id != null) this.subRoutesById.set(String(id), sr);
    }
  }

  /** Configuración de respaldo: lo que se asume donde el maestro calla. */
  private fleetFallback(): FleetFallback {
    return {
      capacityLb: DEFAULT_CAPACITY_LB,
      shift: this.assumedShift,
      // `1` = sin recargas: el modo single evita mandar un `max_trips` que el
      // resolver tendría que des-hacer.
      tripMode: this.maxTrips > 1 ? 'reload' : 'single',
      maxTrips: this.maxTrips,
      reloadMin: this.reloadMin,
    };
  }

  /**
   * Parte las órdenes de cada oficina en grupos (sub-ruta → municipio →
   * residual), resuelve la flota de cada uno desde el maestro y los planifica
   * con concurrencia acotada, pintando cada resultado apenas llega.
   */
  private async runPartitioned(
    loaded: Array<{ view: OfficePlanView; orders: OrderAttributes[] }>,
  ): Promise<void> {
    const fallback = this.fleetFallback();
    const weekday = DateTime.fromJSDate(new Date(this.data.loadDate)).weekday; // 1 = lunes
    const tasks: Array<() => Promise<void>> = [];

    // Se publican ANTES de correr: el render progresivo necesita las secciones
    // ya montadas para poder ir llenándolas grupo a grupo.
    this.officePlans = loaded.map((l) => l.view);

    for (const { view, orders } of loaded) {
      if (view.error || !orders.length) continue;

      // Solo el maestro de ESTA oficina: cruzar oficinas asignaría pedidos a
      // camiones que salen de otro almacén.
      const master = [...this.subRoutesById.values()].filter(
        (sr) => (sr as unknown as { salesOffice?: { id?: number } }).salesOffice?.id === view.officeId,
      );
      // La FLOTA es el punto de partida: solo los territorios con camión asignado
      // se planifican. El alcance viaja a las dos —detección y flota—: mirar la
      // disputa acotada y la flota sin acotar dejaría al camión irse en dos
      // peticiones por el hueco entre los dos alcances.
      const scope = this.effectiveTerritoryIds();
      const excludedVehicleIds = [...this.excludedVehicleIdSet()];

      // Pasada 1 — sin resolver disputas: solo para CONTAR. La decisión es "el
      // territorio con más pedidos", y esos pedidos no se pueden saber antes de
      // repartirlos. Es una partición pura, sin HTTP: barata de correr dos veces y
      // mucho más clara que arreglar la primera a mano.
      const candidates = fleetByTerritory(master, { excludedVehicleIds, territoryIds: scope });
      const demand = this.demandByTerritory(orders, candidates, master);

      // La disputa se resuelve y se DECLARA. Automatizar sí, en silencio no: es la
      // misma convención que las órdenes asignadas a su ruta por territorio.
      const resolutions = resolveDuplicateVehicles(
        crossTerritoryConflicts(master, scope),
        demand,
      );
      this.duplicateVehicles = resolutions;
      view.fleetWarnings = resolutions.map(
        (r) =>
          `${r.label} → ${r.territoryName} (${r.orders} ${r.orders === 1 ? 'pedido' : 'pedidos'}): ` +
          `varias rutas lo declaran y se asignó al de más pedidos, así que ` +
          `${r.lost.map((l) => `${l.territoryName} (${l.orders})`).join(', ')} ` +
          `${r.lost.length === 1 ? 'queda' : 'quedan'} sin él.`,
      );

      // Pasada 2 — con la disputa resuelta: el camión solo en su territorio ganador.
      const fleet = fleetByTerritory(master, {
        excludedVehicleIds,
        territoryIds: scope,
        assignedTerritoryByVehicle: new Map(resolutions.map((r) => [r.vehicleId, r.territoryId])),
      });
      const fleetTerritoryIds = fleet.map((f) => f.territoryId);
      // Los territorios que PERDIERON su camión: sus pedidos salen con ese motivo y
      // no con "no tiene camión asignado", que se arregla de otra forma.
      const takenTerritories = new Map<number, string>();
      for (const r of resolutions) {
        for (const lost of r.lost) takenTerritories.set(lost.territoryId, r.territoryName);
      }

      const { groups, excluded, coverage, unplanned, assignments, groupKeyByOrderId } =
        partitionByTerritory(orders, {
          fleetTerritoryIds,
          masterSubRoutes: master,
          takenTerritories,
          // Separa "su ruta no tiene vehículo" de "no tiene ruta": dos causas con
          // arreglos distintos que sin este conjunto caían bajo el mismo motivo.
          territoriesWithRoute: territoriesFromSubRoutes(master).map((t) => t.id),
        });
      view.coverage = coverage;
      // La resolución por territorio se declara, pedido por pedido.
      //
      // Solo las que resolvió la ESCALERA: `assignments` trae también el caso trivial
      // —el territorio que declara la propia ruta—, porque el recorrido de decisión de
      // cada parada lo necesita. Esta insignia responde otra pregunta, "qué hubo que
      // resolver", y listar la oficina entera la haría ilegible. El filtro vive acá y
      // no en la partición: es de este consumidor.
      view.autoAssignedNotes = assignments
        .filter((a) => a.method !== 'sub-route')
        .map((a) => {
          // La MISMA regla que las paradas: si acá se compusiera a mano, el mismo
          // cliente aparecería escrito distinto en la nota y en su fila.
          const { name } = clientLabel(
            a.order.shipTo as ClientLike | undefined,
            orderIdOf(a.order),
          );
          const how =
            a.method === 'territory'
              ? 'su territorio tiene flota'
              : 'territorio compartido, el más cercano';
          const dist = a.distanceKm !== undefined ? ` (${a.distanceKm} km)` : '';
          return `${name} → ${a.territoryName} — ${how}${dist}`;
        });
      // El routeKey de los puntos (tags, no-ubicados) respeta el grupo real. Con
      // partición por territorio la clave YA NO se puede derivar del pedido solo,
      // así que este mapa es la única fuente.
      for (const [orderId, key] of groupKeyByOrderId) {
        this.autoGroupKeyByOrderId.set(orderId, key);
      }
      // El primer tramo del recorrido de decisión de cada parada. Incluye el caso
      // trivial —el territorio que declara la ruta—, que es el de la mayoría.
      for (const a of assignments) {
        const id = orderIdOf(a.order);
        if (id) this.assignmentByOrderId.set(id, a);
      }
      const pointOf = this.makePointOf(orders);
      view.excludedPoints = excluded.map((e) => pointOf(orderIdOf(e.order)));
      for (const o of orders) {
        const id = orderIdOf(o);
        if (id) this.decisionPointByOrderId.set(id, pointOf(id));
      }
      view.fleetGaps = this.buildFleetGaps(unplanned, pointOf);
      // Los pedidos que no cayeron en ningún territorio con flota son justamente
      // los de la brecha: la lista les da la misma entrada directa que a los
      // excluidos, sin enterrarlos en la tarjeta de un grupo que ya no existe.
      view.withoutRoutePoints = view.fleetGaps.flatMap((g) => g.points);

      const inputs = fleet
        .filter((f) => groups.some((g) => g.territoryId === f.territoryId))
        .map((f) => {
          const group = groups.find((g) => g.territoryId === f.territoryId);
          const resolved = resolveTerritoryFleet({
            territoryId: f.territoryId,
            territoryName: f.territoryName,
            units: f.units,
            weekday,
            fallback,
          });
          // El rótulo del grupo es la RUTA, no el territorio. Con varias rutas en el
          // territorio cae al nombre del territorio: el grupo agrupa las paradas del
          // territorio, y nombrarlo con una de sus rutas afirmaría que abarca menos.
          const routeNames = f.units
            .map((u) => (u.subRoute as unknown as { name?: unknown }).name)
            .map((n) => (n === undefined || n === null ? '' : String(n)))
            .filter((n) => !!n);
          view.groups.push({
            key: group.key,
            label: routeNames.length === 1 ? routeNames[0] : f.territoryName,
            territoryName: f.territoryName,
            origin: 'sub-route',
            territoryId: f.territoryId,
            subRouteId: resolved.trucks[0]?.subRouteId,
            stops: group.orders.length,
            status: 'pending',
            fleetFromMaster: resolved.trucks.every((t) => t.capacitySource === 'master'),
            truckCount: resolved.trucks.length,
            // Sin camión no hay grupo, así que un camión excluido ya no manda el
            // grupo al respaldo: lo manda a la brecha, con el territorio entero.
            vehicleUnavailable: false,
            warnings: resolved.warnings,
            // Se asigna acá, en orden de creación, no al llegar la respuesta: las
            // corridas son concurrentes y el color tiene que viajar en el request.
            color: truckColorAt(this.truckColorIndex++),
          });
          // Los parámetros con los que se planifica cada camión del territorio, con
          // la procedencia de cada uno. Antes se armaban, se enviaban y se tiraban:
          // sin ellos una capacidad supuesta se mostraba igual que una declarada.
          for (const rt of resolved.trucks) {
            if (!rt.subRouteId) continue;
            this.paramsBySubRouteId.set(rt.subRouteId, {
              capacityLb: rt.truck.capacity,
              capacitySource: rt.capacitySource,
              shift: rt.truck.shift,
              shiftSource: rt.shiftSource,
              maxTrips: rt.truck.max_trips ?? 1,
              reloadMin: rt.truck.reload_min ?? 0,
              tripsSource: rt.tripsSource,
              // La atención es de la CORRIDA, no del camión: un supuesto de pantalla
              // que se aplica a todas las paradas por igual.
              serviceMin: this.assumedServiceMin,
              window: resolved.window,
              fleetSize: resolved.trucks.length,
              territoryName: f.territoryName,
            });
          }
          return {
            key: group.key,
            orders: group.orders,
            trucks: resolved.trucks.map((t) => t.truck),
            window: resolved.window,
          };
        });

      for (const req of toGroupPlanRequests(inputs, {
        date: this.planDate,
        salesOffice: view.office,
        weightUnit: 'lb',
        groupByCustomer: true,
        serviceMin: this.assumedServiceMin,
      })) {
        const group = view.groups.find((g) => g.key === req.key);
        if (!group) continue;
        group.stops = req.request.orders.length;
        this.indexPlanWeights(req.request);
        tasks.push(() => this.runGroupPlan(view, group, req.request, orders));
      }
    }

    this.groupsTotal = tasks.length;
    this.groupsDone = 0;
    await runWithLimit(tasks, PLAN_CONCURRENCY);

    // El dimensionamiento va DESPUÉS y aparte: es información adicional sobre lo
    // que no se pudo planificar, así que no puede retrasar ni tumbar las rutas
    // reales. Mismo criterio que el baseline de `/evaluate-route`.
    await runWithLimit(
      loaded.flatMap(({ view }) =>
        view.fleetGaps
          .filter((gap) => gap.territoryId !== undefined && gap.points.length > 0)
          .map((gap) => () => this.sizeFleetGap(view, gap)),
      ),
      PLAN_CONCURRENCY,
    );
  }

  /**
   * Dimensiona la flota que falta en UN territorio de la brecha.
   *
   * NUNCA lanza: sin dimensionamiento el territorio sigue en la brecha, solo sin
   * cifra, y el resultado declara la cobertura parcial. Un fallo acá no puede
   * costar las rutas que sí se planificaron con flota real.
   *
   * Va en modo `fleet` y no `trucks` porque el contrato exige **exactamente uno**
   * de los dos: no es una preferencia de diseño, es una petición aparte.
   */
  private async sizeFleetGap(view: OfficePlanView, gap: FleetGap): Promise<void> {
    try {
      const orders = gap.points
        .map((p) => this.allOrders.find((o) => orderIdOf(o) === basePlanOrderId(p.orderId)))
        .filter((o): o is OrderAttributes => !!o);
      if (!orders.length) return;

      const sizing = sizingFleet(
        // Los moldes son la flota REAL de la oficina: "con los camiones que
        // tenés". Salen del MAESTRO y no de `this.vehicles`: ese es el catálogo
        // de la UI y está vacío si nadie abrió el combo, así que usarlo hacía
        // que una oficina CON camiones dimensionara con la capacidad supuesta.
        assignedTrucksFromSubRoutes(
          [...this.subRoutesById.values()].filter(
            (sr) =>
              (sr as unknown as { salesOffice?: { id?: number } }).salesOffice?.id === view.officeId,
          ),
        ).map((t) => t.vehicle),
        gap.weightLb,
        this.assumedShift,
        DEFAULT_CAPACITY_LB,
      );
      const [req] = toGroupPlanRequests(
        [{ key: gap.territoryName, orders, trucks: [{ id: 'sizing', capacity: 1, shift: this.assumedShift }] }],
        {
          date: this.planDate,
          salesOffice: view.office,
          weightUnit: 'lb',
          groupByCustomer: true,
          serviceMin: this.assumedServiceMin,
        },
      );
      if (!req) return;
      // El mapper arma el request en modo `trucks`; acá se cambia por `fleet`,
      // que es lo único que distingue una estimación de un plan.
      const { trucks: _ignored, ...body } = req.request;
      const res = await this.safe(
        this.routeOptimizationService.plan({ ...body, fleet: sizing.fleet }),
        null,
        GROUP_PLAN_TIMEOUT_MS,
      );
      gap.sizing = readFleetSizing(res, sizing.molds, sizing.capacityAssumed);
      if (!gap.sizing) {
        view.fleetWarnings = [
          ...view.fleetWarnings,
          `No se pudo dimensionar la flota que falta en ${gap.territoryName}.`,
        ];
      }
    } catch (err) {
      // El motivo va a la consola: un catch mudo ya nos costó una tarde.
      console.warn(`[fleet-sizing] ${gap.territoryName} no dimensionado:`, err);
    }
  }

  /**
   * Pedidos y peso por territorio candidato, para decidir una disputa de camión.
   *
   * Se obtiene de una partición **sin resolver** las disputas: la decisión es "el
   * territorio con más pedidos" y esos pedidos no se pueden saber antes de
   * repartirlos. `partitionByTerritory` es pura y sin HTTP, así que correrla dos
   * veces cuesta un par de recorridos sobre las órdenes — mucho más claro que
   * arreglar a mano la primera.
   */
  private demandByTerritory(
    orders: OrderAttributes[],
    candidates: readonly { territoryId: number }[],
    master: readonly SubRouteAttributes[],
  ): Map<number, TerritoryDemand> {
    const { groups } = partitionByTerritory(orders, {
      fleetTerritoryIds: candidates.map((c) => c.territoryId),
      masterSubRoutes: master,
    });
    return new Map(
      groups.map((g) => [
        g.territoryId,
        {
          orders: g.orders.length,
          weightLb: g.orders.reduce(
            (n, o) => n + (this.weightByCustomer.get(customerIdOf(o)) ?? 0),
            0,
          ),
        },
      ]),
    );
  }

  /**
   * La brecha de flota, agrupada por territorio.
   *
   * Reemplaza al KPI de "rutas con flota de respaldo": esa condición dejó de
   * existir cuando un territorio sin camión dejó de planificarse con una
   * capacidad inventada. El dato accionable pasó a ser cuál territorio no tiene
   * con qué salir, porque se arregla asignando un camión en el maestro, no
   * ajustando la corrida.
   *
   * Los pedidos sin territorio identificable se agrupan bajo un solo renglón por
   * motivo: nombrar un territorio que no se pudo determinar sería inventarlo.
   */
  private buildFleetGaps(
    unplanned: readonly UnplannedOrder[],
    pointOf: (id: string) => DeliveryPoint,
  ): FleetGap[] {
    // Cada causa dice QUÉ pasa y DÓNDE se arregla. Antes los motivos nombraban el
    // estado interno de la partición —si el pedido traía su sub-ruta o no—, así que
    // la misma causa aparecía bajo dos etiquetas y una etiqueta escondía tres.
    //
    // La etiqueta nombra el ESTADO DEL DATO, no el sujeto: los cinco motivos empezaban
    // por "Clientes...", que es justo lo que el contador del renglón ya cuenta al lado
    // (4 clientes · 6 pedidos · 6.170 lb). Lo que el sujeto no dice, y decide a qué
    // pantalla ir, es si la ficha EXISTE y le falta un campo o si no existe.
    const CAUSES: Record<UnplannedReason, { reason: string; action: string }> = {
      route_without_vehicle: {
        reason: 'Su ruta de entrega existe pero no tiene vehículo asignado',
        action: 'abrir la ruta en el maestro de rutas y asignarle un vehículo',
      },
      territory_without_route: {
        reason: 'Su territorio no está en ninguna ruta de entrega',
        action: 'asignarle una ruta de entrega al territorio, en el maestro de rutas',
      },
      client_without_territory: {
        reason: 'El cliente no tiene territorio cargado',
        action: 'cargar el territorio en la dirección del cliente',
      },
      preventa_subroute: {
        reason: 'Solo tienen ruta de preventa, ninguna de entrega',
        action: 'asignarles una ruta de entrega en el maestro de rutas',
      },
      vehicle_taken: {
        reason: 'Su camión existe, pero se lo llevó otro territorio',
        action: 'un mismo camión está en dos rutas: resolver el duplicado en el maestro',
      },
    };
    const byKey = new Map<string, FleetGap & { clientIds: Set<string> }>();
    for (const item of unplanned ?? []) {
      const territoryName =
        item.territoryName || (item.territoryId !== undefined ? String(item.territoryId) : '');
      // La clave incluye la causa: el mismo territorio puede aportar pedidos por dos
      // causas distintas, y fundirlos escondería una de las dos.
      const key = `${item.reason}|${item.territoryId ?? ''}`;
      // Una foto restaurada de una caché anterior trae motivos que ya no existen: se
      // muestra el motivo tal cual en vez de dejar el renglón sin texto.
      const cause = CAUSES[item.reason] ?? { reason: String(item.reason), action: '' };
      const gap =
        byKey.get(key) ??
        {
          territoryId: item.territoryId,
          territoryName: territoryName || 'Sin territorio determinado',
          routeName: this.routeNameOfTerritory(item.territoryId),
          reason: cause.reason,
          action: cause.action,
          causeKey: String(item.reason),
          clients: 0,
          orders: 0,
          weightLb: 0,
          points: [],
          clientIds: new Set<string>(),
        };
      const point = pointOf(orderIdOf(item.order));
      gap.orders += 1;
      // El peso por PEDIDO. Antes caía al total del cliente y con dos pedidos de un
      // mismo cliente la brecha lo sumaba dos veces (ver `seedOrderWeights`).
      gap.weightLb += point.weightLb ?? 0;
      gap.clientIds.add(customerIdOf(item.order) || orderIdOf(item.order));
      gap.clients = gap.clientIds.size;
      gap.points.push(point);
      byKey.set(key, gap);
    }
    return [...byKey.values()]
      .map(({ clientIds: _ignored, ...gap }) => gap)
      .sort((a, b) => b.orders - a.orders || a.territoryName.localeCompare(b.territoryName));
  }

  /**
   * Renglones de la brecha agrupados por CAUSA, para poder decirla una sola vez.
   *
   * Antes la causa se repetía renglón por renglón: con cinco territorios en el mismo
   * problema había que leer cinco veces el mismo texto para descubrir que compartían
   * causa, y dos territorios con causas distintas se leían igual.
   */
  fleetGapCausesOf(view: OfficePlanView): Array<{
    reason: string;
    action: string;
    clients: number;
    orders: number;
    weightLb: number;
    gaps: FleetGap[];
  }> {
    const byCause = new Map<string, {
      reason: string;
      action: string;
      clients: number;
      orders: number;
      weightLb: number;
      gaps: FleetGap[];
    }>();
    for (const gap of view.fleetGaps ?? []) {
      const entry =
        byCause.get(gap.causeKey) ??
        { reason: gap.reason, action: gap.action, clients: 0, orders: 0, weightLb: 0, gaps: [] };
      entry.clients += gap.clients;
      entry.orders += gap.orders;
      entry.weightLb += gap.weightLb;
      entry.gaps.push(gap);
      byCause.set(gap.causeKey, entry);
    }
    return [...byCause.values()].sort((a, b) => b.orders - a.orders);
  }

  /** Los tres números de un renglón o de una causa, en una línea. */
  fleetGapCountsLabel(counts: { clients: number; orders: number; weightLb: number }): string {
    const clients = `${counts.clients} ${counts.clients === 1 ? 'cliente' : 'clientes'}`;
    const orders = `${counts.orders} ${counts.orders === 1 ? 'pedido' : 'pedidos'}`;
    const weight = `${Math.round(counts.weightLb).toLocaleString('en-US')} lb`;
    return `${clients} · ${orders} · ${weight}`;
  }

  /**
   * Titular del bloque de brecha: cuántos pedidos son y qué les pasa HOY.
   *
   * Decía "no se planificaron", que describe lo que hizo el sistema. Lo que hace
   * falta saber es la consecuencia: esos clientes hoy no reciben. Y el total va acá
   * porque hasta ahora solo estaba desglosado por causa, así que el tamaño del
   * problema había que sumarlo a mano.
   *
   * NO nombra ninguna causa, y eso es un requisito: el titular está sobre los cinco
   * grupos y tres de las cinco causas no son un vehículo faltante, así que nombrar
   * una haría leer las otras como subcasos de un problema que no tienen.
   */
  fleetGapHeadlineOf(view: OfficePlanView): string {
    const orders = (view.fleetGaps ?? []).reduce((n, g) => n + g.orders, 0);
    return orders === 1
      ? 'Este pedido quedó fuera del plan: hoy no sale.'
      : `Estos ${orders} pedidos quedaron fuera del plan: hoy no salen.`;
  }

  /**
   * Titular del detalle plano. Dice "los mismos" porque LO SON: la lista es el
   * aplanado de `fleetGaps`, así que no puede traer un pedido que el agrupado no
   * tenga.
   *
   * Sin esa palabra, dos bloques consecutivos con el mismo dato se leen como dos
   * cubetas distintas y se busca una diferencia que no existe — ya pasó. El título
   * viejo ("Pedidos sin flota, uno por uno") nombraba la FORMA del listado, que es
   * lo único que no hacía falta explicar.
   */
  withoutRouteHeadlineOf(view: OfficePlanView): string {
    const orders = view.withoutRoutePoints?.length ?? 0;
    return orders === 1
      ? 'El mismo pedido, en detalle.'
      : `Los mismos ${orders} pedidos, uno por uno.`;
  }

  /**
   * Ruta de entrega de un territorio, del maestro ya cargado.
   *
   * Con varias rutas en el territorio se nombran todas: el operador tiene que saber
   * en cuál asignar el vehículo, y elegir una sola escondería la otra.
   */
  private routeNameOfTerritory(territoryId: number | undefined): string | undefined {
    if (territoryId === undefined) return undefined;
    const names: string[] = [];
    for (const subRoute of this.subRoutesById.values()) {
      // Solo las de ENTREGA. Un territorio suele tener varias sub-rutas y casi todas
      // son de PREVENTA: nombrarlas todas entierra la única en la que se puede
      // asignar el vehículo, que es justo lo que el renglón pide hacer.
      if (!isDeliverySubRoute(subRoute as unknown as { function?: { name?: string } })) continue;
      const declares = ((subRoute as unknown as { routes?: Array<{ id?: unknown }> }).routes ?? [])
        .map((r) => territoryIdOf(r?.id))
        .includes(territoryId);
      if (!declares) continue;
      const name = (subRoute as unknown as { name?: unknown }).name;
      const label = name === undefined || name === null ? '' : String(name);
      if (label && !names.includes(label)) names.push(label);
    }
    return names.length ? names.join(' · ') : undefined;
  }

  /**
   * Planifica UN grupo. Su fallo se queda en el grupo: no aborta la corrida ni
   * descarta lo ya resuelto, y queda marcado para reintentarlo solo a él.
   */
  private async runGroupPlan(
    view: OfficePlanView,
    group: GroupPlanView,
    request: PlanRequest,
    orders: OrderAttributes[],
  ): Promise<void> {
    group.status = 'running';
    try {
      // `/route-map` en vez de `/plan`: mismo optimizador y misma latencia
      // (medido 2,72 s vs 2,78 s), pero devuelve además la geometría ya
      // decodificada que el mapa necesita para dibujar el recorrido.
      const mapRes = await firstValueFrom(
        this.routeOptimizationService
          .routeMap({ ...request, colors: this.colorsOfGroup(group, request) })
          .pipe(timeout(GROUP_PLAN_TIMEOUT_MS)),
      );
      const res = mapRes.plan;
      // Las sub-órdenes `#N` que dividió el servicio no existen en el request:
      // sus pesos se derivan acá, ANTES de construir las vistas, para que las
      // paradas y los no-asignados no caigan al total del cliente.
      registerServiceSplitWeights(
        [
          ...(res.routes ?? []).flatMap((r) => (r.stops ?? []).map((s) => s.order_id)),
          ...(res.unassigned ?? []).map((u) => u.order_id),
        ],
        this.weightByOrderId,
      );
      this.geojsonByGroup.set(group.key, mapRes.geojson);
      // La otra mitad del KPI: el MISMO conjunto de paradas, en el orden en que
      // llegó (sin optimizar). No bloquea el pintado del grupo: corre detrás y
      // el KPI aparece cuando responde.
      void this.loadGroupBaseline(group.key, request);
      const pointOf = this.makePointOf(orders, group);
      for (const route of res.routes ?? []) {
        if (!(route.stops?.length ?? 0)) continue;
        // El camión de ESTA ruta, por su `truck_id`: un territorio puede llevar
        // varios y cada tarjeta tiene que nombrar el suyo.
        const vehicle = this.vehicleOfRoute(route.truck_id);
        // Y SU sub-ruta, resuelta por camión + territorio: de ahí sale el nombre de
        // ruta que la tarjeta muestra y la sub-ruta contra la que se confirma.
        const routeSubRouteId = this.subRouteIdOfVehicle(route.truck_id, group.territoryId);
        const routeName =
          (this.subRoutesById.get(routeSubRouteId ?? '') as unknown as { name?: string } | undefined)
            ?.name || group.label;
        view.truckViews = [
          ...view.truckViews,
          buildTruckRouteView(
            route,
            {
              // La llave del grupo, no su etiqueta: es lo que empareja la
              // tarjeta con su tag de entrega (ver `TruckRouteView.groupKey`).
              groupKey: group.key,
              // La RUTA de este camión. El territorio va aparte, no en su lugar.
              label: routeName,
              color: group.color ?? truckColorAt(0),
              // La sub-ruta del camión de ESTA ruta, no la del grupo: con dos
              // camiones en un territorio son sub-rutas distintas.
              subRouteId: routeSubRouteId ?? group.subRouteId,
              subRouteName: routeName,
              territoryName: group.territoryName,
              capacityLb: capacityLbOrDefault(vehicle),
              vehicle,
            },
            pointOf,
          ),
        ];
      }
      view.unassignedGroups = mergeUnassigned(view.unassignedGroups, res.unassigned, pointOf);
      view.dash = recomputeOfficeDash(view);
      group.status = 'done';
      group.error = undefined;
    } catch (err) {
      group.status = 'failed';
      group.error = planErrorMessage(err);
    } finally {
      this.groupsDone++;
      this.publishPartialResult();
    }
  }

  /**
   * Publica lo que hay hasta ahora para pintar sin esperar al resto: el usuario
   * ve rutas a los pocos segundos en vez de un spinner de minutos.
   */
  private publishPartialResult(): void {
    this.dash = aggregateDash(this.officePlans);
    this.officeSummary = buildOfficeSummary(this.officePlans);
    this.rebuildMapOptions();
    this.rebuildAverages();
    // La corrida publica por partes: si hay un filtro activo —texto o entregas—,
    // lo nuevo también tiene que filtrarse.
    if (this.filterActive) this.applyFilters();
    // Reasignación inmutable: dispara la detección de cambios de la lista.
    this.officePlans = [...this.officePlans];
    if (this.state === 'optimizing') this.state = 'result';
  }

  private finalize(views: OfficePlanView[]): void {
    this.officePlans = views;
    this.progressOfficeName = '';
    this.dash = aggregateDash(views);
    this.officeSummary = buildOfficeSummary(views);
    this.rebuildMapOptions();
    this.rebuildAverages();
    // La corrida publica por partes: si hay un filtro activo —texto o entregas—,
    // lo nuevo también tiene que filtrarse.
    if (this.filterActive) this.applyFilters();
    this.state = 'result';
  }

  /* --------------------------- Reintento ----------------------------- */

  /** ¿La corrida sigue resolviendo grupos? */
  get isRunningGroups(): boolean {
    return this.groupsTotal > 0 && this.groupsDone < this.groupsTotal;
  }

  /** Grupos que fallaron y se pueden reintentar sin repetir los resueltos. */
  get failedGroups(): Array<{ view: OfficePlanView; group: GroupPlanView }> {
    return this.officePlans.flatMap((view) =>
      view.groups.filter((g) => g.status === 'failed').map((group) => ({ view, group })),
    );
  }

  get failureReasons(): Array<{ message: string; count: number }> {
    return failureReasonsOf(this.failedGroups.map(({ group }) => group));
  }

  /**
   * Reintenta SOLO los grupos fallidos: re-planificar los resueltos sería tirar
   * minutos de motor por un fallo puntual.
   */
  async retryFailedGroups(): Promise<void> {
    const failed = this.failedGroups;
    if (!failed.length || this.isRunningGroups) return;

    const fallback = this.fleetFallback();
    const weekday = DateTime.fromJSDate(new Date(this.data.loadDate)).weekday;
    const ordersByOffice = new Map<number, OrderAttributes[]>();
    for (const o of this.allOrders) {
      const officeId = (o.salesOffice as { id?: number })?.id;
      if (officeId == null) continue;
      const list = ordersByOffice.get(officeId) ?? [];
      list.push(o);
      ordersByOffice.set(officeId, list);
    }

    const tasks = failed.map(({ view, group }) => () => {
      const orders = ordersByOffice.get(view.officeId) ?? this.allOrders;
      // Se re-deriva el MISMO alcance de la corrida: la flota de la oficina y la
      // partición por territorio. Reintentar un grupo con otro alcance daría un
      // resultado que no encaja con el resto del plan.
      const master = [...this.subRoutesById.values()].filter(
        (sr) => (sr as unknown as { salesOffice?: { id?: number } }).salesOffice?.id === view.officeId,
      );
      const fleet = fleetByTerritory(master, {
        excludedVehicleIds: [...this.excludedVehicleIdSet()],
        territoryIds: this.effectiveTerritoryIds(),
        assignedTerritoryByVehicle: new Map(
          this.duplicateVehicles.map((r) => [r.vehicleId, r.territoryId]),
        ),
      });
      const { groups } = partitionByTerritory(orders, {
        fleetTerritoryIds: fleet.map((f) => f.territoryId),
        masterSubRoutes: master,
      });
      const target = groups.find((g) => g.key === group.key);
      const unit = fleet.find((f) => f.territoryId === target?.territoryId);
      if (!target || !unit) {
        group.error = 'El territorio ya no tiene flota ni pedidos en los datos cargados.';
        this.groupsDone++;
        return Promise.resolve();
      }
      const resolved = resolveTerritoryFleet({
        territoryId: unit.territoryId,
        territoryName: unit.territoryName,
        units: unit.units,
        weekday,
        fallback,
      });
      const [req] = toGroupPlanRequests(
        [
          {
            key: target.key,
            orders: target.orders,
            trucks: resolved.trucks.map((t) => t.truck),
            window: resolved.window,
          },
        ],
        {
          date: this.planDate,
          salesOffice: view.office,
          weightUnit: 'lb',
          groupByCustomer: true,
          // El mismo valor de la corrida original: un recálculo de un grupo no
          // puede quedar con otra atención que el resto del plan.
          serviceMin: this.assumedServiceMin,
        },
      );
      this.indexPlanWeights(req.request);
      return this.runGroupPlan(view, group, req.request, orders);
    });

    this.groupsTotal = this.groupsDone + tasks.length;
    await runWithLimit(tasks, PLAN_CONCURRENCY);
    this.finalize(this.officePlans);
  }

  /* --------------------------- Confirmación --------------------------- */

  /**
   * Rutas confirmables: las que salieron de una sub-ruta real. Los grupos por
   * municipio o residuales no tienen `subRouteId` — `ave-trip` no sabe qué
   * `Trip` crearles, así que quedan fuera de la confirmación.
   */
  get confirmableTruckViews(): TruckRouteView[] {
    return this.officePlans.flatMap((op) => op.truckViews).filter((t) => !!t.subRouteId);
  }

  /**
   * ¿Hay algo para confirmar? Sin esto el botón quedaría habilitado sin efecto.
   *
   * `!isRunningGroups`: mientras la corrida todavía está resolviendo grupos
   * (barra "Generando rutas — X de Y…"), `officePlans`/`confirmableTruckViews`
   * reflejan solo lo que terminó hasta ese momento — confirmar ahí persistiría
   * una propuesta parcial, sin los grupos que faltan.
   */
  get canConfirm(): boolean {
    return !this.confirming && !this.isRunningGroups && this.confirmableTruckViews.length > 0;
  }

  /** Resultado de la última confirmación para esta ruta, si ya se confirmó. */
  resultFor(truck: TruckRouteView): IConfirmOptimizedRouteResult | undefined {
    return this.confirmResults.find((r) => r.subRouteId === truck.subRouteId);
  }

  /* ---------------------- Gestor de confirmación ---------------------- */

  /** `true` = el gestor reemplaza el contenido del resultado. */
  reviewOpen = false;
  /** Pestaña del gestor: ver el orden del motor, o editarlo. */
  reviewTab: 'optimizer' | 'custom' = 'optimizer';
  /** Rutas en revisión. Sobreviven a cerrar el gestor; una corrida nueva las tira. */
  reviewRoutes: ReviewRoute[] = [];

  /**
   * Sub-rutas (mongo `_id`) cuyo viaje del día YA tiene un orden de visita
   * guardado — posiblemente afinado a mano en el editor de trips. Confirmar lo
   * PISA, así que el botón de guardar avisa antes.
   *
   * Mejor esfuerzo: se resuelve de fondo mientras el usuario revisa; si el
   * chequeo falla o no terminó, solo se pierde el aviso — nunca se bloquea la
   * confirmación por él.
   */
  private savedOrderSubRouteIds = new Set<string>();

  /**
   * El orden de visita guardado de cada sub-ruta, como **secuencia de destinos**.
   *
   * Es el mismo dato que el chequeo de pisada ya trae: `loadOverwriteWarnings` hacía el
   * `GET trip-visit-order/:tripId` y medía `visitOrder.length` para saber si existía,
   * descartando el array. Conservarlo **no agrega ninguna llamada**.
   *
   * El `Set` de arriba dice *si existe* y este dice *qué es*: uno no reemplaza al otro,
   * el aviso de pisada sigue usando el primero.
   *
   * Cada destino es `businessPartnerMongoId|businessPartnerAddressMongoId`, que del lado
   * del pedido son `shipTo._id` y `address._id`.
   */
  private savedOrderBySubRoute = new Map<string, string[]>();

  /**
   * Sub-rutas donde el usuario adoptó el orden guardado.
   *
   * Sirve para dejar de advertir una pisada que ya no ocurre: lo que se va a guardar en
   * esas rutas ES el orden guardado más los pedidos que el motor ubicó.
   */
  private appliedSavedOrder = new Set<string>();

  /** Resumen de la última adopción, o `null` si no se accionó. */
  savedOrderApplyResult: {
    /** Rutas cuya SECUENCIA se reordenó. */
    applied: number;
    /** Rutas que ya coincidían **de verdad**: misma secuencia y sin destinos suyos en otra parte. */
    matched: number;
    /** Paradas que volvieron a la ruta que su orden guardado declara. */
    reclaimedStops: number;
    /** Paradas que no volvieron: esa ruta no visita el territorio del cliente. */
    blockedStops: number;
    /** Destinos que dos órdenes guardados reclaman a la vez. */
    conflictedDestinations: number;
    /** Destinos del orden guardado que ya no están en el día. */
    goneDestinations: number;
  } | null = null;

  /**
   * Averigua de fondo qué sub-rutas pisarían un orden guardado. NUNCA lanza.
   *
   * Dos pasos porque el dato vive en dos colecciones: los viajes existentes del
   * día (por sub-ruta), y el orden de visita de cada viaje (`prc-trip-visit-order`,
   * que solo existe si alguien lo guardó).
   */
  private async loadOverwriteWarnings(): Promise<void> {
    this.savedOrderSubRouteIds = new Set();
    this.savedOrderBySubRoute = new Map();
    try {
      // mongo _id (llave del gestor) → id numérico (llave de la consulta de trips)
      const numByMongo = new Map<string, number>();
      for (const r of this.reviewRoutes) {
        const numId = (this.subRoutesById.get(r.subRouteId) as unknown as { id?: number })?.id;
        if (numId != null) numByMongo.set(r.subRouteId, numId);
      }
      if (!numByMongo.size) return;

      const trips = await firstValueFrom(
        this.tripService
          .findTripsForOverwriteCheck([...numByMongo.values()], this.data.dayRange)
          .pipe(timeout(15_000)),
      );
      const rows = (trips?.data ?? []) as unknown as Array<{
        _id?: unknown;
        subRoute?: { id?: number };
      }>;
      if (!rows.length) return;

      const mongoByNum = new Map([...numByMongo].map(([m, n]) => [n, m]));
      const checks = rows
        .filter((t) => t._id != null && t.subRoute?.id != null)
        .map((t) => async () => {
          const order = await firstValueFrom(
            this.tripVisitOrderService.findByTrip(String(t._id), true).pipe(timeout(15_000)),
          );
          const entries = order?.data?.visitOrder ?? [];
          if (entries.length > 0) {
            const mongoId = mongoByNum.get(t.subRoute.id as number);
            if (mongoId) {
              this.savedOrderSubRouteIds.add(mongoId);
              // La secuencia de destinos, ordenada por `position`. Se guarda acá y no
              // se vuelve a pedir: es el mismo array que ya llegó.
              this.savedOrderBySubRoute.set(
                mongoId,
                [...entries]
                  .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                  .map((e) =>
                    destinationKey(e.businessPartnerMongoId, e.businessPartnerAddressMongoId),
                  ),
              );
            }
          }
        });
      await runWithLimit(checks, 4);
    } catch {
      // Sin chequeo no hay aviso ni orden que adoptar; la confirmación sigue
      // disponible igual.
      this.savedOrderSubRouteIds = new Set();
      this.savedOrderBySubRoute = new Map();
    }
  }

  /**
   * Abre el gestor en vez de guardar directo.
   *
   * El gestor ABSORBE al "¿Estás seguro?": revisar el orden ES la confirmación
   * consciente, y un segundo diálogo encima sería ceremonia.
   *
   * Las rutas se construyen solo si no hay una revisión previa, así que lo
   * personalizado sobrevive a cerrar el gestor **y también a cerrar el modal**: la
   * foto de la corrida guarda el delta de lo editado y `restoreReview` lo vuelve a
   * aplicar. Lo tira la corrida nueva, que es cuando el plan de base cambió de
   * verdad, y lo tira una huella distinta, que es cuando cambió el universo.
   *
   * Antes esta promesa valía solo para cerrar el GESTOR: cerrar el modal destruía
   * el componente y el orden editado no viajaba en la foto, así que al reabrir se
   * reconstruía desde el plan y todo volvía sin la marca de `custom` — sin ninguna
   * señal de que se había perdido trabajo.
   */
  confirm(): void {
    if (!this.canConfirm) return;
    if (!this.reviewRoutes.length) {
      this.reviewRoutes = buildReviewRoutes(this.confirmableTruckViews);
      // El panel de detalle del gestor no sobrevive a un gestor reconstruido:
      // apuntaría a una parada de otra corrida.
      this.detailId.delete('review');
      this.detailOrders.delete('review');
    }
    this.prepareReviewContext();
    // Si ya hay edición, se aterriza en Personalizado: reabrir en "Del
    // optimizador" hacía creer que lo editado se había perdido.
    this.reviewTab = customCount(this.reviewRoutes) > 0 ? 'custom' : 'optimizer';
    this.reviewOpen = true;
  }

  /**
   * Lo que el gestor necesita saber de los PEDIDOS para poder mover paradas: los
   * territorios y el cliente de cada uno.
   *
   * Factorizado porque ahora hay DOS entradas al gestor —abrirlo desde el
   * resultado y restaurarlo con el diálogo— y la pregunta es la misma. Sin esto,
   * un gestor restaurado no ofrecería ningún destino y el síntoma sería "no puedo
   * mover nada", **sin ninguna explicación en pantalla**.
   */
  private prepareReviewContext(): void {
    this.reviewOrderTerritories = new Map(
      this.allOrders.map((o) => [
        orderIdOf(o),
        ((o as unknown as { routes?: RouteRef[] }).routes ?? []).filter((r) => !!r),
      ]),
    );
    this.reviewOrderCustomer = new Map(
      this.allOrders
        .map((o): [string, string] => [
          orderIdOf(o),
          String((o.shipTo as unknown as { _id?: unknown } | undefined)?._id ?? ''),
        ])
        .filter(([, customerId]) => !!customerId),
    );
    this.reviewTargetsCache.clear();
    // Los pendientes dependen de los territorios que se acaban de indexar: sin ese
    // orden, el filtro de "tiene territorio" respondería que no a todos.
    this.preparePending();
    // De fondo, sin bloquear la revisión: mientras no llegue, decide la copia
    // del pedido (respaldo); al llegar, los destinos se recalculan.
    void this.loadReviewClientTerritories();
    // De fondo, sin bloquear la revisión: para cuando el usuario llegue al botón
    // de guardar, el aviso de pisada ya suele estar resuelto.
    void this.loadOverwriteWarnings();
  }

  /**
   * Devuelve al gestor lo que el operador había decidido, desde la foto.
   *
   * Las rutas se reconstruyen del PLAN y recién después se les aplica el delta:
   * el plan es la única fuente de las rutas y de sus datos, y la foto solo lleva
   * la decisión. Ver `applyReviewOrderDelta` para qué pasa con un pedido que ya
   * no está en el plan y con una parada que el delta no menciona.
   *
   * Sin sección en la foto no se toca nada: es una foto anterior a que esto se
   * conservara, o una corrida que nunca se revisó.
   */
  private restoreReview(review: ReviewViewState | undefined): void {
    if (!review) return;
    this.reviewRoutes = buildReviewRoutes(this.confirmableTruckViews);
    // El contexto ANTES del delta, no después: restaurar un pedido incorporado a mano
    // necesita el pool —de donde sale su punto— y la regla territorial, y las dos se
    // arman acá. `prepareReviewContext` indexa los territorios y recién entonces llama
    // a `preparePending`, así que adelantar solo el pool no alcanzaba: el filtro de
    // "tiene territorio" habría respondido que no a todos.
    if (this.reviewRoutes.length) this.prepareReviewContext();
    applyReviewOrderDelta(this.reviewRoutes, review.order, this.restoreMissingStop);
    this.appliedSavedOrder = new Set(review.appliedSaved ?? []);
    this.reviewTab = review.tab ?? 'optimizer';
    // El gestor CERRADO no se abre solo: si el usuario cerró el diálogo desde el
    // resultado, ahí es donde estaba mirando.
    this.reviewOpen = !!review.open;
    if (!this.reviewRoutes.length) {
      this.reviewOpen = false;
      return;
    }
  }

  /**
   * La parada de un pedido que el plan no trae, para restaurar lo incorporado a mano.
   *
   * Sale del pool (`pendingPointByOrderId`) y **NO se revalida contra el territorio**,
   * por dos razones que apuntan al mismo lado:
   *
   * 1. Restaurar no es asignar. La asignación ya pasó por la compuerta cuando el
   *    operador la hizo; la foto es el registro de esa decisión, no una nueva.
   * 2. El reclamo de las paradas MOVIDAS entre rutas nunca validó territorio —mirá el
   *    reparto de `applyReviewOrderDelta`—, así que exigirlo solo a las incorporadas
   *    sería una regla más estricta para el caso que menos lo aguanta.
   *
   * Y hay una tercera, decisiva: el maestro de clientes llega **async**
   * (`loadReviewClientTerritories`) y el delta se aplica sincrónico. La comprobación
   * usaría la copia embebida del pedido —que suele traer menos territorios que el
   * maestro— y rechazaría justo los que el operador pudo añadir gracias al maestro.
   *
   * Un maestro que cambió no queda en silencio: la parada muestra su territorio y el
   * recorrido de decisión la marca como disconforme.
   */
  private restoreMissingStop = (orderId: string): ReviewStop | undefined => {
    const point = this.pendingPointByOrderId.get(orderId);
    return point ? toReviewStop(point) : undefined;
  };

  /** Abre o cierra las paradas de una ruta del gestor. */
  toggleReviewRoute(route: ReviewRoute): void {
    route.open = !route.open;
  }

  /** Vuelve al resultado sin confirmar. Conserva lo editado (ver `confirm`). */
  closeReview(): void {
    this.reviewOpen = false;
  }

  /* ------------- Adoptar el orden de visita guardado del día ------------- */

  /**
   * Destino de un pedido: `shipTo._id` (el cliente que recibe) + `address._id` (la
   * dirección de entrega). Es el par con el que la API indexa el orden guardado.
   *
   * Se arma sobre `allOrders`, que el diálogo ya tiene cargado. `customerIdOf` usa solo
   * el cliente porque agrupa visitas —y el optimizador asigna una dirección por
   * cliente— pero para casar contra el guardado hace falta el par.
   */
  private destinationKeyOf(baseOrderId: string): string | undefined {
    const order = this.allOrders.find((o) => orderIdOf(o) === baseOrderId);
    if (!order) return undefined;
    const shipTo = order.shipTo as unknown as { _id?: unknown } | undefined;
    const address = (order as unknown as { address?: { _id?: unknown } }).address;
    return destinationKey(shipTo?._id, address?._id);
  }

  /** ¿Hay algún orden guardado que se pueda adoptar? Si no, la acción no se ofrece. */
  get canApplySavedOrder(): boolean {
    return this.reviewRoutes.some((r) => (this.savedOrderBySubRoute.get(r.subRouteId)?.length ?? 0) > 0);
  }

  /**
   * Adopta el orden guardado, **solo en las rutas cuya secuencia de destinos difiere**.
   *
   * Global pero selectivo: un botón por ruta serían diecisiete clics para lo mismo, y uno
   * global ciego tocaría rutas que ya coincidían y las marcaría como personalizadas sin
   * que nadie las personalizara.
   *
   * Agregar no cuenta como diferencia —ver `savedOrderDiffers`— así que el caso corriente
   * de "se guardó a las 8 y entraron pedidos a las 10" no mueve nada ni enciende ningún
   * indicador.
   */
  applySavedVisitOrder(): void {
    const keyOf = (id: string) => this.destinationKeyOf(id);
    // Se arma ANTES del reclamo: después, los traídos ya no están en el pool.
    const poolByDestination = new Map<string, ReviewStop[]>();
    for (const item of pendingPool(this.pendingAll, this.reviewRoutes)) {
      // SOLO los que nunca entraron. Un `fromPlan` en el pool significa una cosa: el
      // plan lo puso en una ruta y hoy no está en ninguna, o sea que el operador lo
      // RETIRÓ. Traerlo desharía esa decisión en silencio.
      //
      // Adoptar el orden guardado sí deshace los MOVIMIENTOS entre rutas, y está bien
      // porque lo declara de antemano. Un retiro es más fuerte: dice que ese pedido no
      // se entrega hoy, la pantalla lo cuenta y lo pregunta al confirmar. Y el pedido
      // volvería al FINAL de la ruta, en la parte sin orden fijo, donde cuesta notarlo.
      if (item.fromPlan) continue;
      const key = this.destinationKeyOf(basePlanOrderId(item.point.orderId));
      if (!key) continue;
      const list = poolByDestination.get(key) ?? [];
      list.push(toReviewStop(item.point));
      poolByDestination.set(key, list);
    }

    // FASE 1 — la PERTENENCIA, sobre todas las rutas a la vez. Un destino que la
    // ruta A reclama puede estar hoy en B, así que resolver ruta por ruta lo dejaría
    // en las dos o lo perdería. Y tiene que terminar antes de reordenar: reordenar
    // sobre un contenido que todavía va a cambiar deja posiciones calculadas contra
    // otra lista.
    const reclaim = reclaimSavedDestinations(
      this.reviewRoutes,
      this.savedOrderBySubRoute,
      keyOf,
      // La MISMA regla que decide los destinos del arrastre: el gestor no puede
      // producir una asignación que arrastrar habría rechazado.
      (subRouteId, stop) =>
        canRouteReceive(
          subRouteId,
          stop,
          this.territoryRefsOfOrder,
          this.declaredRefsOfSubRoute,
        ),
      // Los NO ASIGNADOS, indexados por destino: es la unidad del orden guardado. Sin
      // esto, un destino que el guardado nombra y que está en el pool se contaba como
      // ausente del día — estando a la vista.
      (destKey) => poolByDestination.get(destKey),
    );
    // El pool es derivado, pero su memo no: traer un destino lo saca de la lista.
    if (reclaim.reclaimedStops) this.pendingCache = null;
    for (const subRouteId of reclaim.reclaimedInto) {
      // Recibió lo que su orden guardado reclamaba: lo que se va a guardar ahí ES ese
      // orden, así que advertir una pisada sería falso.
      this.appliedSavedOrder.add(subRouteId);
    }

    // FASE 2 — el ORDEN, por ruta, como siempre.
    const unresolved = new Set(reclaim.unresolved);
    let applied = 0;
    let matched = 0;
    for (const route of this.reviewRoutes) {
      const saved = this.savedOrderBySubRoute.get(route.subRouteId);
      if (!saved?.length) continue;
      if (!savedOrderDiffers(route, saved, keyOf)) {
        // "Ya coincidía" SOLO si además no le falta ningún destino suyo. Una ruta con
        // un destino bloqueado o en conflicto no coincide, aunque su secuencia
        // restringida a los compartidos sea idéntica.
        if (!unresolved.has(route.subRouteId)) matched++;
        continue;
      }
      applySavedOrder(route, saved, keyOf);
      // Se RECALCULA, no se afirma: un orden guardado puede coincidir con el del
      // motor, y entonces la ruta no tiene orden personalizado. Afirmarlo era además
      // inestable —`applyReviewOrderDelta` sí recalcula al restaurar la foto— así que
      // la insignia desaparecía al cerrar y reabrir el diálogo.
      refreshReviewCustom(route);
      this.appliedSavedOrder.add(route.subRouteId);
      applied++;
    }

    // La pertenencia cambió: los destinos válidos de cada parada ya no son los mismos.
    this.reviewTargetsCache.clear();
    this.savedOrderApplyResult = {
      applied,
      matched,
      reclaimedStops: reclaim.reclaimedStops,
      blockedStops: reclaim.blockedStops,
      conflictedDestinations: reclaim.conflictedDestinations,
      goneDestinations: reclaim.goneDestinations,
    };
  }

  /**
   * ¿Esta ruta sigue pisando un orden afinado a mano?
   *
   * Después de adoptarlo, lo que se va a guardar **es** ese orden más los pedidos que el
   * motor ubicó, así que advertir de una pisada sería falso. Pero si el usuario editó a
   * mano DESPUÉS de adoptar, la advertencia vuelve: ya no es el orden guardado.
   */
  reviewOverwritesSavedOrder(route: ReviewRoute): boolean {
    if (!this.savedOrderSubRouteIds.has(route.subRouteId)) return false;
    if (!this.appliedSavedOrder.has(route.subRouteId)) return true;
    const saved = this.savedOrderBySubRoute.get(route.subRouteId);
    if (!saved?.length) return true;
    // Si volvió a diferir, hubo edición manual encima de lo adoptado.
    return savedOrderDiffers(route, saved, (id) => this.destinationKeyOf(id));
  }

  /** Cuántas rutas llevan orden personalizado, para declararlo en el botón. */
  get reviewCustomCount(): number {
    return customCount(this.reviewRoutes);
  }

  /**
   * Drop de una parada: dentro de la ruta reordena; entre rutas la MUEVE a la
   * posición donde se soltó. Cruzar solo es posible hacia rutas compatibles —
   * el predicado de entrada ya lo bloqueó antes de llegar acá.
   */
  onReviewDrop(route: ReviewRoute, event: CdkDragDrop<ReviewStopLike[]>): void {
    if (event.previousContainer !== event.container) {
      const source = event.previousContainer?.data as unknown;
      // Del pool: no es un movimiento entre rutas sino una INCORPORACIÓN, y no pasa
      // por la regla territorial (ver `pending-orders`).
      if (source === this.PENDING_LIST) {
        const item = event.item.data as PendingOrder | undefined;
        if (item) this.addPendingToRoute(item, route, event.currentIndex);
        return;
      }
      const from = source as ReviewRoute | undefined;
      if (!from) return;
      // La parada se lee ANTES de moverla: `moveStopToRoute` la saca del arreglo y
      // después de eso el índice ya no apunta a ella.
      const stop = from.stops[event.previousIndex];
      moveStopToRoute(from, event.previousIndex, route, event.currentIndex);
      this.noteReviewMove(stop, from, route);
      this.reviewTargetsCache.clear();
      // Las dos rutas cambiaron de paradas, así que el desvío de los pendientes
      // hacia ellas ya no es el que dice el memo.
      this.pendingCache = null;
      return;
    }
    moveReviewStop(route, event.previousIndex, event.currentIndex);
  }

  /**
   * El último movimiento ENTRE rutas, para poder declarar dónde cayó.
   *
   * Existe porque en modo grid el panel muestra las paradas de UNA ruta, así que un
   * pedido que cruza sale de la vista y no aparece en ninguna parte: el operador ve
   * desaparecer una fila y nada más. En modo tarjetas eso lo resuelve
   * `to.open = true` —abre el destino y se ven las dos puntas— pero el panel del
   * mosaico no lee `open`, así que ahí esa línea no hace nada.
   *
   * Persiste hasta que otro movimiento lo reemplace, y no por tiempo. Es la misma
   * decisión que ya tomó esta pantalla para el resumen de adoptar el orden guardado:
   * un mensaje que se va obliga a mirar en el instante exacto, y en ese instante el
   * operador está mirando la fila que desapareció. Sin temporizador no hay nada que
   * limpiar ni nada que volver frágil los tests.
   */
  lastReviewMove: {
    name: string;
    fromSubRouteId: string;
    toSubRouteId: string;
    toLabel: string;
    toColor: string;
  } | null = null;

  /**
   * Registra el movimiento, si de verdad cruzó de ruta.
   *
   * El panel y el tile de una misma ruta son dos `cdkDropList` con el MISMO dato, así
   * que un drop puede cambiar de contenedor sin cambiar de ruta. `moveStopToRoute` ya
   * lo trata como no-op; acá tampoco se declara nada.
   */
  private noteReviewMove(
    stop: ReviewStopLike | undefined,
    from: ReviewRoute,
    to: ReviewRoute,
  ): void {
    if (!stop || from === to) return;
    this.lastReviewMove = {
      name: stop.name,
      fromSubRouteId: from.subRouteId,
      toSubRouteId: to.subRouteId,
      toLabel: to.label,
      toColor: to.color,
    };
  }

  /**
   * Pasa a ver la ruta destino del último movimiento. **Acción del operador.**
   *
   * NO se ejecuta sola. Saltar automáticamente sería el equivalente literal de abrir
   * la tarjeta destino, pero saca al operador de donde estaba: repartiendo cinco
   * paradas de una misma ruta, el panel saltaría después de cada movimiento y habría
   * que volver cinco veces. Y en modo tarjetas `to.open` **no** saca del origen —se
   * siguen viendo los dos— así que lo fiel es declarar y dejar el salto a criterio de
   * quien mueve.
   *
   * Abre además la tarjeta destino: el modo tarjetas no tiene panel, y ahí "ir" es
   * justamente tenerla abierta.
   */
  goToLastReviewMove(): void {
    const move = this.lastReviewMove;
    if (!move) return;
    this.reviewGridRouteId = move.toSubRouteId;
    const target = this.reviewRoutes.find((r) => r.subRouteId === move.toSubRouteId);
    if (target) target.open = true;
  }

  /**
   * Estado del arrastre entre rutas. Se arma en el `pointerdown` del asa —
   * ANTES del umbral de movimiento con el que CDK inicia el arrastre y cachea
   * la geometría— porque es el único momento seguro para reacomodar el layout:
   * ocultar las rutas incompatibles y abrir las compatibles DURANTE el arrastre
   * dejaría los rects cacheados mintiendo y los drops caerían torcidos.
   */
  private reviewDragging: { fromKey: string; targetKeys: Set<string> } | null = null;

  /** Prepara el escenario del arrastre: solo quedan visibles los destinos válidos. */
  beginReviewDrag(route: ReviewRoute, stop: { orderId: string }): void {
    const targets = this.reviewMoveTargets(route, stop);
    this.reviewDragging = {
      fromKey: route.subRouteId,
      targetKeys: new Set(targets.map((t) => t.route.subRouteId)),
    };
    // Un destino cerrado no tiene lista donde soltar: se abre ya, con el layout
    // aún libre de cachés.
    //
    // Solo en modo tarjetas. En modo grid la lista donde soltar es el TILE, que
    // siempre está ahí: abrir los destinos no haría falta, y dejaría un puñado de
    // rutas abiertas al volver a tarjetas sin que el usuario las haya abierto.
    if (this.viewMode === 'list') {
      for (const t of targets) t.route.open = true;
    }
  }

  /**
   * La ruta elegida en el mosaico del gestor, por `subRouteId`.
   *
   * NO es `gridTruckId`: ese guarda un `truckId` de `TruckRouteView` y acá la
   * identidad es el `subRouteId` de `ReviewRoute`. Escribir uno en el otro daría una
   * selección que no resuelve a ninguna ruta, y el mosaico caería a la primera sin
   * que se entienda por qué. El MODO sí se comparte; la selección no puede.
   */
  reviewGridRouteId: string | null = null;

  /**
   * Las rutas que el mosaico del gestor muestra.
   *
   * Durante un arrastre se van las que no pueden recibir el pedido, con el mismo
   * `reviewRouteHidden` que ya usaban las tarjetas: así el mosaico completo pasa a
   * ser la respuesta a "¿dónde puedo soltarlo?" en vez de la confirmación de una
   * adivinanza previa.
   */
  visibleReviewRoutes(): ReviewRoute[] {
    return (this.reviewRoutes ?? []).filter((r) => !this.reviewRouteHidden(r));
  }

  /**
   * La ruta que el panel del gestor muestra. **Deriva**, igual que en el resultado:
   * una elegida que el arrastre escondió dejaría el panel mostrando una ruta que no
   * está en el mosaico.
   */
  selectedReviewRouteOf(): ReviewRoute | undefined {
    const visible = this.visibleReviewRoutes();
    if (!visible.length) return undefined;
    const chosen = visible.find((r) => r.subRouteId === this.reviewGridRouteId);
    return chosen ?? visible[0];
  }

  /** ¿Este tile del gestor es el que se está mostrando? */
  isReviewRouteSelected(route: ReviewRoute): boolean {
    return this.selectedReviewRouteOf()?.subRouteId === route.subRouteId;
  }

  /** Elige una ruta en el mosaico del gestor. */
  selectReviewRoute(route: ReviewRoute): void {
    this.reviewGridRouteId = route.subRouteId;
  }

  /**
   * Ocupación del tile del gestor, contra lo que su camión carga en el día.
   *
   * Se calcula, no se guarda: es la **única** métrica del gestor que se actualiza al
   * mover pedidos —los tiempos pertenecen al orden del optimizador y no hay cómo
   * reevaluar un orden fijo—. Congelarla perdería justo lo que la hace útil: soltás
   * un pedido en un tile y ese tile declara la sobrecarga antes de que confirmes.
   *
   * Se corta en 100 para la barra; la sobrecarga la declara `reviewOverloadLb`.
   */
  reviewLoadPct(route: ReviewRoute): number {
    const capacity = this.reviewDayCapacityLb(route);
    if (capacity <= 0) return 0;
    return Math.min(100, Math.round((this.reviewLoadLb(route) / capacity) * 100));
  }

  /** ¿La tarjeta se oculta mientras se arrastra? Las que no pueden recibir. */
  reviewRouteHidden(route: ReviewRoute): boolean {
    if (!this.reviewDragging) return false;
    return (
      route.subRouteId !== this.reviewDragging.fromKey &&
      !this.reviewDragging.targetKeys.has(route.subRouteId)
    );
  }

  /** ¿La tarjeta es destino válido del arrastre en curso? Para resaltarla. */
  reviewRouteIsTarget(route: ReviewRoute): boolean {
    return !!this.reviewDragging && this.reviewDragging.targetKeys.has(route.subRouteId);
  }

  /**
   * Candado del cruce: CDK consulta antes de dejar ENTRAR la ficha a otra
   * lista. Aunque el ocultado visual fallara, soltar en una incompatible es
   * imposible. Arrow function: CDK la invoca sin `this`.
   */
  reviewEnterPredicate = (_drag: CdkDrag, drop: CdkDropList): boolean => {
    const target = drop.data as ReviewRoute | undefined;
    if (!target || !this.reviewDragging) return false;
    return this.reviewDragging.targetKeys.has(target.subRouteId);
  };

  /**
   * Al soltar (en cualquier parte, incluso sin haber arrastrado) las rutas
   * ocultas reaparecen. `document:` porque el dedo puede levantarse fuera del
   * asa, del diálogo o de la ventana del navegador.
   */
  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  endReviewDrag(): void {
    this.reviewDragging = null;
  }

  /** Sube o baja una parada un lugar — la alternativa accesible al arrastre. */
  moveReviewStopBy(route: ReviewRoute, index: number, delta: number): void {
    moveReviewStop(route, index, index + delta);
  }

  /* ---------- Los pedidos que NO entraron al plan ---------- *
   *
   * El gestor podía reordenar y cruzar de ruta lo que el motor ubicó, pero un pedido
   * de la brecha de flota no tenía ninguna salida: la pantalla decía "asignale un
   * vehículo a la ruta en el maestro" y ahí terminaba. Acá se puede subir a un camión
   * que pasa cerca, que es lo que el operador hace igual fuera del sistema.
   *
   * El territorio ORDENA los destinos en vez de restringirlos, y el porqué está en
   * `pending-orders.ts`: la restricción daría cero destinos siempre, por construcción
   * de la partición y no por los datos del día.
   */

  /** Identidad de la lista del pool como `cdkDropList`. No es una `subRouteId`. */
  readonly PENDING_LIST = 'pending';

  /** ¿El bloque de pendientes está desplegado? Nace cerrado, como el resto. */
  pendingOpen = false;

  /** Candidatos de la corrida: se recalculan al preparar el contexto, no por ciclo. */
  private pendingAll: PendingOrder[] = [];

  /** Coordenadas por `orderId`, para medir el desvío de cada ruta. */
  private reviewCoordByOrderId = new Map<string, [number, number]>();

  /** Los puntos de los candidatos, para que el export no los emita sin coordenadas. */
  private pendingPointByOrderId = new Map<string, DeliveryPoint>();

  /** Origen del recorrido por sub-ruta. Lo piden el recálculo y el export. */
  private reviewWarehouseBySubRouteId = new Map<string, LatLon>();

  /**
   * Memo del pool, invalidado por quien lo cambia.
   *
   * El pool es DERIVADO —candidatos menos lo que ya está en alguna ruta— y eso es lo
   * que hace que "vuelve al pool" no necesite código. Esto es un memo de esa única
   * definición, no una segunda copia: nadie escribe pendientes acá, solo se recalcula.
   * Mismo patrón que `reviewTargetsCache`.
   */
  private pendingCache: PendingGroup[] | null = null;

  /**
   * Destinos VÁLIDOS de cada pendiente, calculados junto con el pool.
   *
   * Eager y no perezoso como el menú de mover: la fila del pool necesita saber
   * **antes de abrir nada** si el pedido tiene a dónde ir, para no ofrecer una ficha
   * que no se puede soltar en ningún lado. Se calcula una vez por cambio del pool
   * —no por ciclo de detección— que es lo que hace que el costo no importe.
   */
  private pendingTargetsByOrderId = new Map<string, RouteDetour[]>();

  /** Lo que el gestor arma una sola vez: candidatos, coordenadas y puntos. */
  private preparePending(): void {
    const sources: PendingSource[] = [];
    this.reviewCoordByOrderId = new Map();
    this.reviewWarehouseBySubRouteId = new Map();
    for (const op of this.officePlans) {
      // El origen es de la OFICINA, y todas sus rutas salen de ahí. Se resuelve en la
      // MISMA pasada que las coordenadas: recorrer `officePlans` otra vez para lo
      // mismo sería una segunda copia de la misma traducción.
      const warehouse = resolveStart(op.office);
      // Sin flota que los lleve y sin asignar por el motor son, para quien mira la
      // pantalla, el mismo problema: pedidos que no salieron. Se ofrecen juntos, con
      // el motivo de cada uno como encabezado de su grupo.
      for (const gap of op.fleetGaps ?? []) {
        sources.push({ reason: gap.reason, points: gap.points ?? [] });
      }
      for (const group of op.unassignedGroups ?? []) {
        sources.push({ reason: group.label, points: group.items ?? [] });
      }
      const planStops: DeliveryPoint[] = [];
      for (const t of op.truckViews ?? []) {
        if (t.subRouteId && warehouse) this.reviewWarehouseBySubRouteId.set(t.subRouteId, warehouse);
        for (const stop of t.stops) {
          planStops.push(stop);
          if (stop.lat !== null && stop.lon !== null) {
            this.reviewCoordByOrderId.set(stop.orderId, [stop.lat, stop.lon]);
          }
        }
      }
      // Las paradas del PLAN también son candidatas, y esto NO cambia nada al abrir:
      // todas están en alguna ruta, así que el pool arranca idéntico. Lo que habilita
      // es que retirar una la deje en el pool en vez de borrarla de la pantalla — el
      // pool es derivado, así que la derivación ya hace el trabajo.
      // Va ÚLTIMA a propósito: `pendingCandidates` deduplica quedándose con la primera
      // fuente que nombró un id, así que un pedido que además esté en la brecha
      // conserva su motivo real.
      sources.push({ reason: RETIRADO_DE_RUTA, points: planStops, fromPlan: true });
    }

    // La MISMA pregunta que decide los destinos del arrastre: dos definiciones de
    // "tiene territorio" divergirían, y el síntoma sería que el pool ofrece lo que el
    // rótulo de fuera-de-territorio no sabe evaluar.
    this.pendingAll = pendingCandidates(
      sources,
      (orderId) => (this.territoryRefsOfOrder(orderId)?.length ?? 0) > 0,
    );

    this.pendingPointByOrderId = new Map(this.pendingAll.map((p) => [p.point.orderId, p.point]));
    for (const p of this.pendingAll) {
      if (p.point.lat !== null && p.point.lon !== null) {
        this.reviewCoordByOrderId.set(p.point.orderId, [p.point.lat, p.point.lon]);
      }
    }
    this.pendingCache = null;
    // El coeficiente de la corrida necesita las coordenadas que se acaban de
    // indexar: sin ese orden, todas las muestras saldrían en cero.
    this.calibrateRunEstimate();
  }

  /** Los pendientes que todavía no están en ninguna ruta, agrupados por motivo. */
  pendingGroupsView(): PendingGroup[] {
    if (!this.pendingCache) {
      const pool = pendingPool(this.pendingAll, this.reviewRoutes);
      this.pendingTargetsByOrderId = new Map(
        pool.map((item) => [item.point.orderId, this.computePendingTargets(item)]),
      );
      this.pendingCache = groupPendingByReason(pool);
    }
    return this.pendingCache;
  }

  /**
   * Los destinos válidos de un pendiente: las rutas que visitan algún territorio de
   * su cliente, ordenadas por desvío.
   *
   * La puerta es `canRouteReceive` —la MISMA que decide el arrastre entre rutas— y
   * no una regla propia: incorporar no puede permitir lo que mover prohíbe. El
   * desvío decide entre las que pasan, no si pasan.
   */
  private computePendingTargets(item: PendingOrder): RouteDetour[] {
    return rankRoutesByDetour(
      this.reviewRoutes,
      item.point,
      this.reviewStopCoord,
      (route) =>
        canRouteReceive(
          route.subRouteId,
          toReviewStop(item.point),
          this.territoryRefsOfOrder,
          this.declaredRefsOfSubRoute,
        ),
    );
  }

  /**
   * Pedidos que estaban en una ruta y el operador retiró.
   *
   * Se cuentan aparte de los del pool: los que nunca entraron no tenían camión y no
   * son novedad; estos SÍ lo tenían y salieron del día por una decisión. Es el número
   * del que depende el aviso de que una entrega no sale.
   */
  get removedFromPlanCount(): number {
    return this.pendingGroupsView().reduce(
      (n, g) => n + g.items.filter((i) => i.fromPlan).length,
      0,
    );
  }

  /** Los nombres de lo retirado, para poder preguntarlo al confirmar. */
  private removedFromPlanNames(): string[] {
    return this.pendingGroupsView()
      .flatMap((g) => g.items)
      .filter((i) => i.fromPlan)
      .map((i) => i.point.name);
  }

  /** Cuántos pedidos quedan por incorporar. */
  get pendingCount(): number {
    return this.pendingGroupsView().reduce((n, g) => n + g.items.length, 0);
  }

  /** Cuántos se incorporaron a mano. Se declara antes de confirmar. */
  get reviewInjectedCount(): number {
    return injectedCount(this.reviewRoutes);
  }

  /** Coordenada de una parada del gestor, para medir contra ella. */
  private reviewStopCoord = (orderId: string): [number, number] | undefined =>
    this.reviewCoordByOrderId.get(orderId);

  /**
   * Las rutas que pueden recibir este pendiente, ordenadas por desvío.
   *
   * Lee del memo que `pendingGroupsView` llenó: la plantilla lo consulta por fila y
   * por ciclo de detección, y recalcular el cruce territorial ahí sería el costo que
   * esta pantalla ya evitó una vez con el menú de mover.
   */
  pendingTargets(item: PendingOrder): RouteDetour[] {
    return this.pendingTargetsByOrderId.get(item.point.orderId) ?? [];
  }

  /** Incorpora un pendiente a una ruta, en la posición dada o al final. */
  addPendingToRoute(item: PendingOrder, route: ReviewRoute, at?: number): void {
    if (!item || !route) return;
    addStopToRoute(route, toReviewStop(item.point), at);
    // En el mosaico el panel muestra UNA ruta: sin esto, añadir desde el menú
    // cambiaba una ruta que no estaba a la vista y el operador no veía nada. Es el
    // equivalente del `to.open = true` que `addStopToRoute` hace para las tarjetas.
    this.reviewGridRouteId = route.subRouteId;
    this.pendingCache = null;
    this.reviewTargetsCache.clear();
  }

  /**
   * ¿Esta parada incorporada va en una ruta que NO visita el territorio del cliente?
   *
   * Con la puerta territorial puesta esto ya **no debería pasar**, y por eso se
   * conserva: es la señal de la única grieta que queda. Los territorios del cliente
   * llegan de fondo (`loadReviewClientTerritories`) y mientras no llegan decide la
   * copia del pedido; si el maestro contradice a la copia, una parada incorporada
   * antes puede quedar en una ruta que el maestro no avala. Declararlo es la misma
   * decisión que ya tomó `territoryMismatch` para las paradas del plan.
   *
   * Solo aplica a las incorporadas: una parada del plan está en su ruta por
   * construcción.
   */
  reviewStopOutOfTerritory(route: ReviewRoute, stop: ReviewStopLike): boolean {
    if (!stop?.injected) return false;
    return !canRouteReceive(
      route.subRouteId,
      stop,
      this.territoryRefsOfOrder,
      this.declaredRefsOfSubRoute,
    );
  }

  /** Retira una parada incorporada. Vuelve al pool sola: el pool es derivado. */
  removePendingStop(route: ReviewRoute, index: number): void {
    removeReviewStop(route, index);
    this.pendingCache = null;
    this.reviewTargetsCache.clear();
  }

  /**
   * Prepara el arrastre desde el pool: solo quedan visibles las rutas que **pueden
   * recibirlo**, igual que al mover una parada entre rutas.
   *
   * Los destinos salen del MISMO memo que alimenta el menú, así que arrastrar y
   * elegir del menú no pueden ofrecer cosas distintas.
   *
   * A diferencia de `beginReviewDrag`, acá no se abren las rutas destino en modo
   * tarjetas: el camino de ese modo es el menú, y abrir tarjetas al levantar la ficha
   * dejaría rutas abiertas que nadie abrió.
   */
  beginPendingDrag(item: PendingOrder): void {
    this.reviewDragging = {
      fromKey: this.PENDING_LIST,
      targetKeys: new Set(this.pendingTargets(item).map((t) => t.route.subRouteId)),
    };
  }

  /**
   * Candado del pool: solo acepta lo que salió de él.
   *
   * Sin esto, soltar ahí una parada del plan la sacaría de su ruta sin que nada lo
   * pida. Retirar una incorporada tiene su propio botón, que además es el único gesto
   * que existe en el modo sin arrastre.
   */
  pendingEnterPredicate = (): boolean => this.reviewDragging?.fromKey === this.PENDING_LIST;

  /* ---------- El recorrido REAL de una ruta editada ---------- *
   *
   * El gestor declaraba en su encabezado que esto era imposible —"no existe endpoint
   * para reevaluar un orden fijo"— y era falso: `POST /sort` está en el cliente HTTP
   * desde antes que el gestor. Lo que sí es cierto es que **reordena**: recalcular es
   * devolverle la ruta al motor con el contenido que ahora tiene, y el control lo
   * dice antes de accionarse.
   *
   * La obsolescencia se DERIVA (`recalcIsStale`), así que ninguna de las siete
   * operaciones que cambian una ruta tiene que acordarse de apagar nada.
   */

  /** El resultado vigente o viejo de cada ruta. Vive en memoria del diálogo. */
  private recalcBySubRouteId = new Map<string, RouteRecalc>();

  /** Rutas con una petición en vuelo. */
  private recalcingSubRouteIds = new Set<string>();

  /** Por qué falló el recálculo de una ruta. Se declara en ella, no en un snackbar. */
  private recalcErrorBySubRouteId = new Map<string, string>();

  /** Los supuestos con los que se recalcula, o `null` si no se puede. */
  private recalcAssumptionsOf(route: ReviewRoute): RecalcAssumptions | null {
    const warehouse = this.reviewWarehouseBySubRouteId.get(route.subRouteId);
    if (!warehouse) return null;
    return {
      warehouse,
      // Los supuestos CONGELADOS de la corrida, no los del selector: si el operador
      // movió el control después de correr, recalcular con eso mezclaría dos mundos.
      shiftStart: this.assumedShift.start,
      shiftEnd: this.assumedShift.end,
      serviceMinPerStop: this.assumedServiceMin,
    };
  }

  /** ¿Se le puede pedir el recorrido al motor? */
  canRecalcReviewRoute(route: ReviewRoute): boolean {
    return (
      !!route?.custom &&
      !!this.recalcAssumptionsOf(route) &&
      canRecalcRoute(route, this.reviewStopCoord)
    );
  }

  /**
   * El resultado de una ruta **solo si sigue describiéndola**.
   *
   * Única puerta de lectura: todo lo que muestra tiempos pasa por acá, así que un
   * resultado obsoleto no puede colarse en ninguna vista.
   */
  recalcOf(route: ReviewRoute): RouteRecalc | undefined {
    const recalc = this.recalcBySubRouteId.get(route?.subRouteId);
    if (!recalc || recalcIsStale(route, recalc)) return undefined;
    return recalc;
  }

  /** Estado del recálculo de una ruta, para rotularlo sin adivinar. */
  recalcStateOf(route: ReviewRoute): 'none' | 'running' | 'fresh' | 'stale' | 'error' {
    if (this.recalcingSubRouteIds.has(route.subRouteId)) return 'running';
    if (this.recalcErrorBySubRouteId.has(route.subRouteId)) return 'error';
    const stored = this.recalcBySubRouteId.get(route.subRouteId);
    if (!stored) return 'none';
    return recalcIsStale(route, stored) ? 'stale' : 'fresh';
  }

  /** El mensaje del último fallo de esta ruta. */
  recalcErrorOf(route: ReviewRoute): string | undefined {
    return this.recalcErrorBySubRouteId.get(route.subRouteId);
  }

  /** Los tiempos de una parada, o `undefined` si su ruta no está al día. */
  recalcStopTimes(route: ReviewRoute, stop: ReviewStopLike): RecalcStopTimes | undefined {
    return this.recalcOf(route)?.stops.get(stop.orderId);
  }

  /** Minutos por encima del fin del turno. Se declara, no bloquea. */
  recalcExceedsMin(route: ReviewRoute): number {
    return recalcExceedsShiftMin(this.recalcOf(route), this.assumedShift.end);
  }

  /** Rutas editadas cuyos tiempos NO están al día: las que el botón alcanza. */
  get reviewRoutesToRecalc(): ReviewRoute[] {
    return this.reviewRoutes.filter((r) => r.custom && !this.recalcOf(r));
  }

  /** `true` mientras haya alguna petición de recálculo en vuelo. */
  get isRecalculating(): boolean {
    return this.recalcingSubRouteIds.size > 0;
  }

  /**
   * Le pide al motor el recorrido de UNA ruta. **Nunca lanza**: un fallo se declara
   * en esa ruta y no toca a las demás, igual que la confirmación por sub-ruta.
   */
  async recalcReviewRoute(route: ReviewRoute): Promise<void> {
    const assumptions = this.recalcAssumptionsOf(route);
    if (!assumptions || !canRecalcRoute(route, this.reviewStopCoord)) return;
    if (this.recalcingSubRouteIds.has(route.subRouteId)) return;

    this.recalcingSubRouteIds.add(route.subRouteId);
    this.recalcErrorBySubRouteId.delete(route.subRouteId);
    try {
      const res = await firstValueFrom(
        this.routeOptimizationService
          .sort(toSortRequestForRoute(route, this.reviewStopCoord, assumptions))
          .pipe(timeout(30_000)),
      );
      this.recalcBySubRouteId.set(route.subRouteId, applySortResult(route, res, assumptions));
      // El motor reordenó: los destinos memorizados y el desvío de los pendientes
      // hacia esta ruta ya no son los mismos.
      this.reviewTargetsCache.clear();
      this.pendingCache = null;
    } catch (err: unknown) {
      // El motivo va a la consola y un texto corto a la ruta: un catch mudo ya nos
      // costó una tarde de "los logs no muestran nada".
      console.error(`[sort] recálculo de ${route.label} falló:`, err);
      this.recalcErrorBySubRouteId.set(
        route.subRouteId,
        'No se pudo recalcular esta ruta. Probá de nuevo.',
      );
    } finally {
      this.recalcingSubRouteIds.delete(route.subRouteId);
    }
  }

  /**
   * La OTRA pregunta: *"¿qué cabe en este camión, en este turno?"*.
   *
   * `POST /route-map` con **un solo camión** y las paradas que la ruta tiene: su
   * contrato ya dice que re-planifica, así que resolver una ruta sola no necesita
   * nada nuevo del motor. A cambio de poder devolver pedidos, trae las horas
   * DIRECTAS, modela las recargas y entrega el trayecto ya decodificado.
   *
   * **Nunca lanza**, igual que reordenar.
   */
  async replanReviewRoute(route: ReviewRoute): Promise<void> {
    const assumptions = this.recalcAssumptionsOf(route);
    const truckId = this.truckIdOfReviewRoute(route);
    if (!assumptions || !truckId || !canRecalcRoute(route, this.reviewStopCoord)) return;
    if (this.recalcingSubRouteIds.has(route.subRouteId)) return;

    this.recalcingSubRouteIds.add(route.subRouteId);
    this.recalcErrorBySubRouteId.delete(route.subRouteId);
    try {
      const res = await firstValueFrom(
        this.routeOptimizationService
          .routeMap(
            toPlanRequestForRoute(
              route,
              this.reviewStopCoord,
              assumptions,
              {
                id: truckId,
                capacityLb: route.capacityLb,
                trips: route.trips,
                reloadMin: this.reloadMin,
              },
              this.planDate,
            ),
          )
          .pipe(timeout(60_000)),
      );
      // El trayecto sale del `geojson` con el MISMO lector que el mapa ya usa: acá
      // no se decodifica nada, el servidor lo entrega en claro.
      const path = routeMapView(res.geojson, truckId).polylines[0]?.points ?? [];
      this.recalcBySubRouteId.set(
        route.subRouteId,
        applyPlanResult(route, res.plan?.routes?.[0], res.plan?.unassigned, path, assumptions),
      );
      this.reviewTargetsCache.clear();
      this.pendingCache = null;
    } catch (err: unknown) {
      console.error(`[route-map] replan de ${route.label} falló:`, err);
      this.recalcErrorBySubRouteId.set(
        route.subRouteId,
        'No se pudo replanificar esta ruta. Probá de nuevo.',
      );
    } finally {
      this.recalcingSubRouteIds.delete(route.subRouteId);
    }
  }

  /** Las paradas que el último replan no pudo ubicar, por `orderId`. */
  unplacedReasonOf(route: ReviewRoute, stop: ReviewStopLike): string | undefined {
    const recalc = this.recalcOf(route);
    if (!recalc?.unplaced.length) return undefined;
    const hit = recalc.unplaced.find((u) => u.orderId === stop.orderId);
    // Cadena vacía = quedó fuera y el motor no dijo por qué. Se distingue de
    // `undefined` (sí entró) con el `!!` del template.
    return hit ? hit.reason || 'El optimizador no la pudo ubicar en este camión' : undefined;
  }

  /** Cuántas paradas dejó sin ubicar el replan vigente de esta ruta. */
  unplacedCountOf(route: ReviewRoute): number {
    return this.recalcOf(route)?.unplaced.length ?? 0;
  }

  /**
   * Recalcula todas las rutas editadas que no estén al día.
   *
   * Con el mismo techo de concurrencia que el resto de las colas del diálogo: son
   * peticiones al mismo motor que acaba de resolver la corrida.
   */
  async recalcEditedRoutes(): Promise<void> {
    const pending = this.reviewRoutesToRecalc.filter((r) => this.canRecalcReviewRoute(r));
    if (!pending.length) return;
    await runWithLimit(
      pending.map((r) => () => this.recalcReviewRoute(r)),
      4,
    );
  }

  /** Restaura el orden del optimizador en UNA ruta (deshace también sus movimientos). */
  resetReview(route: ReviewRoute): void {
    resetReviewRoute(route, this.reviewRoutes);
    this.reviewTargetsCache.clear();
    // Las incorporadas de esta ruta se descartaron: el pool las vuelve a ofrecer.
    this.pendingCache = null;
    // Restaurar CUALQUIERA de las dos puntas puede deshacer el movimiento
    // declarado, y una declaracion que describe algo que ya no paso es peor que
    // ninguna. Una ruta ajena no lo contradice, asi que ahi se conserva.
    const move = this.lastReviewMove;
    if (
      move &&
      (move.fromSubRouteId === route.subRouteId || move.toSubRouteId === route.subRouteId)
    ) {
      this.lastReviewMove = null;
    }
  }

  /**
   * Territorios de la COPIA del pedido, por id BASE de la orden. Es el
   * RESPALDO: la relación Cliente↔Territorio vive en el maestro del cliente
   * (`reviewClientTerritories`), y la copia embebida puede traer un
   * subconjunto estampado al importar — decidir con ella dejaba rutas válidas
   * afuera ("mi cliente tiene 5 rutas y solo me deja la suya").
   */
  private reviewOrderTerritories = new Map<string, RouteRef[]>();
  /** `shipTo._id` por id BASE de la orden, para resolver el cliente del pedido. */
  private reviewOrderCustomer = new Map<string, string>();
  /** Territorios del CLIENTE (maestro de businessPartner), por `shipTo._id`. */
  private reviewClientTerritories = new Map<string, RouteRef[]>();
  /**
   * Destinos calculados por (ruta, parada). El template los consulta en cada
   * ciclo de detección de cambios; el cruce territorial no es caro, pero
   * repetirlo rutas × paradas × ciclo sí. Se limpia con cada movimiento.
   */
  private reviewTargetsCache = new Map<string, MoveTarget[]>();

  /**
   * Rutas a las que ESTA parada puede moverse: solo las que visitan algún
   * territorio del cliente. Regla de negocio: cliente y ruta deben compartir
   * territorio — una ruta no entrega donde no visita.
   */
  reviewMoveTargets(route: ReviewRoute, stop: { orderId: string }): MoveTarget[] {
    // Una parada INCORPORADA no cruza de ruta: se quita y se vuelve a incorporar.
    // Ofrecerle destinos exigiría una segunda regla de "a dónde puede ir esto" dentro
    // de la función que define la primera, y el gesto de arrastrar significaría dos
    // cosas distintas según el origen de la ficha, sin nada que lo distinga.
    if ((stop as ReviewStopLike).injected) return [];
    const key = `${route.subRouteId}|${stop.orderId}`;
    const cached = this.reviewTargetsCache.get(key);
    if (cached) return cached;
    const targets = moveTargetsFor(
      this.reviewRoutes,
      route,
      stop as ReviewRoute['stops'][number],
      this.territoryRefsOfOrder,
      this.declaredRefsOfSubRoute,
    );
    this.reviewTargetsCache.set(key, targets);
    return targets;
  }

  /**
   * Cuántas rutas pueden recibir esta parada. `0` = no se puede mover a ninguna.
   *
   * Sobre el MISMO cálculo memoizado que alimenta el menú, así que el número y la
   * lista no pueden discrepar: son el mismo dato leído de dos formas.
   *
   * Existe porque el gestor ya sabía la respuesta y solo se veía al ABRIR el menú de
   * cada fila: el disparador se veía idéntico con cinco destinos posibles y con
   * ninguno, así que decidir qué reacomodar empezaba por catorce clics de
   * reconocimiento sobre información ya calculada.
   *
   * Un CONTEO y no un booleano: sale gratis del mismo cálculo y dice más —`1` es un
   * movimiento forzado, `5` es margen real— y no depende de qué caso sea el raro en los
   * datos. Si casi todas las paradas se pudieran mover, una marca de sí/no sería ruido
   * en casi todas las filas mientras el número sigue distinguiéndolas.
   */
  reviewMoveTargetCount(route: ReviewRoute, stop: { orderId: string }): number {
    return this.reviewMoveTargets(route, stop).length;
  }

  /**
   * Trae del maestro de clientes los territorios de TODOS los clientes del
   * gestor, en una sola consulta (`_id in`). Su fallo no bloquea nada: queda
   * el respaldo del pedido, que era el comportamiento anterior.
   */
  async loadReviewClientTerritories(): Promise<void> {
    const ids = [...new Set(this.reviewOrderCustomer.values())];
    if (!ids.length) return;
    const res = await this.safe(
      this.businessPartnerService.findTerritoriesForPlanning(ids),
      null,
      20_000,
    );
    if (!res) return;
    for (const bp of res.data ?? []) {
      const key = String((bp as unknown as { _id?: unknown })._id ?? '');
      if (!key) continue;
      this.reviewClientTerritories.set(
        key,
        ((bp as unknown as { routes?: RouteRef[] }).routes ?? []).filter((r) => !!r),
      );
    }
    this.reviewTargetsCache.clear();
    // Los destinos de los pendientes se decidieron con la copia del pedido mientras
    // esto no llegaba. Ahora manda el maestro, y el maestro suele traer MÁS
    // territorios: sin invalidar, un pedido con destino real seguiría diciendo que
    // ninguna ruta lo visita.
    this.pendingCache = null;
  }

  /**
   * Territorios del CLIENTE del pedido. El maestro del cliente manda; la copia que
   * trae el pedido es el respaldo, para cuando esa consulta no llegó.
   *
   * Extraído porque ahora lo usan DOS cosas —los destinos del arrastre y la
   * restauración de la pertenencia desde el orden guardado— y dos copias del cruce
   * darían dos definiciones de "compatible" que divergen. El síntoma sería que
   * arrastrar permite lo que adoptar el orden guardado prohíbe.
   */
  private territoryRefsOfOrder = (baseOrderId: string): readonly RouteRef[] | undefined => {
    const customerId = this.reviewOrderCustomer.get(baseOrderId);
    const fromClient = customerId ? this.reviewClientTerritories.get(customerId) : undefined;
    return fromClient?.length ? fromClient : this.reviewOrderTerritories.get(baseOrderId);
  };

  /** Territorios que el maestro declara para una sub-ruta. */
  private declaredRefsOfSubRoute = (subRouteId: string): readonly RouteRef[] | undefined =>
    (this.subRoutesById.get(subRouteId) as unknown as { routes?: RouteRef[] } | undefined)
      ?.routes;

  /** Ejecuta el movimiento a una ruta compatible (la validación es del menú). */
  moveReviewStopTo(from: ReviewRoute, index: number, target: ReviewRoute): void {
    // Antes de mover: despues el indice ya no apunta a la parada.
    const stop = from.stops[index];
    moveStopToRoute(from, index, target);
    // Los dos caminos —arrastrar y el menu— declaran por la MISMA funcion: dos
    // copias divergirian y el sintoma seria que uno avisa y el otro no.
    this.noteReviewMove(stop, from, target);
    this.reviewTargetsCache.clear();
    // Igual que en el drop: cambió el contenido de las dos rutas y con él su desvío.
    this.pendingCache = null;
  }

  /* Carga vs camión, EN VIVO: delegan en el módulo puro (ver confirm-review). */
  reviewLoadLb(route: ReviewRoute): number {
    return reviewLoadLb(route);
  }
  reviewDayCapacityLb(route: ReviewRoute): number {
    return reviewDayCapacityLb(route);
  }
  reviewOverloadLb(route: ReviewRoute): number {
    return reviewOverloadLb(route);
  }

  /**
   * Confirma con el orden VIGENTE del gestor (editado o del motor).
   *
   * Si alguna sub-ruta ya tiene un orden de visita guardado, se AVISA antes de
   * pisarlo — este diálogo no es ceremonia: aparece solo cuando hay algo real
   * que perder, con la cuenta exacta de rutas afectadas.
   */
  /**
   * Arma el archivo exportable de las rutas en revisión.
   *
   * Público y separado de la descarga para que se pueda fijar por test qué sale, sin
   * tocar el DOM ni el sistema de archivos.
   *
   * Las coordenadas, los tiempos y los totales salen de las paradas y las rutas ya
   * resueltas del PLAN, indexadas acá. Es el mismo índice que `buildMapView` arma
   * para los pines: resolverlos otra vez desde las órdenes sería una segunda copia de
   * la misma traducción.
   */
  buildReviewExport(): RouteExport {
    const stopByOrderId = new Map<string, StopRow>();
    const engineBySubRouteId = new Map<string, RouteEngineTotals>();
    const warehouseBySubRouteId = new Map<string, LatLon>();
    // Solo los recorridos que SIGUEN describiendo su ruta. Se filtran acá, con la
    // misma puerta que usa la pantalla (`recalcOf`): que el archivo y lo que se ve
    // puedan discrepar sobre qué está al día es exactamente lo que no puede pasar.
    const recalcBySubRouteId = new Map<string, RouteRecalc>();
    for (const route of this.reviewRoutes) {
      const recalc = this.recalcOf(route);
      if (recalc) recalcBySubRouteId.set(route.subRouteId, recalc);
    }

    for (const op of this.officePlans) {
      // El origen es de la OFICINA, y todas las rutas de esa oficina salen de ahí.
      // Si no se resuelve no se registra: el exportador lo emite como nulo, y eso es
      // deliberado —ver `review-export`—.
      const warehouse = resolveStart(op.office);
      for (const t of op.truckViews) {
        for (const stop of t.stops) stopByOrderId.set(stop.orderId, stop);
        if (!t.subRouteId) continue;
        engineBySubRouteId.set(t.subRouteId, {
          distanceKm: t.distanceKm,
          durationMin: t.durationMin,
        });
        if (warehouse) warehouseBySubRouteId.set(t.subRouteId, warehouse);
      }
    }

    return buildRouteExport(this.reviewRoutes, {
      loadDate: this.data.loadDate ? new Date(this.data.loadDate).toISOString().slice(0, 10) : '',
      // Los supuestos CONGELADOS de la corrida, no los del selector: si el operador
      // movió el control después de correr, el archivo tiene que declarar con qué se
      // calculó lo que exporta.
      serviceMinPerStop: this.assumedServiceMin,
      shift: { ...this.assumedShift },
      stopByOrderId,
      // Las paradas incorporadas a mano no están en el plan, así que sus coordenadas
      // no salen de `truckViews`. Sin esta fuente el archivo las emitiría en null y
      // las contaría como "no se pueden rutear" — lo contrario de la condición que
      // las dejó entrar.
      pendingByOrderId: this.pendingPointByOrderId,
      warehouseBySubRouteId,
      engineBySubRouteId,
      recalcBySubRouteId,
    });
  }

  /** Descarga el archivo de rutas del gestor. */
  downloadReviewExport(): void {
    if (!this.reviewRoutes.length) return;
    const data = this.buildReviewExport();
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    saveAs(
      new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' }),
      `rutas-gestor_${data.loadDate || 'sin-fecha'}_${stamp}.json`,
    );
    const sinGeo = data.stopsWithoutCoordinates;
    this.handlerMessage.success({
      message: sinGeo
        ? `Rutas descargadas. ${sinGeo} ${sinGeo === 1 ? 'parada' : 'paradas'} sin coordenadas: ` +
          `salen en null y no se pueden rutear.`
        : 'Rutas descargadas.',
    });
  }

  confirmFromReview(): void {
    if (this.confirming) return;
    const payload = toConfirmRoutes(this.reviewRoutes);

    // `reviewOverwritesSavedOrder` y no el `Set` pelado: en las rutas donde se adoptó el
    // orden guardado no hay pisada que advertir —lo que se guarda ES ese orden— y dejar
    // la advertencia enseñaría a ignorarla, justo la que protege el caso contrario.
    const overwriting = this.reviewRoutes.filter((r) =>
      this.reviewOverwritesSavedOrder(r),
    );
    // Las dos consecuencias se ACUMULAN en la misma pregunta: son distintas —una pisa
    // un orden guardado, la otra deja pedidos sin entregar— y mostrar solo una
    // escondería la que el operador no eligió ver.
    const retirados = this.removedFromPlanNames();
    const avisos: string[] = [];
    if (overwriting.length) {
      const names = overwriting.map((r) => r.label).join(', ');
      avisos.push(
        `${overwriting.length === 1 ? 'Esta ruta ya tiene' : `Estas ${overwriting.length} rutas ya tienen`} ` +
          `un orden de visita guardado (${names}), posiblemente ajustado a mano. ` +
          'Confirmar lo reemplaza por el orden de esta propuesta.',
      );
    }
    if (retirados.length) {
      avisos.push(
        `${retirados.length === 1 ? 'Sacaste 1 pedido' : `Sacaste ${retirados.length} pedidos`} ` +
          `de su ruta y ${retirados.length === 1 ? 'quedó' : 'quedaron'} sin asignar ` +
          `(${retirados.join(', ')}). ${retirados.length === 1 ? 'No se entrega' : 'No se entregan'} hoy.`,
      );
    }
    if (!avisos.length) {
      void this.confirmSave(payload);
      return;
    }

    this.confirmationService
      .confirm({
        title: retirados.length
          ? 'Hay pedidos que no se van a entregar'
          : 'Se reemplazará un orden guardado',
        description: avisos.join(' '),
        type: ConfirmationModalType.WARNING,
        confirmButtonText: 'Sí, confirmar',
        cancelButtonText: 'Cancelar',
      })
      .subscribe((confirmed) => {
        if (confirmed) void this.confirmSave(payload);
      });
  }

  /**
   * Confirma de verdad todas las rutas confirmables del alcance actual contra
   * `POST trip/confirm-optimized-routes` (ave-trip, fs-98-trip-visit-order-crud).
   * Mejor esfuerzo del lado del backend: una subruta que falle no bloquea a las
   * demás, por eso el resultado se guarda por subruta en vez de todo-o-nada.
   */
  private async confirmSave(routes: ConfirmRoutePayload[]): Promise<void> {
    // El payload llega del gestor con el orden VIGENTE: `sequence` renumerada
    // por posición actual e ids limpios de sufijos de división (`#N`/`__sN`),
    // sin fusionar ni descartar — cada parte de un pedido dividido sigue siendo
    // una visita real (ver `toConfirmRoutes`).
    if (!routes.length) return;

    this.confirming = true;
    try {
      const res = await firstValueFrom(
        this.tripService.confirmOptimizedRoutes({ date: this.planDate, routes }),
      );
      this.confirmResults = res.data.results;
      const failed = this.confirmResults.filter((r) => r.status === 'error');
      if (failed.length) {
        this.handlerMessage.warning({
          message: `${failed.length} de ${this.confirmResults.length} subrutas no se pudieron confirmar — revisa el detalle en cada tarjeta.`,
        });
      } else {
        this.handlerMessage.success({ message: 'Rutas confirmadas correctamente.' });
      }
      // Con resultados en mano —bien o mal— el gestor cumplió: la vista de
      // resultado es la que muestra el estado por ruta.
      this.reviewOpen = false;
    } catch (err) {
      // Mensaje genérico a propósito: el detalle crudo del backend (ej. errores
      // de validación por datos mal formados) no es útil para el usuario final
      // acá — se loguea para diagnóstico, pero no se muestra en el snackbar.
      console.error('[confirm-optimized-routes] error:', err);
      this.handlerMessage.error({
        message: 'Ocurrió un error al confirmar la propuesta.',
      });
    } finally {
      this.confirming = false;
    }
  }

  /* ---------------------------- Helpers ------------------------------ */

  /**
   * Sub-ruta que declara el camión de una ruta del resultado, **en su territorio**.
   *
   * La confirmación crea el viaje contra una sub-ruta, así que con dos camiones en un
   * territorio cada ruta tiene que apuntar a la SUYA: usar la del grupo mandaría los
   * dos viajes a la misma sub-ruta.
   *
   * El territorio es parte del criterio, no un extra. Un camión puede estar declarado
   * por sub-rutas de territorios distintos, y el maestro llega `orderBy id ASC`: tomar
   * la primera que lo declare devuelve la sub-ruta del territorio de menor id, que
   * puede no ser el que esta tarjeta está sirviendo. El síntoma visible es un nombre
   * de ruta equivocado; el invisible es el viaje creado contra otra ruta.
   */
  private subRouteIdOfVehicle(truckId: string, territoryId?: number): string | undefined {
    if (!truckId) return undefined;
    let fallback: string | undefined;
    for (const [subRouteId, subRoute] of this.subRoutesById) {
      const vehicle = (subRoute as unknown as { vehicle?: VehicleAttributes } | undefined)?.vehicle;
      if (vehicle?._id == null || String(vehicle._id) !== truckId) continue;
      if (territoryId === undefined) return subRouteId;
      const declares = ((subRoute as unknown as { routes?: Array<{ id?: unknown }> }).routes ?? [])
        .map((r) => territoryIdOf(r?.id))
        .includes(territoryId);
      if (declares) return subRouteId;
      // Declara el camión pero NO este territorio: solo sirve si no aparece otra.
      fallback = fallback ?? subRouteId;
    }
    return fallback;
  }

  private vehicleOfRoute(truckId: string): VehicleAttributes | undefined {
    if (!truckId) return undefined;
    // `resolveTerritoryFleet` usa el `_id` del vehículo como `truck.id`, así que
    // la respuesta identifica al camión REAL. Es el único cruce correcto ahora
    // que un territorio puede llevar varios: derivarlo del GRUPO respondía "el
    // camión del territorio", que con dos camiones no es una pregunta con
    // respuesta. Antes se derivaba de `group.key` quitándole el prefijo
    // `sub-route:` — con la clave `territory:N` eso no empareja con nada y la
    // tarjeta decía "Vehículo por asignar" teniendo el camión asignado.
    const excluded = this.excludedVehicleIdSet();
    if (excluded.has(truckId)) return undefined;
    for (const subRoute of this.subRoutesById.values()) {
      const vehicle = (subRoute as unknown as { vehicle?: VehicleAttributes } | undefined)?.vehicle;
      if (vehicle?._id != null && String(vehicle._id) === truckId) return vehicle;
    }
    return undefined;
  }

  /**
   * @param group grupo al que pertenecen las paradas, cuando se conoce: de su
   *   sub-ruta salen los territorios contra los que se resuelve el de la
   *   entrega. Sin él (p. ej. los excluidos, que no llegaron a ningún grupo) la
   *   parada queda sin rótulo territorial en vez de mostrar uno arbitrario.
   */
  private makePointOf(
    orders: OrderAttributes[],
    group?: GroupPlanView,
  ): (id: string) => DeliveryPoint {
    const subRoute = group?.subRouteId ? this.subRoutesById.get(group.subRouteId) : undefined;
    return makePointOf(orders, {
      groupKeyByOrderId: this.autoGroupKeyByOrderId,
      weightByOrderId: this.weightByOrderId,
      weightByCustomer: this.weightByCustomer,
      territoryLabel: (o) => this.territoryPartsOf(o, subRoute, group),
    });
  }

  /**
   * Ruta y territorio de la entrega, rotulables por separado.
   *
   * El territorio se resuelve **contra los que declara la ruta**: un pedido
   * puede pertenecer a decenas de territorios y casi ninguno tiene relación con
   * quien lo reparte (ver `delivery-territory.ts`).
   */
  private territoryPartsOf(
    order: OrderAttributes | undefined,
    subRoute: SubRouteAttributes | undefined,
    group?: GroupPlanView,
  ): TerritoryParts {
    const { territory, mismatch } = deliveryTerritory(
      order?.routes as RouteRef[] | undefined,
      subRoute?.routes as RouteRef[] | undefined,
    );
    return {
      territory,
      // La ruta no depende de los territorios del maestro: si esos faltan, el
      // rótulo conserva la mitad que sí se puede afirmar. El último respaldo es
      // la sub-ruta que declara la ORDEN: es la única que tienen los excluidos,
      // que no llegaron a ningún grupo y salían sin rótulo de ruta.
      routeName: subRoute?.name || group?.label || subRouteNameOf(order) || undefined,
      territoryMismatch: mismatch || undefined,
    };
  }

  /**
   * Indexa el peso (lb) de cada parada del request (acumula entre grupos).
   *
   * Corre DESPUÉS de `seedOrderWeights` y lo sobreescribe a propósito: las paradas
   * agrupadas por cliente y las divididas por capacidad (`__sN`) tienen ids propios
   * y pesos propios que tienen que ganar sobre el peso por pedido.
   */
  private indexPlanWeights(request: PlanRequest): void {
    for (const o of request.orders) this.weightByOrderId.set(o.id, o.weight);
  }

  /**
   * Siembra el peso por PEDIDO de todas las órdenes cargadas.
   *
   * Existe porque `indexPlanWeights` solo corre por cada petición **emitida**, así
   * que un pedido sin flota nunca entraba al índice y `makePointOf` caía al peso
   * ACUMULADO de su cliente. Con dos pedidos del mismo cliente, cada fila mostraba
   * el total y la brecha lo sumaba dos veces: en la corrida medida, 11.875 lb donde
   * había 6.010 — un peso que no cabía en el camión que la misma pantalla decía que
   * alcanzaba, y una demanda inflada para el dimensionamiento.
   *
   * El piso por defecto es el mismo que usa el peso por cliente: un pedido sin
   * detalle de materiales pesa algo, y cero lo haría invisible en la brecha.
   */
  private seedOrderWeights(): void {
    for (const o of this.allOrders) {
      const id = orderIdOf(o);
      if (!id) continue;
      const weight = orderDeliveryWeightLb(o);
      this.weightByOrderId.set(id, weight > 0 ? weight : DEFAULT_ORDER_WEIGHT);
    }
  }


  /* ------------------- Detalle de pedidos por parada ------------------ */

  /**
   * Estado del detalle de pedidos: **uno abierto por lista**.
   *
   * Hay tres listas donde puede aparecer una entrega —las paradas de las rutas,
   * los que no entraron y los que nunca llegaron al motor— y el detalle de
   * materiales solo existía en la primera. Justamente la menos interesante
   * cuando lo que se está averiguando es por qué algo NO se entrega.
   *
   * Una por lista, no una global: comparar lo que sí entró contra lo que quedó
   * afuera es el uso natural, y con un solo panel global abrir uno cerraría el
   * otro. Dentro de cada lista sigue siendo una sola —una ruta tiene 30 paradas
   * y el diálogo ya es alto— igual que `toggleDetails` en la grilla de pedidos.
   */
  private readonly detailId = new Map<DetailList, string>();
  /**
   * Pedidos del detalle abierto de cada lista, resueltos **una vez** al abrirlo.
   *
   * No es un método llamado desde el template a propósito: se evaluaría en cada
   * ciclo de detección de cambios, recorriendo las órdenes de la corrida.
   */
  private readonly detailOrders = new Map<DetailList, StopOrdersView>();

  /** Id abierto en una lista, para marcar la fila y girar su chevron. */
  detailOpenIn(list: DetailList): string | null {
    return this.detailId.get(list) ?? null;
  }

  /* ------------------- La cobertura como cascada ---------------------- */

  /**
   * Pedidos de las visitas que el optimizador NO ubicó.
   *
   * Se derivan con `stopOrders`, que es la MISMA pieza con la que la pantalla muestra
   * el detalle de esa visita: si al abrirla dice "2 pedidos", acá cuenta 2. Una
   * segunda forma de contar lo mismo daría dos números para una sola pregunta.
   *
   * Se deduplica por CLIENTE porque una visita dividida (`__sN`) resuelve al cliente
   * completo: sin deduplicar, sus dos partes contarían sus pedidos dos veces. Un ítem
   * que no se puede resolver cuenta como uno — es el mínimo cierto, una visita
   * representa al menos un pedido, y no inventa un número mayor.
   */
  private unassignedOrdersOf(view: OfficePlanView): number {
    const items = (view.unassignedGroups ?? []).flatMap((g) => g.items ?? []);
    if (!items.length) return 0;

    // UNA pasada por los pedidos, no una por ítem. Antes se llamaba a `stopOrders`
    // por cada visita no ubicada y cada llamada recorría las órdenes: O(ítems ×
    // pedidos), y desde que la tarjeta de clientes también consume esta cuenta serían
    // dos recorridos completos por ciclo de detección.
    //
    // Da el MISMO número: `stopOrders` reconstruye el contenido de una visita
    // agrupando por cliente, así que un mapa de cliente → cantidad de pedidos es esa
    // misma cuenta con la misma regla.
    const pedidosPorCliente = new Map<string, number>();
    const ordenPorId = new Map<string, OrderAttributes>();
    for (const o of this.allOrders) {
      const cliente = customerIdOf(o);
      if (cliente) pedidosPorCliente.set(cliente, (pedidosPorCliente.get(cliente) ?? 0) + 1);
      ordenPorId.set(orderIdOf(o), o);
    }

    // Deduplicado por CLIENTE: una visita dividida (`__sN`) resuelve al cliente
    // completo, así que sus dos partes contarían sus pedidos dos veces.
    const vistos = new Set<string>();
    let total = 0;
    for (const item of items) {
      const base = basePlanOrderId(item.orderId);
      const cliente = customerIdOf(ordenPorId.get(base));
      const clave = cliente || base;
      if (vistos.has(clave)) continue;
      vistos.add(clave);
      // Un ítem que no se resuelve cuenta como UNO: es el mínimo cierto —una visita
      // representa al menos un pedido— y no inventa un número mayor.
      total += (cliente && pedidosPorCliente.get(cliente)) || 1;
    }
    return total;
  }


  /**
   * La cobertura de una oficina como cascada.
   *
   * El titular pasa a ser el RESULTADO: `plannable` se calcula antes del optimizador
   * y solo significa "tiene coordenadas e identidad", así que llamarlo "se
   * optimizaron" afirmaba un trabajo que todavía no había ocurrido.
   */
  coverageFunnelOf(view: OfficePlanView): CoverageFunnel | null {
    const c = view?.coverage;
    if (!c) return null;
    return coverageFunnel({
      loaded: c.loaded,
      invalidGeo: c.invalidGeo,
      missingIdentity: c.missingIdentity,
      plannable: c.plannable,
      unplanned: c.unplanned ?? 0,
      // La partición ya lo cuenta; no se recalcula acá.
      reached: c.planned,
      unassignedOrders: this.unassignedOrdersOf(view),
      // Las visitas que llegaron al optimizador: las ubicadas más las que no.
      reachedVisits: (view.dash?.total ?? 0),
    });
  }

  /** Peso REAL (lb) por cliente: `Σ baseQuantity × grossWeight/unidad`. */
  private buildWeightByCustomer(): void {
    const map = new Map<string, number>();
    for (const o of this.allOrders) {
      const weight = orderDeliveryWeightLb(o);
      map.set(
        customerIdOf(o),
        (map.get(customerIdOf(o)) ?? 0) + (weight > 0 ? weight : DEFAULT_ORDER_WEIGHT),
      );
    }
    this.weightByCustomer = map;
  }

  formatDuration(min: number): string {
    return formatDuration(min);
  }

  /* ---------------- Identidad de las listas de la cascada ---------------- */

  /**
   * `coverageFunnelOf` devuelve un objeto NUEVO por llamada y la plantilla lo llama en
   * cada ciclo de detección, así que sin `trackBy` el `*ngFor` destruye y recrea todas
   * sus vistas — y con ellas el `<button>` del descuento.
   *
   * Eso no es solo costo: un clic del navegador exige `mousedown` y `mouseup` sobre el
   * MISMO elemento. Con el botón reemplazado en el ciclo del `mousedown`, el `click`
   * nunca se emite y el control se ve vivo sin responder.
   *
   * La clave es el DATO y no el índice: un descuento que desaparece —su cantidad bajó a
   * cero— correría los siguientes, y el índice emparejaría el panel abierto con otro
   * destino.
   */
  trackFunnelLevel = (_index: number, level: { label: string }): string => level.label;

  trackFunnelDrop = (_index: number, drop: { target: string }): string => drop.target;

  /**
   * Mismo churn de identidad: `fleetGapCausesOf` devuelve `[...byCause.values()]`, que
   * son objetos nuevos por llamada. Hoy sus filas son informativas, así que no hay clic
   * que perder — se cierra el patrón antes de que alguien agregue un control ahí, que
   * es donde este defecto no falla donde se comete.
   */
  trackGapCause = (_index: number, cause: { reason: string }): string => cause.reason;

  /* -------- Por qué una acción de ruta no aplica, para decirlo -------- */

  /**
   * El motivo por el que no se puede reordenar ni replanificar la ruta, o `''`.
   *
   * En la barra fija los controles se **deshabilitan** en vez de ocultarse: ocultarlos
   * cambia el ancho de la barra y corre de lugar a los vecinos cada vez que cambia la
   * selección, y un botón que se mueve bajo el cursor es cómo se aprieta el que no era.
   * En una tarjeta ocultar está bien —es su propio contexto, sin vecinos que se corran—,
   * así que ahí se conserva el `*ngIf`.
   *
   * Los motivos se separan por CAUSA: "no la editaste" y "no tiene paradas suficientes"
   * se arreglan distinto, y un solo mensaje mandaría a la mitad al lugar equivocado.
   */
  routeRecalcBlockedReason(route: ReviewRoute | undefined): string {
    if (!route) return 'Elegí una ruta en el mosaico.';
    if (!route.custom) return 'Esta ruta no tiene cambios: su orden es el que propuso el optimizador.';
    if (!this.recalcAssumptionsOf(route)) {
      return 'Falta la jornada o la atención por parada de esta ruta.';
    }
    if (!canRecalcRoute(route, this.reviewStopCoord)) {
      return 'Necesita al menos dos paradas con coordenadas.';
    }
    return '';
  }

  /** El motivo por el que no hay cambios que deshacer en la ruta, o `''`. */
  routeUndoBlockedReason(route: ReviewRoute | undefined): string {
    if (!route) return 'Elegí una ruta en el mosaico.';
    if (!route.custom) return 'Esta ruta no tiene cambios que deshacer.';
    return '';
  }

  /** `true` si el panel de ese descuento está abierto. */
  dropOpen(view: OfficePlanView, target: 'excluded' | 'fleet-gap' | 'unassigned'): boolean {
    if (target === 'excluded') return !!view.excludedOpen;
    if (target === 'fleet-gap') return !!view.fleetGapOpen;
    return !!view.unassignedOpen;
  }

  /**
   * Los motivos de un descuento, para leerlos sin abrirlo. **Sin cuentas.**
   *
   * La cantidad ya la trae el descuento y está en PEDIDOS; las de los motivos van por
   * visita. Dos cifras contiguas en unidades distintas es el defecto que la cascada
   * vino a eliminar, y cuántos hay por motivo se lee dentro del panel.
   */
  dropSubtitle(view: OfficePlanView, target: 'excluded' | 'fleet-gap' | 'unassigned'): string {
    if (target !== 'unassigned') return '';
    return unassignedSummary(view.unassignedGroups, { counts: false });
  }


  /** Abre la lista donde se revisan los pedidos de un descuento de la cascada. */
  openDropTarget(view: OfficePlanView, target: 'excluded' | 'fleet-gap' | 'unassigned'): void {
    if (target === 'excluded') view.excludedOpen = !view.excludedOpen;
    else if (target === 'fleet-gap') view.fleetGapOpen = !view.fleetGapOpen;
    else view.unassignedOpen = !view.unassignedOpen;
  }

  /** Pedidos del detalle abierto en una lista, o `null` si no hay ninguno. */
  detailOrdersIn(list: DetailList): StopOrdersView | null {
    return this.detailOrders.get(list) ?? null;
  }

  /**
   * Abre el detalle de `orderId` en `list`, o lo cierra si ya estaba abierto.
   *
   * Los pesos se calculan con el mismo criterio que `buildWeightByCustomer` para
   * que la suma del panel coincida con el peso que la fila informa.
   */
  toggleDetail(list: DetailList, orderId: string): void {
    if (this.detailId.get(list) === orderId) {
      this.detailId.delete(list);
      this.detailOrders.delete(list);
      return;
    }
    this.detailId.set(list, orderId);
    this.detailOrders.set(
      list,
      stopOrders(orderId, this.allOrders, (o) => {
        const weight = orderDeliveryWeightLb(o);
        return weight > 0 ? weight : DEFAULT_ORDER_WEIGHT;
      }),
    );
  }

  /**
   * Cuánto tiempo del día consume una parada: viaje + atención.
   *
   * **Solo informativo**: no toca el plan ni lo que se envía al motor. Se calcula
   * en el template pero para UNA parada —la expandida—, así que es una llamada
   * por ciclo de detección de cambios, no 226.
   */
  stopCostOf(stop: StopRow, index: number): StopTimeCost {
    return stopTimeCost(stop, index === 0);
  }

  /* ------------- El recorrido de decisión de una parada ---------------- */

  /**
   * El método de la partición traducido a la base del tramo de territorio.
   *
   * Sin asignación devuelve `undefined` y NO `declared`: no saberlo no es lo mismo
   * que saber que lo declara la ruta, y el tramo se redacta distinto.
   */
  private basisOf(method?: TerritoryAssignment['method']): TerritoryBasis | undefined {
    if (method === 'sub-route') return 'declared';
    if (method === 'territory') return 'single-fleet';
    if (method === 'territory-nearest') return 'nearest';
    return undefined;
  }

  /**
   * Lo que se sabe de una parada, para el módulo que arma el recorrido.
   *
   * Una sola composición para las dos vistas: si el resultado y el gestor armaran
   * cada uno la suya, la misma parada se explicaría distinto según dónde se la mire.
   */
  private decisionInput(
    point: DeliveryPoint | undefined,
    ctx: {
      subRouteId?: string;
      vehicleLabel?: string;
      placement: Placement;
      consequence?: MoveConsequence;
    },
  ): StopDecisionInput {
    const assignment = point
      ? this.assignmentByOrderId.get(basePlanOrderId(point.orderId))
      : undefined;
    // Los parámetros SIN el rótulo del camión: la lectura compacta no lo usa, y
    // fusionarlo acá crearía un objeto nuevo por parada y por ciclo de detección.
    const params = ctx.subRouteId ? this.paramsBySubRouteId.get(ctx.subRouteId) : undefined;
    return {
      territoryName: point?.territory || assignment?.territoryName,
      routeName: point?.routeName,
      vehicleLabel: ctx.vehicleLabel,
      basis: this.basisOf(assignment?.method),
      distanceKm: assignment?.distanceKm,
      // El tamaño de la flota sale de los MISMOS parámetros: dos fuentes darían un
      // tramo que dice "única opción" sobre un territorio con dos camiones.
      fleetSize: params?.fleetSize,
      params,
      territoryMismatch: point?.territoryMismatch,
      placement: ctx.placement,
      consequence: ctx.consequence,
    };
  }

  /* --------------- Los parámetros de planificación de una ruta -------------- */

  /**
   * Los parámetros de una ruta, con el rótulo de su camión.
   *
   * El rótulo se fusiona acá y no se guarda: la tarjeta ya lo resuelve por
   * `truck_id`, y guardarlo aparte sería una segunda fuente para el mismo nombre.
   */
  routeParamsOf(route: { subRouteId?: string; vehicleLabel?: string }): RoutePlanParams | null {
    const stored = route?.subRouteId ? this.paramsBySubRouteId.get(route.subRouteId) : undefined;
    if (!stored) return null;
    // Una ruta del GESTOR no trae el rótulo —`ReviewRoute` no lo tiene—, así que se
    // resuelve contra la vista del plan. Una sola definición para las dos: si el
    // gestor lo compusiera por su cuenta, el mismo camión se leería distinto en el
    // panel y en la tarjeta.
    const label =
      route.vehicleLabel ??
      this.confirmableTruckViews.find((t) => t.subRouteId === route.subRouteId)?.vehicleLabel;
    return { ...stored, vehicleLabel: label };
  }

  /** Las filas del panel de una ruta, o `null` si no se conocen sus parámetros. */
  routeParamRowsOf(route: { subRouteId?: string; vehicleLabel?: string }): ParamRow[] | null {
    const params = this.routeParamsOf(route);
    return params ? paramRows(params) : null;
  }

  /** Rótulo de la procedencia de un parámetro. */
  paramSourceLabel(source?: 'master' | 'fallback'): string {
    return source ? PARAM_SOURCE_LABELS[source] : '';
  }

  /**
   * `true` si la capacidad con la que se planificó esta ruta es un supuesto.
   *
   * Se declara junto al número: hoy la tarjeta rellena con el valor por defecto y lo
   * muestra igual que uno real, así que un plan armado sobre un camión supuesto de
   * diez toneladas se lee idéntico a uno armado sobre el camión que existe.
   */
  capacityIsAssumed(route: { subRouteId?: string }): boolean {
    return capacityAssumed(
      route?.subRouteId ? this.paramsBySubRouteId.get(route.subRouteId) : undefined,
    );
  }

  /**
   * Parada con su recorrido de decisión abierto.
   *
   * Estado PROPIO, separado de `detailId`: son dos preguntas —por qué está acá y qué
   * se le entrega— y compartir el estado haría que abrir una cerrara la otra, que es
   * justo el acoplamiento que este control vino a deshacer.
   *
   * Uno por vez y no uno por lista como el detalle de materiales: ahí tener uno por
   * lista servía para comparar lo que entró contra lo que quedó afuera, y el recorrido
   * solo existe sobre paradas de una ruta — que viven en una vista a la vez, el
   * resultado o el gestor, nunca las dos.
   */
  decisionOpenId: string | null = null;

  /** Abre el recorrido de una parada, o lo cierra si ya estaba abierto. */
  toggleDecision(orderId?: string): void {
    if (!orderId) return;
    this.decisionOpenId = this.decisionOpenId === orderId ? null : orderId;
  }

  /** Sub-ruta con el panel de parámetros abierto. Uno por vez, como el detalle. */
  paramsOpenId: string | null = null;

  /** Abre el panel de parámetros de una ruta, o lo cierra si ya estaba abierto. */
  toggleRouteParams(subRouteId?: string): void {
    if (!subRouteId) return;
    this.paramsOpenId = this.paramsOpenId === subRouteId ? null : subRouteId;
  }

  /**
   * El recorrido de una parada del RESULTADO.
   *
   * Su ubicación es siempre `run`: esta vista muestra lo que la corrida produjo, y
   * ahí no hay nada que el operador haya movido.
   */
  stopDecisionOf(stop: StopRow, truck: TruckRouteView): StopDecision {
    return stopDecision(
      this.decisionInput(stop, {
        subRouteId: truck.subRouteId,
        vehicleLabel: truck.vehicleLabel,
        placement: 'run',
      }),
    );
  }

  /**
   * El recorrido de una parada del GESTOR.
   *
   * La ubicación se DERIVA del `original` de la ruta, que es por construcción el
   * orden del motor: una parada que no está ahí llegó por una decisión del operador.
   * Sin conjunto aparte que mantener, igual que el pool.
   */
  reviewStopDecisionOf(stop: ReviewStop, route: ReviewRoute): StopDecision {
    const planned = this.confirmableTruckViews.find((t) => t.subRouteId === route.subRouteId);
    const point = this.decisionPointByOrderId.get(basePlanOrderId(stop.orderId));
    const placement: Placement = stop.injected
      ? 'injected'
      : route.original.some((o) => o.orderId === stop.orderId)
        ? 'run'
        : 'moved';

    return stopDecision(
      this.decisionInput(point, {
        subRouteId: route.subRouteId,
        vehicleLabel: planned?.vehicleLabel,
        placement,
        // Una parada de la corrida no tiene consecuencia que declarar: está donde
        // el plan la puso, y sus números YA son los de la ruta.
        consequence: placement === 'run' ? undefined : this.moveConsequenceOf(stop, route),
      }),
    );
  }

  /**
   * Qué cambia por tener una parada donde el operador la puso.
   *
   * Enuncia; no recomienda. La compuerta de territorio ya decide lo que no se puede
   * hacer, y para lo que sí se puede el operador tiene contexto que la pantalla no
   * tiene. El costo —una estimación por parada manual y por ciclo— se acepta por lo
   * mismo que se aceptó no memoizar el resto: son pocas, y un memo habría que
   * invalidarlo en las siete mutaciones que cambian una ruta.
   */
  private moveConsequenceOf(stop: ReviewStop, route: ReviewRoute): MoveConsequence {
    const from = this.reviewStopCoord(stop.orderId);
    const others = route.stops
      .filter((s) => s.orderId !== stop.orderId)
      .map((s) => this.reviewStopCoord(s.orderId))
      .filter((c): c is [number, number] => !!c);
    const day = this.estimatedDayOf(route);
    const over = day ? this.estimatedExceedsMin(day) : 0;

    return {
      detourKm: from && others.length ? nearestStopKm(from, others) : undefined,
      loadLb: this.reviewLoadLb(route),
      capacityLb: route.capacityLb,
      overShiftMin: over > 0 ? over : undefined,
    };
  }

  /* ------------------------ Colapso de las rutas ---------------------- */

  /** Abre o cierra las paradas de una ruta. */
  toggleTruck(truck: TruckRouteView): void {
    truck.open = !truck.open;
  }

  /**
   * Deja abiertas las rutas con coincidencias y cerradas las demás.
   *
   * El buscador **escribe** `open`, no lo pisa con una segunda capa: si el
   * estado visible fuera `open || hayCoincidencias`, la tarjeta no se podría
   * cerrar mientras se busca —el clic apagaba una bandera que la otra mitad de
   * la condición volvía a encender— y el usuario no tiene forma de saber que hay
   * dos estados peleando.
   *
   * El costo es que una ruta abierta a mano se cierra al buscar y al limpiar. Es
   * el comportamiento correcto: el filtro redefine qué es relevante, y volver a
   * "todo cerrado" es justamente lo que se pidió como estado de arranque.
   */
  private syncTruckOpenState(hitsByTruck: Map<string, number>): void {
    for (const op of this.officePlans) {
      for (const t of op.truckViews) {
        t.open = this.filterActive && (hitsByTruck.get(t.truckId) ?? 0) > 0;
      }
    }
  }

  /* ----------------------------- Mapa -------------------------------- */

  /**
   * Color a enviarle al motor para los camiones de este grupo, de modo que la
   * línea y las paradas del `geojson` lleguen ya pintadas como su tarjeta.
   */
  private colorsOfGroup(group: GroupPlanView, request: PlanRequest): Record<string, string> {
    const color = group.color;
    if (!color) return {};
    return Object.fromEntries((request.trucks ?? []).map((t) => [t.id, color]));
  }

  /**
   * Rearma las opciones del selector desde lo resuelto hasta ahora y refresca
   * el mapa. Se llama en cada publicación parcial: las rutas van apareciendo.
   */
  private rebuildMapOptions(): void {
    this.routeOptions = this.officePlans.flatMap((op) =>
      op.truckViews.map((t) => ({
        truckId: t.truckId,
        label: t.label,
        color: t.color,
        stops: t.stops.length,
      })),
    );
    // Por defecto, la primera ruta: el problema a resolver es justamente que
    // aparecían todas juntas. Si la seleccionada ya no existe, se vuelve a ella.
    const stillThere = this.routeOptions.some((o) => o.truckId === this.selectedTruckId);
    if (!stillThere) this.selectedTruckId = this.routeOptions[0]?.truckId ?? null;
    this.rebuildRouteTags();
    this.refreshVisibleMap();
  }

  /**
   * Rearma los tags de entrega y descarta del filtro los que ya no existen.
   *
   * Sin la limpieza, un tag seleccionado que desaparece —al volver a calcular con
   * otro alcance— dejaría el filtro activo contra una llave inalcanzable: la
   * pantalla vacía y ningún tag encendido que explique por qué.
   */
  private rebuildRouteTags(): void {
    this.routeTags = buildRouteTags(this.officePlans);
    const alive = new Set(this.routeTags.map((t) => t.key));
    let dropped = false;
    for (const key of [...this.selectedTagKeys]) {
      if (alive.has(key)) continue;
      this.selectedTagKeys.delete(key);
      dropped = true;
    }
    if (dropped) this.applyFilters();
  }

  /** Cambia la ruta que muestra el mapa. `null` = todas. */
  selectMapRoute(truckId: string | null): void {
    this.selectedTruckId = truckId;
    this.refreshVisibleMap();
  }

  /**
   * Abre el mapa de UNA ruta del plan, superpuesto.
   *
   * NO toca `activeTab`, `selectedTruckId` ni `reviewOpen`: la capa es una consulta y
   * al cerrarla el diálogo queda exactamente como estaba.
   */
  openRouteOnMap(truckId: string | undefined | null, label?: string): void {
    if (!truckId) return;
    this.showMapOverlay(truckId, label ?? this.labelOfTruck(truckId), undefined);
  }

  /** Rótulo de una ruta del plan, para la barra de la capa. */
  private labelOfTruck(truckId: string): string {
    return this.routeOptions.find((o) => o.truckId === truckId)?.label ?? '';
  }

  /**
   * Abre la capa y arma su vista. Diferido el `resize()` porque Leaflet no detecta el
   * cambio de tamaño de su contenedor —el mismo motivo por el que `toggleMapExpanded`
   * lo agenda en vez de confiar en el reflow—.
   */
  private showMapOverlay(truckId: string, label: string, edited?: ReviewRoute): void {
    const view = this.buildMapView(truckId, edited);
    this.overlayMarkerGroups = view.groups;
    this.overlayPolylines = view.lines;
    this.mapOverlay = { truckId, label, edited: !!edited };
    setTimeout(() => this.geoManager?.resize(), 60);
  }

  /** Cierra la capa del mapa. El diálogo queda como estaba. */
  closeMapOverlay(): void {
    this.mapOverlay = null;
    this.overlayMarkerGroups = [];
    this.overlayPolylines = [];
    // El mapa principal vuelve a montarse: hay que remedir su contenedor.
    setTimeout(() => this.geoManager?.resize(), 60);
  }

  /** Llave del contenedor de la capa: el `GeoManager` solo AGREGA marcadores. */
  get overlayRenderKey(): string {
    const o = this.mapOverlay;
    return o ? `${o.truckId}${o.edited ? '|editado' : ''}` : 'cerrado';
  }

  /**
   * `truckId` de una ruta del gestor. Traduce entre los dos espacios de identidad:
   * el resultado nombra sus rutas por camión y el gestor por sub-ruta.
   *
   * Es 1:1 porque una sub-ruta declara UN vehículo (`subRoute.vehicle`, campo único),
   * así que dos camiones no pueden caer en la misma sub-ruta.
   */
  truckIdOfReviewRoute(route: ReviewRoute): string | undefined {
    return this.confirmableTruckViews.find((t) => t.subRouteId === route.subRouteId)?.truckId;
  }

  /* ---------- Los tiempos que el gestor muestra ---------- */

  /**
   * El recorrido de una ruta, **según lo que la pestaña está mostrando**.
   *
   * ÚNICO resolvedor. Dos vistas que eligen la fuente por su cuenta terminan
   * afirmando 10:33 y 10:48 sobre la misma parada, en la pantalla que decide si el
   * camión sale.
   *
   * - Pestaña del optimizador → los tiempos del PLAN. Son ciertos aunque la ruta se
   *   haya editado: esa pestaña pinta `r.original`, que ES el orden del motor, y lo
   *   editado vive en la otra.
   * - Pestaña personalizada → los del recálculo, y solo si sigue vigente:
   *   `recalcOf` ya filtra los obsoletos, así que la garantía se hereda sin repetir
   *   el chequeo.
   *
   * `null` significa "no hay horas que afirmar", y la plantilla no pinta ni la
   * salida ni el regreso — no un cero.
   *
   * Se llama **una vez por ruta**, desde el contexto del cuerpo, y el resultado baja
   * a las filas. Llamarlo por parada reconstruiría el mapa en cada fila.
   */
  reviewTimelineOf(route: ReviewRoute): RouteTimeline | null {
    const planned = this.confirmableTruckViews.find((t) => t.subRouteId === route.subRouteId);
    if (this.reviewTab === 'optimizer') return timelineFromPlan(planned);

    // Un recálculo explícito es la respuesta MÁS específica: si existe y sigue
    // vigente, gana — incluso cuando el motor devolvió el mismo orden del plan.
    const fromRecalc = timelineFromRecalc(this.recalcOf(route));
    if (fromRecalc) return fromRecalc;

    // Una ruta SIN EDITAR es, parada por parada y en el mismo orden, la del plan:
    // `custom` se recalcula contra `original` en cada mutación, y `original` es por
    // construcción el orden del motor. Así que las horas del plan describen
    // exactamente lo que esta pestaña está pintando, y esconderlas era un falso
    // negativo — pedir recalcular para volver a obtener el orden que ya se tiene.
    if (!route.custom) return timelineFromPlan(planned);

    // Editada y sin recalcular: lo único que se puede afirmar es una ESTIMACIÓN, y
    // se afirma marcada. `null` solo cuando no hay ni con qué estimar.
    return this.estimatedTimelineOf(route)?.timeline ?? null;
  }

  /**
   * El recorrido ESTIMADO de una ruta, con su jornada. Una sola función para los dos
   * —la columna y la insignia— porque son el mismo cálculo: separarlos daría totales
   * que difieren por redondeo.
   */
  private estimatedTimelineOf(route: ReviewRoute): EstimatedTimeline | null {
    return estimateRouteTimeline({
      orderIds: route.stops.map((s) => s.orderId),
      coordOf: this.reviewStopCoord,
      warehouse: this.reviewWarehouseBySubRouteId.get(route.subRouteId),
      calibration: this.runCalibration,
      serviceMinPerStop: this.assumedServiceMin,
      shiftStart: this.assumedShift.start,
    });
  }

  /** El costo de tiempo de una visita, sobre la forma única del timeline. */
  timelineStopCost(stop: StopTimeline | undefined, isFirst: boolean): StopTimeCost {
    return stopTimeCost(stop, isFirst);
  }

  /* ---------- La jornada ESTIMADA de una ruta sin tiempos ---------- *
   *
   * El gestor contesta en vivo el techo de CARGA y no el de TIEMPO. Mientras se
   * arrastra, la pregunta es "¿me paso del día?", y frenar a pedirle el número al
   * motor después de cada movimiento es lo que esto evita.
   *
   * Es geometría en línea recta, pero **calibrada con la propia corrida**: ver
   * `route-estimate`.
   */

  /**
   * Lo que costó un kilómetro en línea recta en ESTA corrida, en minutos.
   *
   * `null` mientras no haya con qué calibrar, y entonces no se estima nada.
   */
  private runCalibration: RunCalibration = { urbanMinPerKm: null, haulMinPerKm: null };

  /**
   * Deriva el coeficiente de las rutas que el motor acaba de resolver.
   *
   * Dos conjuntos de muestras, uno por régimen. Sin almacén resoluble una ruta no
   * aporta muestra de carretera —no habría contra qué medir la ida— pero sus tramos
   * entre paradas sirven igual: son independientes del origen.
   */
  private calibrateRunEstimate(): void {
    const haul: CalibrationSample[] = [];
    const urban: CalibrationSample[] = [];
    for (const op of this.officePlans) {
      const warehouse = resolveStart(op.office);
      for (const t of op.truckViews ?? []) {
        // Las paradas del plan ya traen `lat`/`lon` y su tramo: no hace falta el
        // índice de coordenadas ni depender de que ya esté armado.
        const samples = routeCalibrationSamples(t.stops, warehouse, t.outboundMin ?? 0);
        haul.push(...samples.haul);
        urban.push(...samples.urban);
      }
    }
    // El MISMO cociente de totales para cada régimen, por separado: es lo que evita
    // que la carretera —que aporta casi todos los kilómetros— decida el costo de un
    // tramo de ciudad.
    this.runCalibration = {
      urbanMinPerKm: calibrateMinPerKm(urban),
      haulMinPerKm: calibrateMinPerKm(haul),
    };
  }

  /**
   * La jornada estimada de una ruta, o `null` si no corresponde estimarla.
   *
   * **Solo donde no hay tiempos que afirmar.** La puerta es la que ya existe: si
   * `reviewTimelineOf` devuelve algo, hay horas ciertas y una estimación al lado
   * serían dos números para la misma pregunta. En cuanto se recalcula —o se deshace
   * la edición y vuelven las del plan— esto se retira solo, sin una condición nueva
   * que alguien tenga que acordarse de apagar.
   */
  estimatedDayOf(route: ReviewRoute): EstimatedDay | null {
    // La insignia acompaña a la columna: si la ruta no está mostrando tiempos
    // estimados, tampoco hay jornada estimada que declarar.
    if (!this.reviewTimelineOf(route)?.estimated) return null;
    return this.estimatedTimelineOf(route)?.day ?? null;
  }

  /** Minutos que la jornada estimada se pasa del turno. `0` si cabe. */
  estimatedExceedsMin(day: EstimatedDay): number {
    return estimateExceedsShiftMin(day.totalMin, this.assumedShift);
  }

  /**
   * Abre el mapa en una ruta DEL GESTOR, decidiendo qué mapa según la pestaña.
   *
   * En la pestaña del optimizador el mapa del plan ya es cierto: muestra el orden del
   * motor, que es exactamente lo que esa pestaña presenta. En la personalizada —donde
   * se cambian los datos— las paradas las pone el gestor.
   *
   * Una ruta que NO fue editada usa el mapa del plan aunque se accione desde la
   * pestaña personalizada: su contenido y su orden son los del motor, así que suprimir
   * el trayecto sería perder información sin ganar honestidad.
   */
  openReviewRouteOnMap(route: ReviewRoute): void {
    const truckId = this.truckIdOfReviewRoute(route);
    if (!truckId) return;
    // El gestor NO se cierra: la capa va encima y al cerrarla se sigue editando.
    const edited = this.reviewTab === 'custom' && route.custom ? route : undefined;
    this.showMapOverlay(truckId, route.label, edited);
  }

  /**
   * Compone marcadores y trazos de la selección, fusionando el `geojson` de
   * todos los grupos (uno por sub-ruta) en una sola vista.
   */
  private refreshVisibleMap(): void {
    const view = this.buildMapView(this.selectedTruckId);
    this.visibleMarkerGroups = view.groups;
    this.visiblePolylines = view.lines;
  }

  /**
   * Arma marcadores y trazos de una selección. **Una sola definición**, que usan el
   * mapa principal y la capa superpuesta: dos copias divergirían, y acá divergir
   * significa que las dos vistas muestran cosas distintas de la misma ruta.
   *
   * @param edited cuando se da, las paradas salen del GESTOR y no se dibuja trayecto.
   */
  private buildMapView(
    truckId: string | null,
    edited?: ReviewRoute,
  ): { groups: MarkerGroup[]; lines: MapPolyline[] } {
    const groups: MarkerGroup[] = [];
    const lines: MapPolyline[] = [];
    let warehouseAdded = false;
    // Las paradas ya resueltas SON los datos del cliente: nombre, dirección,
    // ruta, territorio, peso y horas. Resolverlos otra vez desde las órdenes
    // sería una segunda copia de la misma traducción, y el `geojson` solo trae
    // el id de Mongo del pedido — que es lo que rotulaba los pines.
    const stopByOrderId = new Map<string, StopRow>();
    for (const op of this.officePlans) {
      for (const t of op.truckViews) {
        for (const s of t.stops) stopByOrderId.set(s.orderId, s);
      }
    }
    for (const geojson of this.geojsonByGroup.values()) {
      const view = routeMapView(geojson, truckId, (id) => {
        // El serial ya viene en la parada (`makePointOf`): resolverlo otra vez acá
        // era una segunda copia de la misma traducción.
        return stopByOrderId.get(id);
      });
      for (const g of view.markerGroups) {
        // El almacén viene en cada `geojson`; se pinta una sola vez. Se conserva
        // también con orden editado: toda ruta sale de ahí y vuelve, así que sin él
        // una ruta aislada no se interpreta.
        if (g.groupName === WAREHOUSE_GROUP_NAME) {
          if (warehouseAdded) continue;
          warehouseAdded = true;
          groups.push(g);
          continue;
        }
        // Con orden editado las paradas del PLAN no entran: las pone el gestor, que es
        // el único que sabe qué tiene la ruta ahora.
        if (edited) continue;
        groups.push(g);
      }
      // El trayecto por calle lo calculó el motor para SU orden. Con orden editado no
      // se dibuja ninguno: `evaluate-route` evalúa una secuencia dada pero devuelve
      // tiempo y distancia, no geometría, así que el trayecto real no existe. Dibujar
      // el del motor afirmaría una secuencia de calles que nadie va a recorrer.
      if (!edited) {
        lines.push(...view.polylines.map((p) => ({ points: p.points, color: p.color })));
      }
    }
    if (edited) {
      groups.push(this.editedRouteMarkers(edited, stopByOrderId));
      // …salvo que el motor YA haya evaluado su secuencia vigente. Ahí el trayecto
      // existe y es el que se va a recorrer, así que la razón para no dibujarlo
      // desaparece. Un resultado obsoleto no pasa: `recalcOf` es la única puerta.
      const path = this.recalcOf(edited)?.path ?? [];
      if (path.length > 1) lines.push({ points: path, color: edited.color });
    }

    return { groups, lines };
  }

  /**
   * Los pines de una ruta con orden editado: las paradas que tiene AHORA, numeradas en
   * el orden vigente.
   *
   * Las coordenadas se resuelven por `orderId` contra las paradas ya resueltas del
   * plan, que las traen (`StopRow extends DeliveryPoint`). Volver a las órdenes sería
   * una segunda copia de la misma traducción — el mismo criterio que `refreshVisibleMap`
   * ya aplica para los rótulos.
   *
   * Sin hora de llegada: la del plan pertenece a otra secuencia, y es la misma razón
   * por la que la lista de paradas no recalcula tiempos sobre un orden manual.
   */
  private editedRouteMarkers(
    route: ReviewRoute,
    stopByOrderId: Map<string, StopRow>,
  ): MarkerGroup {
    const markers: MarkerData[] = [];
    route.stops.forEach((stop, index) => {
      const point = stopByOrderId.get(stop.orderId);
      // Sin coordenadas no hay pin que poner. Se omite en silencio: es el mismo
      // criterio del plan, que tampoco inventa una posición.
      if (point?.lat == null || point?.lon == null) return;
      markers.push({
        title: `${index + 1}. ${stop.name}`,
        description: stop.address || undefined,
        latitude: point.lat,
        longitude: point.lon,
        // El `order` es lo que hace que el pin salga numerado.
        order: index + 1,
      });
    });
    return { groupName: route.label, groupColor: route.color, markers };
  }

  /**
   * Clave que fuerza recrear el `GeoManager` al cambiar de ruta: el componente
   * solo AGREGA marcadores (no limpia los previos), así que sin recrearlo los
   * pines de la ruta anterior quedarían apilados. Mismo mecanismo que el drawer
   * de preventa usa para cambiar de día.
   */
  get mapRenderKey(): string {
    return this.selectedTruckId ?? 'all';
  }

  trackByKey(_index: number, key: string): string {
    return key;
  }

  /** Nombre de la ruta seleccionada, para la leyenda. */
  get selectedRouteLabel(): string {
    return this.routeOptions.find((o) => o.truckId === this.selectedTruckId)?.label ?? '';
  }

  /**
   * Si se muestra el panel colapsable "Detalle por ruta".
   *
   * **Oculto**: era un chip por ruta con su color, su cantidad de paradas y su
   * estado, y los tags de entrega ya son exactamente eso —arriba, siempre a la
   * vista y accionables— así que el panel repetía la información un clic más
   * abajo.
   *
   * No se borra porque sigue siendo lo único que muestra, por ruta, el aviso de
   * "camión y jornada por defecto" (`fleetFromMaster`) y la línea agregada de
   * `groupsSummary`. Las rutas que fallaron y su reintento sí tienen su propio
   * banner, así que ese aviso no se pierde. Poner esto en `true` lo devuelve
   * entero.
   */
  showGroupsPanel: boolean = false;

  /* ------------------------- Filtro por entrega ----------------------- */

  /**
   * Entregas de la corrida, como tags seleccionables.
   *
   * Se rearman en cada publicación parcial, junto con las opciones del mapa: los
   * grupos van apareciendo y el tag de una entrega tiene que existir apenas su
   * sección esté montada, no al final de la corrida.
   */
  routeTags: RouteTag[] = [];

  /**
   * Entregas seleccionadas. Vacío = todas, igual que una búsqueda vacía.
   *
   * Multi-selección y no una sola —a diferencia del selector del mapa, que aísla
   * UNA ruta para poder seguir su recorrido—: acá la pregunta es "comparemos
   * estas tres", y la lista aguanta varias sin volverse ilegible.
   */
  private selectedTagKeys = new Set<string>();

  /** ¿Este tag está seleccionado? */
  tagActive(tag: RouteTag): boolean {
    return this.selectedTagKeys.has(tag.key);
  }

  /** Selecciona o quita una entrega del filtro. */
  toggleTag(tag: RouteTag): void {
    if (this.selectedTagKeys.has(tag.key)) this.selectedTagKeys.delete(tag.key);
    else this.selectedTagKeys.add(tag.key);
    this.applyFilters();
  }

  /** Vuelve a "todas las entregas". */
  clearTags(): void {
    if (!this.selectedTagKeys.size) return;
    this.selectedTagKeys = new Set<string>();
    this.applyFilters();
  }

  /** Cuántas entregas están seleccionadas, para el botón de limpiar. */
  get selectedTagCount(): number {
    return this.selectedTagKeys.size;
  }

  /* ----------------------------- Búsqueda ----------------------------- */

  /** Texto tipeado. El filtro se aplica con retardo, no en cada tecla. */
  searchText = '';
  /** `true` mientras haya una búsqueda vigente. */
  searchActive = false;
  /** Resumen de dónde apareció lo buscado ("3 en rutas · 1 sin asignar"). */
  searchSummaryText = '';
  /**
   * Claves que coinciden. Es un **objeto estable**: se reemplaza solo al cambiar
   * la consulta, y el template pregunta con `has()` — O(1) y sin allocar por
   * ciclo de detección de cambios. Evaluar el predicado en el template
   * normalizaría tres campos por fila y por ciclo: ~700 por ciclo con 226
   * paradas.
   */
  private searchHits = new Set<string>();
  /** Coincidencias por ruta, para "N de M paradas coinciden". */
  private hitsByTruck = new Map<string, number>();
  private searchDebounce?: ReturnType<typeof setTimeout>;

  /**
   * Retardo de 200 ms: el filtrado es en memoria, así que sigue el precedente
   * del filtro local de `address-step`. Los 500 ms de `loads.component` son para
   * consultas al servidor.
   */
  onSearchChange(text: string): void {
    this.searchText = text;
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.applyFilters(), 200);
  }

  clearSearch(): void {
    this.searchText = '';
    clearTimeout(this.searchDebounce);
    this.applyFilters();
  }

  /** ¿Esta parada entra en el filtro vigente? */
  stopMatches(stop: StopRow): boolean {
    return !this.filterActive || this.searchHits.has(stop.orderId);
  }

  /** ¿Este punto (no asignado o excluido) entra en el filtro vigente? */
  pointMatches(point: DeliveryPoint): boolean {
    return !this.filterActive || this.searchHits.has(pointKey(point));
  }

  /** ¿Algún punto del grupo coincide? Para ocultar un motivo sin coincidencias. */
  anyPointMatches(points: readonly DeliveryPoint[]): boolean {
    if (!this.filterActive) return true;
    return (points ?? []).some((p) => this.searchHits.has(pointKey(p)));
  }

  /** Coincidencias de una ruta, para declarar el filtro sin tocar sus totales. */
  truckHits(truck: TruckRouteView): number {
    return this.hitsByTruck.get(truck.truckId) ?? 0;
  }

  /**
   * Los tiles del mosaico que el filtro deja ver.
   *
   * Mismo criterio que el `[hidden]` de la tarjeta en modo lista: una ruta sin
   * paradas que coincidan no se muestra. Acá se materializa la lista porque el
   * modo grid necesita saber CUÁL es la primera visible para poder seleccionarla.
   */
  visibleTrucksOf(view: OfficePlanView): TruckRouteView[] {
    const trucks = view?.truckViews ?? [];
    if (!this.filterActive) return [...trucks];
    return trucks.filter((t) => this.truckHits(t) > 0);
  }

  /**
   * La ruta que el contenedor de paradas está mostrando.
   *
   * **Deriva, no almacena.** Si la elegida quedó fuera del filtro, el panel estaría
   * mostrando una ruta que no está en el mosaico, así que cae a la primera visible.
   * La elección explícita se conserva en `gridTruckId` mientras siga siendo válida:
   * al limpiar el filtro el usuario vuelve donde estaba, en vez de a la primera.
   *
   * Sin tiles visibles devuelve `undefined`: no se inventa una ruta.
   */
  selectedTruckOf(view: OfficePlanView): TruckRouteView | undefined {
    const visible = this.visibleTrucksOf(view);
    if (!visible.length) return undefined;
    const chosen = visible.find((t) => t.truckId === this.gridTruckId);
    return chosen ?? visible[0];
  }

  /** ¿Este tile es el que se está mostrando? Para marcarlo en el mosaico. */
  isTruckSelected(view: OfficePlanView, truck: TruckRouteView): boolean {
    return this.selectedTruckOf(view)?.truckId === truck.truckId;
  }

  /** Elige una ruta en el mosaico. */
  selectTruck(truck: TruckRouteView): void {
    this.gridTruckId = truck.truckId;
  }

  /* ------------------- El divisor del modo grid --------------------- */

  /**
   * Piso de legibilidad del mosaico: un tile de 10rem con su padding y el gap.
   *
   * En px porque el arrastre trabaja en px. Asume la raíz en 16px, que es lo que
   * usa el tema; si eso cambiara, el piso se corre un poco y nada se rompe.
   */
  private static readonly TILES_MIN_PX = 224;

  /** El ancho de arranque, el mismo que el `22rem` del layout por omisión. */
  private static readonly TILES_DEFAULT_PX = 352;

  /** Lo que hace falta recordar mientras se arrastra. */
  private tilesResize?: {
    handle: HTMLElement;
    pointerId: number;
    container: HTMLElement;
    startX: number;
    startWidth: number;
  };

  /**
   * Empieza a arrastrar el divisor.
   *
   * `setPointerCapture` y no `window.addEventListener`: con la captura, los eventos
   * siguen llegando al divisor aunque el cursor se le salga, que es el caso que con
   * listeners en `window` hay que manejar a mano. Y eventos de PUNTERO y no de
   * mouse, para atender touch y lápiz sin código extra.
   */
  startTilesResize(event: PointerEvent): void {
    const handle = event.currentTarget as HTMLElement | null;
    const container = handle?.parentElement;
    if (!handle || !container) return;
    event.preventDefault();
    handle.setPointerCapture(event.pointerId);
    this.tilesResize = {
      handle,
      pointerId: event.pointerId,
      container,
      startX: event.clientX,
      // Se MIDE en vez de asumir el default: con `gridTilesWidth` en `null` el
      // ancho real es el que le dio el layout, y arrancar del default daría un
      // salto en el primer pixel de arrastre.
      startWidth: this.currentTilesWidth(container),
    };
    // El corazón del asunto: el arrastre no pasa por Angular.
    this.zone.runOutsideAngular(() => {
      handle.addEventListener('pointermove', this.onTilesResize);
      handle.addEventListener('pointerup', this.endTilesResize);
      handle.addEventListener('pointercancel', this.endTilesResize);
    });
  }

  /**
   * Aplica el ancho SIN disparar detección de cambios.
   *
   * Escribe la propiedad personalizada en el contenedor y deja que el navegador
   * reacomode el layout — que es trabajo que iba a hacer igual. El campo se muta
   * también, pero estamos fuera de la zona: no dispara ciclo, y si alguno cae por
   * otro lado el binding de la plantilla ya lee el valor nuevo en vez de revertirlo.
   */
  private readonly onTilesResize = (event: PointerEvent): void => {
    const drag = this.tilesResize;
    if (!drag) return;
    const width = this.clampTilesWidth(
      drag.startWidth + (event.clientX - drag.startX),
      drag.container,
    );
    this.gridTilesWidth = width;
    drag.container.style.setProperty('--grid-tiles-w', `${width}px`);
  };

  /** Suelta el divisor: acá, y solo acá, va el único ciclo de detección. */
  private readonly endTilesResize = (): void => {
    const width = this.gridTilesWidth;
    this.detachTilesResize();
    this.zone.run(() => {
      this.gridTilesWidth = width;
    });
  };

  /**
   * Desengancha el arrastre.
   *
   * Se llama al soltar Y en `ngOnDestroy`: cerrar el diálogo a mitad de arrastre
   * dejaría los listeners vivos sobre un elemento que ya no existe.
   */
  private detachTilesResize(): void {
    const drag = this.tilesResize;
    if (!drag) return;
    drag.handle.removeEventListener('pointermove', this.onTilesResize);
    drag.handle.removeEventListener('pointerup', this.endTilesResize);
    drag.handle.removeEventListener('pointercancel', this.endTilesResize);
    if (drag.handle.hasPointerCapture?.(drag.pointerId)) {
      drag.handle.releasePointerCapture(drag.pointerId);
    }
    this.tilesResize = undefined;
  }

  /**
   * Mueve el divisor con el teclado.
   *
   * Un divisor que solo responde al arrastre es inutilizable sin mouse. Corre
   * dentro de la zona a propósito: es una tecla, no sesenta eventos por segundo.
   */
  nudgeTilesWidth(event: Event, deltaPx: number): void {
    const handle = event.currentTarget as HTMLElement | null;
    const container = handle?.parentElement;
    if (!container) return;
    event.preventDefault();
    this.gridTilesWidth = this.clampTilesWidth(
      this.currentTilesWidth(container) + deltaPx,
      container,
    );
  }

  /** El ancho que el mosaico tiene AHORA, elegido o dado por el layout. */
  private currentTilesWidth(container: HTMLElement): number {
    if (this.gridTilesWidth !== null) return this.gridTilesWidth;
    const mosaic = container.firstElementChild as HTMLElement | null;
    return mosaic?.clientWidth || RouteOptimizationSuggestionDialog.TILES_DEFAULT_PX;
  }

  /**
   * Acota el ancho del mosaico.
   *
   * El máximo es la MITAD del contenedor, relativo y no en `rem` a propósito: en un
   * monitor de 1.280 la mitad son ~40rem y en uno de 2.560 son ~80rem, y en los dos
   * casos la mitad es la frontera correcta entre elegir y leer. Un máximo fijo
   * acertaría en una pantalla y estorbaría en la otra — el mismo error que tenía el
   * ancho fijo que este divisor vino a arreglar.
   *
   * El `max(min, ...)` del techo cubre el contenedor angosto: sin él, una ventana
   * chica daría un techo por debajo del piso y el acotado se invertiría.
   */
  private clampTilesWidth(px: number, container: HTMLElement): number {
    const min = RouteOptimizationSuggestionDialog.TILES_MIN_PX;
    const max = Math.max(min, Math.round((container.clientWidth || 0) / 2));
    return Math.round(Math.min(max, Math.max(min, px)));
  }

  /** El ancho que el divisor le declara a un lector de pantalla. */
  get tilesWidthAria(): number {
    return this.gridTilesWidth ?? RouteOptimizationSuggestionDialog.TILES_DEFAULT_PX;
  }

  /** El piso del divisor, para `aria-valuemin`. */
  get tilesWidthMin(): number {
    return RouteOptimizationSuggestionDialog.TILES_MIN_PX;
  }

  /**
   * El ancho para la propiedad personalizada, ya como longitud CSS.
   *
   * `null` cuando el usuario nunca movió el divisor: Angular quita la propiedad y
   * `var(--grid-tiles-w, 22rem)` cae a su respaldo. Se arma acá y no con el sufijo
   * `.px` del binding porque el sufijo sobre una custom property no está
   * garantizado, y una propiedad mal formada se descarta en silencio.
   */
  get gridTilesWidthCss(): string | null {
    return this.gridTilesWidth === null ? null : `${this.gridTilesWidth}px`;
  }

  /**
   * `true` si algo está filtrando: texto, entregas, o las dos.
   *
   * Una sola bandera para las dos, porque las filas se esconden con el mismo
   * `[hidden]`: si el template preguntara por `searchActive`, seleccionar una
   * entrega sin tipear nada no ocultaría nada.
   */
  filterActive = false;

  /** ¿Quedó algo visible en toda la pantalla? */
  get hasSearchResults(): boolean {
    return !this.filterActive || this.searchHits.size > 0;
  }

  /**
   * Recalcula las coincidencias sobre las tres listas donde puede estar una
   * entrega. Si solo mirara las paradas planificadas, un cliente que quedó sin
   * asignar daría "sin resultados" estando visible más abajo.
   *
   * Los dos filtros son **una sola pasada en AND**: el tag acota el universo y el
   * texto acota dentro de él. Evaluarlos por separado obligaría a mantener dos
   * conjuntos de coincidencias y a intersectarlos en el template, y el resumen
   * —que cuenta por lista— quedaría contando el filtro equivocado.
   */
  private applyFilters(): void {
    const query = this.searchText;
    this.searchActive = !!query.trim();
    this.filterActive = this.searchActive || this.selectedTagKeys.size > 0;
    const hits = new Set<string>();
    const byTruck = new Map<string, number>();
    let routes = 0;
    let unassigned = 0;
    let excluded = 0;

    if (this.filterActive) {
      const tags = this.selectedTagKeys;
      for (const op of this.officePlans) {
        for (const t of op.truckViews) {
          let n = 0;
          // La entrega se decide por tarjeta: si no pasa, ninguna de sus paradas
          // entra y no hace falta normalizar 14 nombres para descubrirlo.
          if (passesRouteTags(tags, t.groupKey)) {
            for (const s of t.stops) {
              if (!matchesSearch(query, [s.name, s.commercialName, s.routeName, s.territory])) continue;
              hits.add(s.orderId);
              n += 1;
            }
          }
          byTruck.set(t.truckId, n);
          routes += n;
        }
        for (const g of op.unassignedGroups) {
          for (const it of g.items) {
            if (!passesRouteTags(tags, it.routeKey)) continue;
            if (!matchesSearch(query, [it.name, it.commercialName, it.routeName, it.territory])) continue;
            hits.add(pointKey(it));
            unassigned += 1;
          }
        }
        for (const p of op.excludedPoints) {
          if (!passesRouteTags(tags, p.routeKey)) continue;
          if (!matchesSearch(query, [p.name, p.commercialName, p.routeName, p.territory])) continue;
          hits.add(pointKey(p));
          excluded += 1;
        }
      }
    }

    this.searchHits = hits;
    this.hitsByTruck = byTruck;
    // Con el filtro activo, esconder las coincidencias detrás de un clic más
    // convertiría el buscador —y los tags— en un contador inútil.
    this.syncTruckOpenState(byTruck);
    this.searchSummaryText = searchSummary({ routes, unassigned, excluded });
  }

  /* ------------------------- Tiempos y promedios ---------------------- */

  /**
   * Minutos de atención por parada **elegidos** para la próxima corrida.
   *
   * Cambiarlo no recalcula nada: se lee al generar, igual que el selector de
   * oficinas. La corrida lanza un plan por grupo de cada oficina seleccionada, así
   * que recalcular en cada clic multiplicaría eso por cada cambio del control.
   */
  selectedServiceMin: number = SERVICE_MIN_PER_STOP;
  readonly serviceMinOptions = SERVICE_MIN_OPTIONS;

  /**
   * Minutos de atención con los que se generó el resultado que está en pantalla.
   *
   * Separado de `selectedServiceMin` a propósito: si el usuario mueve el selector
   * sin regenerar, el supuesto declarado tiene que seguir describiendo el plan que
   * se está mirando, no el que se generaría.
   */
  assumedServiceMin: number = SERVICE_MIN_PER_STOP;

  /**
   * Franja de entrega de la corrida, editable en la pantalla.
   *
   * Era una constante de 08:00–17:00 que nadie podía tocar, y es la **otra mitad** de
   * la ecuación del tiempo: con 15 min de atención, 36 paradas agotan los 540 minutos
   * sin moverse un metro. Los pedidos que salían con `shift_full` salían por falta de
   * minutos, y los minutos no eran de nadie — ni del maestro (su `itinerary` lleva un
   * rango de vigencia, no una jornada) ni del usuario.
   *
   * Como el selector de atención, cambiarla no recalcula nada: se lee al generar.
   */
  shiftStart: string = DEFAULT_SHIFT.start;
  shiftEnd: string = DEFAULT_SHIFT.end;

  /**
   * Franja con la que se generó el resultado que está en pantalla.
   *
   * Separada de `shiftStart`/`shiftEnd` por lo mismo que `assumedServiceMin`: si el
   * usuario mueve los campos sin regenerar, el supuesto declarado tiene que seguir
   * describiendo el plan que se está mirando.
   */
  assumedShift: { start: string; end: string } = { ...DEFAULT_SHIFT };

  /** Minutos del turno asumido, para medir cuánto margen queda. */
  get shiftMin(): number {
    return shiftMinutes(this.assumedShift);
  }

  /**
   * Promedios de la corrida: velocidad media observada, tiempos por parada y
   * jornada por ruta. Se recalcula al publicar resultados, no en el template.
   */
  averages: RunAverages = runAverages([]);

  /** Rearma los promedios desde las rutas resueltas hasta ahora. */
  private rebuildAverages(): void {
    this.averages = runAverages(
      this.officePlans.flatMap((op) =>
        op.truckViews.map((t) => ({
          distanceKm: t.distanceKm,
          travelMin: t.durationMin,
          serviceMin: t.serviceMin,
          stops: t.stops.length,
        })),
      ),
    );
  }

  /** Resumen del estado por ruta para su encabezado colapsado. */
  groupsSummaryOf(view: OfficePlanView): string {
    return groupsSummary(view.groups);
  }

  /**
   * La brecha de flota del encabezado: qué territorios no tienen con qué salir.
   *
   * Va aparte de `groupsSummaryOf` a propósito. Ese resume cómo se planificó lo
   * que sí tiene camión; este dice qué NO se puede planificar, que es lo que se
   * arregla en el maestro y no en la corrida.
   */
  fleetGapSummaryOf(view: OfficePlanView): string {
    return fleetGapSummary(view.fleetGaps);
  }


  /**
   * Indicador único de rutas de la oficina: producidas contra grupos a
   * planificar. Sin grupos (camino por oficina) informa solo las producidas.
   */
  routesLabelOf(view: OfficePlanView): string {
    return routesProgressLabel(view.truckViews.length, view.groups.length);
  }



  /** Espera un Observable con timeout; ante fallo/tardanza devuelve `null`. */
  private safe<T>(obs: Observable<T>, fallback: T | null = null, ms = 15_000): Promise<T | null> {
    return firstValueFrom(obs.pipe(timeout(ms), catchError(() => of(fallback))));
  }

  private reset(): void {
    // La corrida que viene reemplaza a la guardada; el aviso de "restaurado" ya
    // no describe nada.
    this.runCache.clear();
    this.restoredAtLabel = '';
    this.confirmResults = [];
    this.officePlans = [];
    this.allOrders = [];
    this.weightByCustomer = new Map();
    this.weightByOrderId = new Map();
    this.subRoutesById = new Map();
    this.masterOfficesKey = '';
    this.duplicateVehicles = [];
    this.truckColorIndex = 0;
    this.dash = undefined;
    this.officeSummary = undefined;
    this.reviewOpen = false;
    this.reviewRoutes = [];
    // Describe un movimiento entre rutas de un plan que ya no existe.
    this.lastReviewMove = null;
    // Los pendientes son los de ESTA corrida: los de la anterior describen una
    // brecha de flota que ya no se midió.
    this.pendingAll = [];
    this.pendingCache = null;
    this.pendingPointByOrderId = new Map();
    this.reviewCoordByOrderId = new Map();
    this.reviewWarehouseBySubRouteId = new Map();
    this.pendingOpen = false;
    // El coeficiente describe la geografía de la corrida que se va.
    this.runCalibration = { urbanMinPerKm: null, haulMinPerKm: null };
    // Los recorridos recalculados describen secuencias de un plan que ya no existe.
    this.recalcBySubRouteId = new Map();
    this.recalcingSubRouteIds = new Set();
    this.recalcErrorBySubRouteId = new Map();
    this.savedOrderSubRouteIds = new Set();
    this.savedOrderBySubRoute = new Map();
    this.appliedSavedOrder = new Set();
    this.savedOrderApplyResult = null;
    this.baselineByGroupKey = new Map();
    this.autoGroupKeyByOrderId = new Map();
    this.assignmentByOrderId = new Map();
    this.paramsBySubRouteId = new Map();
    this.decisionPointByOrderId = new Map();
    this.visibleMarkerGroups = [];
    this.visiblePolylines = [];
    this.routeTags = [];
    this.selectedTagKeys = new Set<string>();
    this.searchText = '';
    this.searchActive = false;
    // Si quedara en `true`, las filas se esconderían contra un conjunto de
    // coincidencias vacío: pantalla en blanco sin nada que lo explique.
    this.filterActive = false;
    this.searchSummaryText = '';
    this.searchHits = new Set();
    this.hitsByTruck = new Map();
    this.routeOptions = [];
    this.selectedTruckId = null;
    this.mapOverlay = null;
    this.overlayMarkerGroups = [];
    this.overlayPolylines = [];
    this.geojsonByGroup = new Map();
    this.errorMessage = '';
    this.activeTab = 'list';
    this.scopeCounts = { 'with-trip': 0, 'without-trip': 0 };
    this.groupsDone = 0;
    this.groupsTotal = 0;
    this.progressOfficeName = '';
    this.progressIndex = 0;
    this.progressTotal = 0;
  }

  private handleError(err: unknown): void {
    this.errorMessage = planErrorMessage(err);
    this.state = 'error';
    this.handlerMessage.error({
      message: this.errorMessage,
      alternativeMessage: 'No se pudo generar la sugerencia de rutas.',
      statusCode: (err as { status?: number })?.status,
    });
  }
}
